// ============================================================================
// SS2 CHRISTIAN RELIGIOUS STUDIES (CRS) — COMPLETE LEARNING MODULE (24 TOPICS)
// NERDC Scheme of Work — First Term to Third Term
// ============================================================================

export const WAEC_CRS_LEARN_SS2 = [
  {
    topic: 'Leadership of Joseph, Moses, Joshua and Deborah I',
    topicCode: 'SS2-CRS-01',
    module: 'Leaders of Israel',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The Bible presents many men and women whom God raised up to lead His people at critical moments in history. In this first study of four great leaders — <span class="learn-keyword">Joseph</span>, <span class="learn-keyword">Moses</span>, <span class="learn-keyword">Joshua</span> and <span class="learn-keyword">Deborah</span> — we examine their early lives and how God called each of them into leadership.\n</div>\n\n<h3 class="learn-subheading">1. Joseph — The Dreamer Who Became a Ruler</h3>\n<p class="learn-p">Joseph was the eleventh son of Jacob and the first son of his beloved wife Rachel (Genesis 30:22-24). His father\'s open favouritism — shown through the famous <strong>coat of many colours</strong> — and Joseph\'s own dreams of future greatness stirred deep jealousy in his brothers (Genesis 37:3-11). Out of envy, his brothers sold him into slavery to Ishmaelite/Midianite traders, who took him to Egypt (Genesis 37:23-28). In Egypt, Joseph served in the house of Potiphar, was falsely accused and imprisoned, yet remained faithful to God throughout. His gift of interpreting dreams eventually brought him before Pharaoh, and he was made second-in-command over all Egypt (Genesis 41:39-41).</p>\n\n<h3 class="learn-subheading">2. Moses — Called from the Burning Bush</h3>\n<p class="learn-p">Moses was born to Hebrew parents of the tribe of Levi at a time when Pharaoh had ordered every Hebrew baby boy to be killed (Exodus 1:22). To save him, his mother hid him for three months, then placed him in a basket among the reeds of the River Nile, where he was found and adopted by Pharaoh\'s daughter (Exodus 2:1-10). Raised in the Egyptian palace, Moses later fled to Midian after killing an Egyptian who was beating a Hebrew slave. While shepherding in Midian, God called him at the burning bush that burned but was not consumed, commissioning him to deliver Israel from slavery in Egypt (Exodus 3:1-10).</p>\n\n<h3 class="learn-subheading">3. Joshua — Trained Under Moses</h3>\n<p class="learn-p">Joshua, son of Nun, from the tribe of Ephraim, served as Moses\' personal assistant. He was one of the twelve spies sent to explore Canaan and, along with Caleb, gave a faithful, courageous report that trusted in God\'s promise, unlike the other ten spies who spread fear (Numbers 13:1-2, 30; 14:6-9). Because of his faith, Joshua (with Caleb) was permitted to enter the Promised Land even though the rest of that generation died in the wilderness.</p>\n\n<h3 class="learn-subheading">4. Deborah — Prophetess and Judge</h3>\n<p class="learn-p">Deborah was a prophetess and the only female judge mentioned in the Book of Judges. She held court under the "Palm of Deborah" between Ramah and Bethel, where the Israelites came to her for judgment (Judges 4:4-5). At a time when Israel was oppressed by Jabin, king of Canaan, and his army commander Sisera, Deborah summoned Barak and, speaking under God\'s direction, commanded him to gather an army against Sisera (Judges 4:6-7).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Leader</th><th>Background</th><th>How God Called/Prepared Them</th></tr></thead>\n    <tbody>\n      <tr><td>Joseph</td><td>Favoured son of Jacob, sold into slavery</td><td>Prepared through suffering and faithfulness in Egypt</td></tr>\n      <tr><td>Moses</td><td>Hebrew child raised in Pharaoh\'s palace</td><td>Called at the burning bush in Midian</td></tr>\n      <tr><td>Joshua</td><td>Assistant to Moses, faithful spy</td><td>Trained under Moses; commissioned after his death</td></tr>\n      <tr><td>Deborah</td><td>Prophetess and judge of Israel</td><td>Held divine authority to judge and direct military action</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Examiners often ask for the specific location or circumstance of each leader\'s call — the burning bush for Moses, the "Palm of Deborah" for Deborah, and the report of the twelve spies for Joshua. Learn these details precisely.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Joseph, Moses, Joshua and Deborah each experienced hardship or preparation before God raised them into leadership. Their early lives teach that God often prepares leaders through humble or difficult beginnings before entrusting them with responsibility.\n</div>\n',
    questions: [
      {
        q: 'Joseph was sold into slavery by his brothers mainly because of:',
        o: [
          'His refusal to work',
          "Their jealousy over his father's favouritism and his dreams",
          'A quarrel over land',
          'His friendship with the Egyptians',
        ],
        a: 1,
        e: "Jacob's open favouritism toward Joseph, shown through the coat of many colours, together with Joseph's dreams of ruling over his brothers, provoked deep jealousy that led them to sell him into slavery (Genesis 37:3-28).",
        h: "Think about the coat of many colours and Joseph's dreams.",
        yr: 'SS2',
      },
      {
        q: 'Who bought Joseph after he was taken to Egypt?',
        o: [
          'Pharaoh himself',
          'Potiphar, an officer of Pharaoh',
          'A Midianite priest',
          'The king of Midian',
        ],
        a: 1,
        e: 'Joseph was sold to Potiphar, an officer of Pharaoh and captain of the royal guard, in whose house he served faithfully (Genesis 39:1).',
        h: 'He served in the household of an Egyptian official before his imprisonment.',
        yr: 'SS2',
      },
      {
        q: 'Moses was placed in a basket on the River Nile because:',
        o: [
          'His mother wanted him to learn to swim',
          'Pharaoh had ordered all Hebrew baby boys to be killed',
          'His family was too poor to feed him',
          'He was being sent to Midian',
        ],
        a: 1,
        e: 'Pharaoh, fearing the growing number of Hebrews, commanded that every Hebrew baby boy be thrown into the Nile. To save Moses, his mother hid him and then placed him in a basket among the reeds (Exodus 1:22; 2:1-4).',
        h: "Pharaoh's decree against Hebrew male infants is the key reason.",
        yr: 'SS2',
      },
      {
        q: 'Who found baby Moses among the reeds of the Nile?',
        o: ["Pharaoh's wife", "Pharaoh's daughter", "Pharaoh's sister", 'A Hebrew midwife'],
        a: 1,
        e: "Pharaoh's daughter came to bathe at the river, found the basket, and had compassion on the crying baby, adopting him as her own (Exodus 2:5-10).",
        h: 'The rescuer was a royal princess, not a queen.',
        yr: 'SS2',
      },
      {
        q: 'Where did God first call Moses to lead Israel out of Egypt?',
        o: [
          'In the palace of Pharaoh',
          'At the burning bush in Midian',
          'On Mount Sinai after the Exodus',
          'In the land of Goshen',
        ],
        a: 1,
        e: "While shepherding his father-in-law Jethro's flock near Horeb, Moses saw a bush that burned but was not consumed. God spoke to him from within the bush and commissioned him to deliver Israel (Exodus 3:1-10).",
        h: 'This event involved fire that did not burn up the plant.',
        yr: 'SS2',
      },
      {
        q: 'Before his call, Moses fled to Midian because he had:',
        o: [
          "Disobeyed Pharaoh's officials",
          'Killed an Egyptian who was beating a Hebrew slave',
          'Been banished by his adoptive mother',
          'Refused to work in the palace',
        ],
        a: 1,
        e: 'Moses killed an Egyptian taskmaster whom he saw beating a Hebrew slave. When this became known, he fled to Midian in fear of Pharaoh (Exodus 2:11-15).',
        h: 'His flight followed an act of violence in defence of a fellow Hebrew.',
        yr: 'SS2',
      },
      {
        q: 'Joshua belonged to which tribe of Israel?',
        o: ['Levi', 'Judah', 'Ephraim', 'Benjamin'],
        a: 2,
        e: 'Joshua, son of Nun, was from the tribe of Ephraim, one of the two tribes descended from Joseph (Numbers 13:8, 16).',
        h: "This tribe descended from one of Joseph's sons.",
        yr: 'SS2',
      },
      {
        q: 'Joshua first rose to prominence in Scripture as:',
        o: [
          'A judge of Israel',
          'One of the twelve spies sent to explore Canaan',
          'A prophet in Egypt',
          'A priest in the tabernacle',
        ],
        a: 1,
        e: 'Joshua was among the twelve spies Moses sent to explore the land of Canaan. Along with Caleb, he gave a faithful report encouraging Israel to trust God and take the land (Numbers 13:1-2; 14:6-9).',
        h: 'Recall the mission of twelve men sent ahead into Canaan.',
        yr: 'SS2',
      },
      {
        q: 'Why were Joshua and Caleb allowed to enter the Promised Land while their generation was not?',
        o: [
          'They were younger than everyone else',
          "They believed God's promise and gave a faithful report",
          'They were priests',
          'They had never sinned',
        ],
        a: 1,
        e: "Unlike the other ten spies who discouraged the people with fear, Joshua and Caleb trusted God's promise that Israel could conquer Canaan. Because of their faith, God spared them from dying in the wilderness (Numbers 14:6-9, 30, 38).",
        h: 'Their faith stood in contrast to the unbelief of the other ten spies.',
        yr: 'SS2',
      },
      {
        q: 'Deborah is uniquely described in the Book of Judges as:',
        o: [
          'A queen of Israel',
          'A prophetess and judge, the only woman judge recorded',
          'A high priestess',
          'The wife of Moses',
        ],
        a: 1,
        e: 'Deborah was a prophetess who also served as a judge over Israel — the only woman among the judges recorded in the Book of Judges (Judges 4:4).',
        h: 'She combined two roles: prophecy and judging.',
        yr: 'SS2',
      },
      {
        q: 'Where did Deborah hold court to judge the Israelites?',
        o: [
          'In the tabernacle at Shiloh',
          'Under the Palm of Deborah between Ramah and Bethel',
          'At Mount Sinai',
          'In the city of Jericho',
        ],
        a: 1,
        e: "Deborah sat under a palm tree, later called the 'Palm of Deborah,' located between Ramah and Bethel in the hill country of Ephraim, where Israelites came for judgment (Judges 4:5).",
        h: 'The location was named after her and marked by a particular tree.',
        yr: 'SS2',
      },
      {
        q: "Who did Deborah summon to lead Israel's army against Sisera?",
        o: ['Gideon', 'Barak', 'Samson', 'Ehud'],
        a: 1,
        e: "Deborah called Barak, son of Abinoam, and, speaking as God directed her, commanded him to gather ten thousand men and confront Sisera, the commander of Jabin's army (Judges 4:6-7).",
        h: 'This military leader initially hesitated to go without Deborah.',
        yr: 'SS2',
      },
      {
        q: 'Which nation had oppressed Israel at the time God raised up Deborah?',
        o: [
          'The Philistines',
          'Jabin, king of Canaan, through his commander Sisera',
          'The Midianites',
          'The Egyptians',
        ],
        a: 1,
        e: 'At that time, Israel was under the oppression of Jabin, king of Canaan, whose army was commanded by Sisera (Judges 4:1-3).',
        h: 'This was a Canaanite king, not the Egyptians or Philistines.',
        yr: 'SS2',
      },
      {
        q: 'What common thread links the calls of Joseph, Moses, Joshua and Deborah?',
        o: [
          'They all sought leadership for personal gain',
          'Each was prepared or chosen by God despite humble or difficult circumstances',
          'They were all born into royal families',
          "They all refused to accept God's call at first",
        ],
        a: 1,
        e: 'Each leader emerged from humbling or difficult circumstances — slavery, danger, subordination, or a male-dominated society — yet God prepared and used each of them for His purposes.',
        h: "Consider what each leader's background had in common before greatness came.",
        yr: 'SS2',
      },
      {
        q: "Joseph's rise to power in Egypt came primarily through his gift of:",
        o: ['Military strategy', 'Interpreting dreams', 'Building structures', 'Leading armies'],
        a: 1,
        e: "Joseph's ability to interpret dreams — first for fellow prisoners, then for Pharaoh regarding the seven years of plenty and famine — led to his elevation as ruler over Egypt, second only to Pharaoh (Genesis 41:14-41).",
        h: 'Think of what earned Joseph an audience with Pharaoh.',
        yr: 'SS2',
      },
      {
        q: "Moses' early life in the Egyptian palace illustrates that:",
        o: [
          'God cannot use people raised outside the faith community',
          'God can providentially position leaders in unlikely places for future purposes',
          'Only priests can become leaders',
          'Royal upbringing guarantees godly character',
        ],
        a: 1,
        e: "Though raised in Pharaoh's household, Moses was providentially positioned there so that, when God called him, he had the training and standing needed to confront Pharaoh and lead Israel out of Egypt.",
        h: "Consider how his palace upbringing later served God's purpose.",
        yr: 'SS2',
      },
      {
        q: "Joshua's role before becoming Israel's leader is best described as:",
        o: [
          'An independent ruler',
          "Moses' faithful assistant and trainee",
          'A foreign mercenary',
          'A judge over Egypt',
        ],
        a: 1,
        e: "Joshua served as Moses' minister/assistant for many years, learning leadership under him before being commissioned to succeed Moses after his death (Exodus 24:13; Joshua 1:1-2).",
        h: 'He learned leadership by serving under someone else first.',
        yr: 'SS2',
      },
      {
        q: "Deborah's leadership shows that in ancient Israel:",
        o: [
          'Only men could be judges',
          'God could raise a woman to significant spiritual and civic leadership',
          'Prophetesses had no authority',
          'Judges only handled military matters',
        ],
        a: 1,
        e: "Deborah's position as both prophetess and judge — with authority recognized by the whole nation, including a military commander — shows that God was not limited by cultural norms in choosing leaders.",
        h: 'Her example challenges the assumption that leadership was reserved only for men.',
        yr: 'SS2',
      },
      {
        q: 'Which of these is a quality all four leaders (Joseph, Moses, Joshua, Deborah) shared before or during their call?',
        o: [
          'Wealth and royal birth',
          'Faithfulness and reliance on God despite personal difficulty',
          'Formal military training',
          'Popularity with foreign kings',
        ],
        a: 1,
        e: 'Despite differing backgrounds, all four remained faithful and trusted God through hardship or uncertainty — Joseph in slavery and prison, Moses in exile, Joshua as a subordinate, and Deborah in a position not typically held by women.',
        h: 'Look for the shared inner quality despite very different life stories.',
        yr: 'SS2',
      },
      {
        q: 'The early lives of Joseph, Moses, Joshua and Deborah primarily teach Christians today that:',
        o: [
          'Leadership always comes without struggle',
          'God prepares His chosen leaders often through trials and unexpected paths',
          'Only the firstborn can lead',
          'Leadership is unrelated to faith in God',
        ],
        a: 1,
        e: 'Their stories show a consistent biblical pattern: God frequently uses hardship, humble beginnings, or unusual circumstances to prepare people for leadership roles He has planned for them.',
        h: 'Consider the overall lesson drawn from comparing all four accounts.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Leadership of Joseph, Moses, Joshua and Deborah II',
    topicCode: 'SS2-CRS-02',
    module: 'Leaders of Israel',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Having studied the early lives and calls of Joseph, Moses, Joshua and Deborah, we now turn to their <span class="learn-keyword">achievements</span>, the <span class="learn-keyword">challenges</span> they faced, and the enduring <span class="learn-keyword">lessons</span> their leadership offers.\n</div>\n\n<h3 class="learn-subheading">1. Joseph\'s Achievements and Challenges</h3>\n<p class="learn-p">As governor of Egypt, Joseph implemented a wise food-storage plan during seven years of plenty, saving Egypt and neighbouring nations from a severe seven-year famine (Genesis 41:46-57). He faced great personal challenges — betrayal by his brothers, false accusation by Potiphar\'s wife, and years of imprisonment — yet did not become bitter. When his brothers eventually came to Egypt for food, he forgave them, famously declaring, "You meant evil against me, but God meant it for good" (Genesis 50:20).</p>\n\n<h3 class="learn-subheading">2. Moses\' Achievements and Challenges</h3>\n<p class="learn-p">Moses confronted Pharaoh through the ten plagues, led Israel out of Egyptian bondage across the Red Sea, received the Ten Commandments at Mount Sinai, and guided the nation through forty years in the wilderness (Exodus 7-20). His challenges included Pharaoh\'s repeated refusals, the people\'s frequent complaints and rebellion (such as the golden calf incident, Exodus 32), and his own moments of frustration, including striking the rock in anger, which cost him entry into Canaan (Numbers 20:7-12).</p>\n\n<h3 class="learn-subheading">3. Joshua\'s Achievements and Challenges</h3>\n<p class="learn-p">After Moses\' death, Joshua led Israel across the River Jordan and conquered the Promised Land, beginning with the miraculous fall of Jericho (Joshua 6). He faced the challenge of leading a nation still learning obedience, including the defeat at Ai caused by Achan\'s sin (Joshua 7), and the responsibility of dividing the conquered land among the twelve tribes (Joshua 13-21).</p>\n\n<h3 class="learn-subheading">4. Deborah\'s Achievements and Challenges</h3>\n<p class="learn-p">Deborah, together with Barak, led Israel to a decisive victory over Sisera\'s army, which was destroyed near the River Kishon (Judges 4:15-16). Sisera himself was later killed by Jael. Deborah\'s challenge was rallying a discouraged, oppressed nation and a hesitant military commander into confident action. Her victory is celebrated in the Song of Deborah (Judges 5), one of the oldest poems in the Bible.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Leader</th><th>Key Achievement</th><th>Major Challenge</th></tr></thead>\n    <tbody>\n      <tr><td>Joseph</td><td>Saved Egypt and his family from famine</td><td>Betrayal, false accusation, imprisonment</td></tr>\n      <tr><td>Moses</td><td>Delivered Israel from Egypt; received the Law</td><td>Pharaoh\'s resistance; the people\'s rebellion</td></tr>\n      <tr><td>Joshua</td><td>Conquered Canaan; divided the land</td><td>Defeat at Ai; leading a young, untested nation</td></tr>\n      <tr><td>Deborah</td><td>Defeated Sisera\'s army with Barak</td><td>Rallying a fearful, oppressed people</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Be ready to quote or paraphrase Joseph\'s famous forgiveness statement in Genesis 50:20, and to explain the causes of Israel\'s setbacks under Moses and Joshua (golden calf; Achan\'s sin at Ai).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Each leader achieved great things for Israel but also faced serious setbacks. Their responses — forgiveness, perseverance, discipline and courage — model godly leadership under pressure.\n</div>\n',
    questions: [
      {
        q: "How did Joseph's administrative plan save Egypt during the famine?",
        o: [
          'By taxing neighbouring nations',
          'By storing surplus grain during seven years of plenty for use during seven years of famine',
          'By importing food from Canaan',
          "By reducing Egypt's population",
        ],
        a: 1,
        e: "Under Joseph's guidance, Egypt stored large quantities of grain during the seven years of abundance, which provided enough food during the seven years of famine that followed (Genesis 41:46-57).",
        h: "Recall Pharaoh's dream of seven fat cows and seven lean cows.",
        yr: 'SS2',
      },
      {
        q: 'What famous statement did Joseph make to his brothers after revealing himself?',
        o: [
          "'I will never forgive you'",
          "'You meant evil against me, but God meant it for good'",
          "'You must leave Egypt at once'",
          "'I no longer consider you my family'",
        ],
        a: 1,
        e: "Joseph reassured his fearful brothers with the statement recorded in Genesis 50:20, showing that he saw God's providence even in their betrayal, turning it into a means of saving many lives.",
        h: "This statement reflects Joseph's view of God's providence over his suffering.",
        yr: 'SS2',
      },
      {
        q: "Which event led directly to Joseph's imprisonment in Egypt?",
        o: [
          "He stole from Pharaoh's treasury",
          "Potiphar's wife falsely accused him after he refused her advances",
          'He insulted Pharaoh publicly',
          'He refused to interpret a dream',
        ],
        a: 1,
        e: "Potiphar's wife falsely accused Joseph of assaulting her after he refused to sin with her, leading to his unjust imprisonment (Genesis 39:7-20).",
        h: "Joseph's integrity in refusing temptation led to false accusation.",
        yr: 'SS2',
      },
      {
        q: 'The ten plagues that Moses announced were directed against:',
        o: [
          'The Israelites for their sins',
          'Pharaoh and Egypt, to compel the release of Israel',
          'The Midianites',
          'The Amalekites',
        ],
        a: 1,
        e: 'God sent ten plagues upon Egypt through Moses to force Pharaoh to release the enslaved Israelites (Exodus 7-12).',
        h: "These judgments targeted the nation that refused to free God's people.",
        yr: 'SS2',
      },
      {
        q: "What incident at Mount Sinai showed Israel's rebellion while Moses received the Law?",
        o: [
          'The bronze serpent',
          'The golden calf',
          'The bitter waters of Marah',
          'The bronze altar',
        ],
        a: 1,
        e: "While Moses was on the mountain receiving the Ten Commandments, the Israelites persuaded Aaron to make a golden calf and worshipped it, provoking God's anger (Exodus 32:1-8).",
        h: 'This idol was made from melted gold jewellery.',
        yr: 'SS2',
      },
      {
        q: 'Why was Moses barred from entering the Promised Land?',
        o: [
          'He worshipped a false god',
          'He struck the rock in anger instead of speaking to it as God commanded',
          'He disobeyed Pharaoh',
          'He refused to lead the people',
        ],
        a: 1,
        e: "At Meribah, God told Moses to speak to a rock to bring forth water, but Moses struck it twice in anger. Because he did not honour God's instruction before the people, he was told he would not enter Canaan (Numbers 20:7-12).",
        h: 'The disobedience involved a rock and water in the wilderness.',
        yr: 'SS2',
      },
      {
        q: 'How did Israel, under Joshua, capture the city of Jericho?',
        o: [
          'Through a long siege with battering rams',
          'By marching around the walls for seven days as God instructed, after which the walls fell',
          'By tunnelling under the walls',
          "By bribing the city's leaders",
        ],
        a: 1,
        e: "Following God's specific instructions, the Israelites marched around Jericho once daily for six days, and seven times on the seventh day, after which the walls collapsed (Joshua 6:1-20).",
        h: 'This victory did not involve conventional siege warfare — it involved marching and trumpets.',
        yr: 'SS2',
      },
      {
        q: "What caused Israel's unexpected defeat at the small city of Ai?",
        o: [
          'Poor military strategy alone',
          "Achan's sin of taking devoted items from Jericho",
          'A lack of soldiers',
          "Joshua's absence from battle",
        ],
        a: 1,
        e: "Achan secretly kept plunder from Jericho that had been devoted to destruction, violating God's command. This sin brought God's displeasure and led to Israel's defeat at Ai until the sin was dealt with (Joshua 7:1-26).",
        h: "One man's disobedience affected the whole army's success.",
        yr: 'SS2',
      },
      {
        q: 'After conquering Canaan, what major responsibility did Joshua carry out?',
        o: [
          'Building a new temple',
          'Dividing the conquered land among the twelve tribes of Israel',
          'Establishing a monarchy',
          'Appointing a new set of judges permanently',
        ],
        a: 1,
        e: "Joshua oversaw the allocation of the conquered territory of Canaan among the twelve tribes of Israel, fulfilling God's promise to their ancestors (Joshua 13-21).",
        h: 'This task involved distributing territory, not appointing kings.',
        yr: 'SS2',
      },
      {
        q: "Where was Sisera's army decisively defeated by Barak's forces?",
        o: ['Near the River Jordan', 'Near the River Kishon', 'At Jericho', 'At Mount Carmel'],
        a: 1,
        e: "Barak's army, following Deborah's direction, routed Sisera's forces near the River Kishon, which overflowed and threw the enemy into confusion (Judges 4:15-16; 5:21).",
        h: "A river near Mount Tabor played a role in the enemy's defeat.",
        yr: 'SS2',
      },
      {
        q: "Who ultimately killed Sisera after his army's defeat?",
        o: ['Barak', 'Deborah', 'Jael, wife of Heber the Kenite', 'Gideon'],
        a: 2,
        e: 'Sisera fled to the tent of Jael, who offered him hospitality but then killed him by driving a tent peg through his temple while he slept (Judges 4:17-21).',
        h: 'The killer was a woman who offered the fleeing commander shelter.',
        yr: 'SS2',
      },
      {
        q: 'The Song of Deborah, found in Judges 5, is significant because it:',
        o: [
          'Records the Ten Commandments',
          "Is a celebratory poem commemorating Israel's victory over Sisera",
          'Predicts the coming Messiah',
          "Lists the twelve tribes' territories",
        ],
        a: 1,
        e: "Judges 5 records the triumphant Song of Deborah and Barak, celebrating God's deliverance of Israel and considered one of the oldest pieces of Hebrew poetry in the Bible.",
        h: 'This chapter is a poetic celebration following a military victory.',
        yr: 'SS2',
      },
      {
        q: "A key lesson from Joseph's response to his brothers' betrayal is:",
        o: [
          'Revenge is justified when wronged',
          "Forgiveness and trust in God's providence can turn evil into good",
          'Family relationships should be abandoned after betrayal',
          'Leaders should punish those who wrong them',
        ],
        a: 1,
        e: "Joseph's forgiveness of his brothers, despite the great wrong they did him, models the Christian virtue of forgiveness and trust that God can work even painful experiences for good.",
        h: "Consider Joseph's attitude when he finally met his brothers again.",
        yr: 'SS2',
      },
      {
        q: 'A key lesson from Moses striking the rock is that:',
        o: [
          'Even great leaders must obey God precisely, for disobedience has consequences',
          'Anger is an acceptable leadership tool',
          'Miracles depend on human methods',
          'God overlooks the mistakes of leaders',
        ],
        a: 0,
        e: "Moses' disobedience — striking rather than speaking to the rock — teaches that no leader, however great, is exempt from the consequences of disobeying God's explicit instructions.",
        h: 'Think about why God still held Moses accountable despite his long, faithful service.',
        yr: 'SS2',
      },
      {
        q: "A key lesson from Achan's sin at Ai is that:",
        o: [
          'Personal sin can have serious consequences for a whole community',
          "Only leaders' sins matter to God",
          'Disobedience only affects the individual involved',
          'Victory is guaranteed regardless of obedience',
        ],
        a: 0,
        e: "Achan's secret disobedience brought defeat and judgment upon the entire nation until the sin was confronted and dealt with, showing that individual sin can affect the whole community.",
        h: "Consider why the whole army suffered because of one man's action.",
        yr: 'SS2',
      },
      {
        q: "A key lesson from Deborah and Barak's victory is that:",
        o: [
          'Victory depends solely on military strength',
          'God can grant victory when His people trust and obey Him, even through unlikely leaders',
          'Women cannot contribute to national deliverance',
          'Military commanders should never seek guidance',
        ],
        a: 1,
        e: "Despite Israel's weakness and Barak's initial hesitation, obedience to God's direction through Deborah led to a decisive victory, showing that God grants victory to those who trust and obey Him.",
        h: "Notice how the story emphasizes trust in God's direction rather than military might alone.",
        yr: 'SS2',
      },
      {
        q: "Which best summarizes a shared lesson from all four leaders' achievements and challenges?",
        o: [
          'Leadership guarantees a life free of hardship',
          'Godly leadership involves both great achievement and real struggle, faced with faith and integrity',
          'Only miracles matter in leadership, not character',
          'Challenges always disqualify a leader from further service',
        ],
        a: 1,
        e: "Joseph, Moses, Joshua and Deborah all achieved great things for God's people while also facing serious personal or national challenges. Their example shows that faith and integrity, not the absence of hardship, define godly leadership.",
        h: 'Look at the pattern common to all four accounts of achievement mixed with challenge.',
        yr: 'SS2',
      },
      {
        q: "What does Joseph's rise from prisoner to prime minister of Egypt primarily illustrate?",
        o: [
          'That worldly success requires compromise',
          'That God can exalt the faithful even through unjust suffering',
          'That family betrayal always ends in tragedy',
          'That dream interpretation is the only path to leadership',
        ],
        a: 1,
        e: "Joseph's journey from slavery and imprisonment to becoming Egypt's second-in-command shows how God can bring about great purposes through a person's faithfulness even amid unjust suffering.",
        h: "Consider the contrast between Joseph's lowest point and his highest position.",
        yr: 'SS2',
      },
      {
        q: "Israel's frequent complaints during the wilderness journey under Moses mainly reflect:",
        o: [
          "Their strong faith in God's provision",
          "A recurring struggle with doubt and dissatisfaction despite God's continual provision",
          "Their satisfaction with Moses' leadership",
          'Their eagerness to return to slavery permanently',
        ],
        a: 1,
        e: "Despite God's continual provision of manna, water, and guidance, the Israelites repeatedly grumbled and doubted, revealing a persistent struggle with trust during the wilderness years (Exodus 16-17; Numbers 14).",
        h: "Recall the pattern of grumbling despite God's provision of manna and water.",
        yr: 'SS2',
      },
      {
        q: "The overall achievements of Joshua's conquest of Canaan primarily fulfilled:",
        o: [
          'A promise God made only to Moses',
          "God's ancient covenant promise to give the land of Canaan to Abraham's descendants",
          'A political alliance with Egypt',
          "Joshua's personal military ambition",
        ],
        a: 1,
        e: "Joshua's conquest and division of Canaan fulfilled God's covenant promise, first made to Abraham, that his descendants would possess the land (Genesis 15:18; Joshua 21:43-45).",
        h: "This promise traces back to God's covenant with Abraham generations earlier.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Leadership of Joseph, Moses, Joshua and Deborah III',
    topicCode: 'SS2-CRS-03',
    module: 'Leaders of Israel',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  In this final study of the four leaders, we consider the <span class="learn-keyword">relevance</span> of Joseph, Moses, Joshua and Deborah\'s leadership examples to <span class="learn-keyword">contemporary Christian life</span>. Their stories are not merely historical accounts; they offer timeless principles for leadership, faith and character today.\n</div>\n\n<h3 class="learn-subheading">1. Joseph\'s Relevance Today</h3>\n<p class="learn-p">Joseph teaches Christians to remain faithful and maintain integrity even when suffering unjustly, to resist temptation regardless of the cost, and to forgive those who wrong us. His example encourages believers to trust that God can work all things — even painful experiences — together for good (Romans 8:28, echoing Genesis 50:20).</p>\n\n<h3 class="learn-subheading">2. Moses\' Relevance Today</h3>\n<p class="learn-p">Moses shows that God can use ordinary, even reluctant, people for extraordinary purposes, and that leaders must remain humble and obedient to God\'s instructions even after long service. His life reminds Christian leaders today of the importance of patience with difficult people and the seriousness of obeying God precisely, not just approximately.</p>\n\n<h3 class="learn-subheading">3. Joshua\'s Relevance Today</h3>\n<p class="learn-p">Joshua\'s courage — captured in God\'s charge to him, "Be strong and of good courage... for the Lord your God is with you wherever you go" (Joshua 1:9) — encourages Christians to face new responsibilities boldly, trusting in God\'s presence. His careful, step-by-step obedience in conquering Canaan models disciplined, faithful leadership.</p>\n\n<h3 class="learn-subheading">4. Deborah\'s Relevance Today</h3>\n<p class="learn-p">Deborah demonstrates that God calls and equips both men and women for leadership and that spiritual discernment (prophecy) can guide practical, even military, decisions. Her partnership with Barak also shows the value of encouraging and supporting others into their own calling rather than seeking sole credit.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Leader</th><th>Timeless Principle for Christians Today</th></tr></thead>\n    <tbody>\n      <tr><td>Joseph</td><td>Integrity under pressure; forgiveness; trust in God\'s providence</td></tr>\n      <tr><td>Moses</td><td>Obedience to God even in leadership fatigue; humility despite great responsibility</td></tr>\n      <tr><td>Joshua</td><td>Courage in new responsibility; careful, faithful obedience</td></tr>\n      <tr><td>Deborah</td><td>God uses willing, faithful people regardless of gender; encouraging others in their calling</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> When asked for "relevance to contemporary Christian life," always connect the Old Testament example to a specific present-day application — for example, workplace integrity (Joseph), obeying God precisely (Moses), courage in new roles (Joshua), or gender-inclusive leadership (Deborah).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> The examples of Joseph, Moses, Joshua and Deborah remain relevant today as models of integrity, obedience, courage, and faithful service — qualities every Christian leader is called to embody.\n</div>\n',
    questions: [
      {
        q: "Joseph's response to injustice offers Christians today a model of:",
        o: [
          'Bitterness and revenge',
          'Integrity and forgiveness despite suffering',
          'Avoidance of responsibility',
          "Distrust of God's plans",
        ],
        a: 1,
        e: 'Joseph maintained integrity in slavery and prison and later forgave his brothers, modelling how Christians should respond to injustice with faithfulness and forgiveness rather than bitterness.',
        h: 'Recall how Joseph treated his brothers when he had the power to take revenge.',
        yr: 'SS2',
      },
      {
        q: "Which New Testament teaching echoes the lesson of Genesis 50:20 (Joseph's forgiveness)?",
        o: [
          'Romans 8:28 — God works all things together for good for those who love Him',
          "John 3:16 — God's love for the world",
          'Matthew 5:3 — Blessed are the poor in spirit',
          'Acts 2:1 — the coming of the Holy Spirit',
        ],
        a: 0,
        e: 'Romans 8:28 teaches that God works all things for good for those who love Him, reflecting the same principle Joseph expressed — that God can turn even evil circumstances into good outcomes.',
        h: "Look for the verse about God working 'all things' together for good.",
        yr: 'SS2',
      },
      {
        q: "Moses' life teaches contemporary leaders the importance of:",
        o: [
          "Acting independently of God's instructions",
          'Precise obedience to God, even after years of faithful service',
          'Avoiding correction from God',
          'Leading only when conditions are easy',
        ],
        a: 1,
        e: "Even after decades of leadership, Moses faced serious consequences for disobeying God's specific instruction at the rock, teaching that leaders must remain obedient to God throughout their service, not only at the start.",
        h: "Consider what happened even late in Moses' long career of leadership.",
        yr: 'SS2',
      },
      {
        q: "What does Moses' initial reluctance at the burning bush teach Christians today?",
        o: [
          'God only uses naturally confident people',
          'God can use even hesitant, self-doubting people who are willing to obey',
          "Excuses are an acceptable reason to reject God's call",
          'Leadership requires no faith',
        ],
        a: 1,
        e: 'Moses offered several objections and expressed self-doubt at the burning bush, yet God equipped and used him powerfully, showing that willingness to obey matters more than natural confidence (Exodus 4:1-17).',
        h: "Recall Moses' objections about his speech and ability before he accepted the call.",
        yr: 'SS2',
      },
      {
        q: "The charge given to Joshua, 'Be strong and of good courage... for the Lord your God is with you,' is especially relevant to Christians who are:",
        o: [
          'Avoiding new responsibilities',
          "Facing new challenges or responsibilities and need confidence in God's presence",
          'Seeking material wealth',
          'Uninterested in spiritual growth',
        ],
        a: 1,
        e: "Joshua 1:9 encourages believers stepping into new, potentially daunting responsibilities to draw courage from the assurance of God's abiding presence, just as Joshua did before leading Israel into Canaan.",
        h: 'This verse is often quoted for encouragement when facing a new task or role.',
        yr: 'SS2',
      },
      {
        q: "Joshua's methodical, step-by-step conquest of Canaan (city by city, tribe by tribe) illustrates for Christians today the value of:",
        o: [
          'Impulsive decision-making',
          "Disciplined, faithful obedience carried out in stages under God's direction",
          "Ignoring God's instructions for convenience",
          'Avoiding careful planning',
        ],
        a: 1,
        e: "Joshua's campaign was carried out carefully and obediently according to God's instructions, illustrating that faithful leadership often requires patient, disciplined follow-through rather than shortcuts.",
        h: 'Consider how the conquest unfolded—was it a single event or a careful process?',
        yr: 'SS2',
      },
      {
        q: "Deborah's example is often cited in contemporary discussions about:",
        o: [
          'The abolition of the judiciary',
          "Women's capacity for spiritual and civic leadership",
          'The rejection of prophecy',
          'The unimportance of national leadership',
        ],
        a: 1,
        e: "As a prophetess and judge respected by the whole nation, including a military commander, Deborah's example is frequently referenced in discussions on women's leadership within both church and society.",
        h: "Think about what made Deborah's position in Judges especially notable.",
        yr: 'SS2',
      },
      {
        q: "Deborah's partnership with Barak, rather than leading the battle entirely alone, teaches Christians the value of:",
        o: [
          'Working in isolation',
          'Encouraging and empowering others to fulfil their own roles and calling',
          'Avoiding shared responsibility',
          'Distrust between leaders',
        ],
        a: 1,
        e: 'Deborah encouraged and directed Barak to lead the army rather than doing so herself, showing the importance of empowering others to step into their own calling under wise guidance.',
        h: 'Consider why Deborah involved Barak rather than commanding the army by herself.',
        yr: 'SS2',
      },
      {
        q: "A workplace application of Joseph's story would be:",
        o: [
          "Taking credit for others' work to advance quickly",
          'Remaining honest and diligent even when unfairly treated by an employer',
          'Resigning immediately at the first sign of unfair treatment',
          'Compromising personal ethics to please a superior',
        ],
        a: 1,
        e: "Joseph's integrity while enslaved and imprisoned — remaining diligent and honest despite unjust treatment — offers a model for Christians facing unfair treatment in the workplace today.",
        h: 'Think about how Joseph behaved even while unjustly treated by Potiphar and in prison.',
        yr: 'SS2',
      },
      {
        q: "Applying Moses' example, a Christian leader today who has served faithfully for many years should still:",
        o: [
          'Assume they are above correction',
          "Remain teachable and obedient to God's instructions",
          'Ignore accountability structures',
          'Expect no consequences for disobedience',
        ],
        a: 1,
        e: "Moses' experience shows that long service does not exempt a leader from the need for continued obedience and accountability before God.",
        h: 'Recall the consequence Moses faced despite decades of faithful leadership.',
        yr: 'SS2',
      },
      {
        q: "Applying Joshua's example, a new Christian leader facing an intimidating task should primarily:",
        o: [
          'Avoid the task altogether',
          "Trust in God's presence and act with courage and careful obedience",
          'Rely solely on personal ability',
          'Delay indefinitely until all fear disappears',
        ],
        a: 1,
        e: "Joshua's example encourages new leaders to step forward in courage, trusting God's presence, while remaining carefully obedient to His instructions — not waiting for fear to disappear entirely before acting.",
        h: "Think about the balance between courage and continued obedience in Joshua's approach.",
        yr: 'SS2',
      },
      {
        q: "Applying Deborah's example, church and community leaders today should:",
        o: [
          'Discourage women from taking up leadership roles',
          'Recognize and support capable, God-called leaders regardless of gender',
          'Ignore spiritual discernment in decision-making',
          'Avoid seeking counsel from prophetic voices',
        ],
        a: 1,
        e: "Deborah's God-given authority as judge and prophetess encourages contemporary leaders to recognize and support capable, faithful leaders whom God has equipped, without limiting leadership by gender.",
        h: "Consider what Deborah's respected authority implies about God's view of capable leadership.",
        yr: 'SS2',
      },
      {
        q: 'Which best captures the shared relevance of all four leaders to contemporary Christian life?',
        o: [
          'Leadership requires wealth and status',
          'Faithfulness, obedience, courage, and integrity remain essential qualities for godly leadership in any era',
          'Only ancient leaders can be examples for believers today',
          'Success in leadership depends on avoiding all hardship',
        ],
        a: 1,
        e: 'Though the settings differ vastly from today, the core qualities modelled by Joseph, Moses, Joshua and Deborah — faithfulness, obedience, courage and integrity — remain essential for Christian leadership in any generation.',
        h: 'Look for the quality that unites all four examples across different eras.',
        yr: 'SS2',
      },
      {
        q: 'What lesson from Joseph is most relevant to a Christian facing temptation to sin for personal advantage?',
        o: [
          'Compromise is sometimes necessary for advancement',
          "Resisting temptation, as Joseph did with Potiphar's wife, honours God even at personal cost",
          'Temptation should be embraced if no one will know',
          'Only leaders need to resist temptation',
        ],
        a: 1,
        e: "Joseph's refusal to sin with Potiphar's wife, despite the personal risk involved, models resisting temptation out of reverence for God rather than fear of being caught (Genesis 39:9).",
        h: "Recall Joseph's stated reason for refusing Potiphar's wife's advances.",
        yr: 'SS2',
      },
      {
        q: 'What lesson from Moses applies to Christians who feel unqualified for a task God is calling them to?',
        o: [
          'They should refuse the call permanently',
          'God equips those He calls, even when they feel inadequate',
          'Feeling unqualified proves God is not calling them',
          'Only naturally gifted people should serve God',
        ],
        a: 1,
        e: 'Moses repeatedly expressed feelings of inadequacy, yet God assured him of His presence and provided Aaron as a helper, showing that God equips those He calls despite their felt weaknesses.',
        h: "Recall how God responded to each of Moses' objections at the burning bush.",
        yr: 'SS2',
      },
      {
        q: 'What lesson from Joshua applies to a Christian entering a new season of responsibility, such as starting a ministry?',
        o: [
          'Success depends entirely on personal talent',
          "Courage grounded in God's presence and promises is essential for stepping into new responsibility",
          'New responsibilities should always be delayed',
          'Fear should be the primary guide for decision-making',
        ],
        a: 1,
        e: "Joshua's commissioning emphasizes courage rooted not in self-confidence but in the assurance of God's abiding presence — a principle directly applicable to anyone stepping into new ministry or responsibility.",
        h: 'Think about the source of the courage God commanded Joshua to have.',
        yr: 'SS2',
      },
      {
        q: 'What lesson from Deborah applies to Christian communities considering who should lead?',
        o: [
          'Leadership should be based on gender alone',
          "Leadership should be based on godly character, faithfulness, and God's calling",
          'Only prophets can hold leadership positions',
          'Military experience is required for all leaders',
        ],
        a: 1,
        e: "Deborah's example shows that leadership in God's economy is based on character, faithfulness and divine calling rather than gender or social convention.",
        h: 'Consider what actually qualified Deborah to lead, beyond her being a woman.',
        yr: 'SS2',
      },
      {
        q: "Which verse contains God's charge to Joshua to 'be strong and of good courage'?",
        o: ['Joshua 1:9', 'Genesis 12:1', 'Exodus 3:14', 'Judges 4:6'],
        a: 0,
        e: "Joshua 1:9 records God's charge to Joshua to be strong and courageous, assured of God's presence, as he took up leadership after Moses.",
        h: 'This verse is commonly quoted for encouragement facing new responsibility.',
        yr: 'SS2',
      },
      {
        q: "What does Deborah's story suggest about the relationship between prophecy and practical leadership?",
        o: [
          'That prophecy and practical leadership are unrelated',
          'That spiritual discernment (prophecy) can directly inform and guide practical decisions, including military ones',
          'That prophets should never be involved in national affairs',
          'That practical leadership requires no spiritual input',
        ],
        a: 1,
        e: "Deborah's dual role as prophetess and judge shows that spiritual discernment can directly inform practical, even military, leadership decisions.",
        h: "Consider how Deborah's prophetic gift connected to her practical guidance of Barak.",
        yr: 'SS2',
      },
      {
        q: 'What is the main takeaway for young Christians considering leadership roles today, drawn from studying all four leaders together?',
        o: [
          'Leadership should be avoided due to its difficulty',
          'Faithfulness and willingness to trust God matter more than natural qualifications alone',
          'Only the naturally gifted should ever lead',
          'Leadership always guarantees comfort and ease',
        ],
        a: 1,
        e: 'The combined study of Joseph, Moses, Joshua and Deborah teaches that faithfulness and trust in God, rather than natural qualification alone, are central to being used in leadership.',
        h: 'Consider the overall message these four very different leaders convey about what qualifies someone to lead.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'God Cares for His People – Protection',
    topicCode: 'SS2-CRS-04',
    module: "God's Providence",
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Throughout Israel\'s journey from Egypt to Canaan, God repeatedly demonstrated His <span class="learn-keyword">protection</span> and <span class="learn-keyword">care</span> for His people in the wilderness. This topic examines specific examples of divine protection and what they mean for divine care today.\n</div>\n\n<h3 class="learn-subheading">1. Protection at the Red Sea</h3>\n<p class="learn-p">When Pharaoh\'s army pursued the fleeing Israelites, God parted the Red Sea, allowing Israel to cross on dry ground while the pursuing Egyptian army was destroyed when the waters returned (Exodus 14:21-31). This event became Israel\'s defining memory of God\'s saving power.</p>\n\n<h3 class="learn-subheading">2. Provision of Manna and Quail</h3>\n<p class="learn-p">In the wilderness, when Israel had no food, God provided manna — bread from heaven — every morning, and quail for meat, sustaining the entire nation for forty years (Exodus 16:4-15, 35).</p>\n\n<h3 class="learn-subheading">3. Water from the Rock</h3>\n<p class="learn-p">When the people thirsted at Rephidim, God instructed Moses to strike the rock at Horeb, and water flowed out to satisfy the whole community (Exodus 17:1-7). A similar provision occurred later at Meribah (Numbers 20:8-11).</p>\n\n<h3 class="learn-subheading">4. The Pillar of Cloud and Fire</h3>\n<p class="learn-p">God guided and protected Israel continuously through a pillar of cloud by day and a pillar of fire by night, which also stood between Israel and the Egyptian army at the Red Sea, protecting them from attack (Exodus 13:21-22; 14:19-20).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Form of Protection</th><th>Scripture</th><th>Need Met</th></tr></thead>\n    <tbody>\n      <tr><td>Parting of the Red Sea</td><td>Exodus 14</td><td>Escape from Pharaoh\'s army</td></tr>\n      <tr><td>Manna and quail</td><td>Exodus 16</td><td>Daily food in the wilderness</td></tr>\n      <tr><td>Water from the rock</td><td>Exodus 17; Numbers 20</td><td>Water in a dry land</td></tr>\n      <tr><td>Pillar of cloud and fire</td><td>Exodus 13:21-22</td><td>Guidance and protection day and night</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Distinguish clearly between manna (bread-like food each morning) and quail (birds provided as meat), and know the two separate rock-striking incidents at Rephidim (Exodus 17) and Meribah (Numbers 20).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> God\'s repeated provision and protection in the wilderness — deliverance from enemies, daily food, water, and guidance — reveal His faithful, continual care for His people, a truth Christians can still rely on today.\n</div>\n',
    questions: [
      {
        q: "How did God protect Israel from Pharaoh's pursuing army at the Red Sea?",
        o: [
          "By sending an angel to fight Pharaoh's soldiers directly",
          'By parting the sea for Israel to cross and drowning the Egyptian army when the waters returned',
          'By making the Egyptians fall asleep',
          'By hiding Israel in a cave',
        ],
        a: 1,
        e: 'God parted the Red Sea, allowing Israel to cross on dry ground. When the pursuing Egyptian army followed, the waters returned and destroyed them (Exodus 14:21-28).',
        h: 'Recall the famous crossing on dry ground between walls of water.',
        yr: 'SS2',
      },
      {
        q: 'What was manna, and when did it appear?',
        o: [
          'Meat that appeared at sunset',
          'Bread-like food from heaven that appeared on the ground each morning',
          'Fruit that grew on wilderness trees',
          'Water that flowed from rocks',
        ],
        a: 1,
        e: 'Manna was a fine, flake-like substance that appeared on the ground with the morning dew, which the Israelites gathered and used as bread (Exodus 16:14-15, 31).',
        h: 'This provision appeared with the dew, not at night.',
        yr: 'SS2',
      },
      {
        q: 'What meat did God provide for Israel in the wilderness in addition to manna?',
        o: ['Fish', 'Quail', 'Lamb', 'Goat'],
        a: 1,
        e: 'God sent quail, covering the camp, to provide meat for the Israelites in addition to the manna (Exodus 16:13).',
        h: 'This bird arrived in great numbers around the Israelite camp.',
        yr: 'SS2',
      },
      {
        q: 'At Rephidim, how did God provide water for the thirsty Israelites?',
        o: [
          'By causing rain to fall',
          'By instructing Moses to strike a rock at Horeb, from which water flowed',
          'By leading them to a hidden river',
          'By turning bitter water sweet with a tree branch',
        ],
        a: 1,
        e: 'God told Moses to strike the rock at Horeb with his staff, and water gushed out for the people to drink (Exodus 17:5-6).',
        h: 'This provision involved a specific rock and a striking action with a staff.',
        yr: 'SS2',
      },
      {
        q: 'What guided the Israelites through the wilderness by day and by night?',
        o: [
          'A voice from heaven only',
          'A pillar of cloud by day and a pillar of fire by night',
          'A map given to Moses',
          'The stars alone',
        ],
        a: 1,
        e: 'God led Israel with a pillar of cloud during the day and a pillar of fire at night, providing continual guidance and visible assurance of His presence (Exodus 13:21-22).',
        h: 'Two forms of the same guiding presence appeared depending on the time of day.',
        yr: 'SS2',
      },
      {
        q: 'How did the pillar of cloud and fire also serve a protective function at the Red Sea?',
        o: [
          'It blinded the Israelites',
          'It moved between the Israelites and the Egyptian army, giving light to Israel but darkness to the Egyptians',
          'It caused a storm that scattered both armies',
          'It had no protective role there',
        ],
        a: 1,
        e: 'The pillar moved behind Israel, standing between them and the pursuing Egyptians, giving darkness to the Egyptians while lighting the way for Israel throughout that night (Exodus 14:19-20).',
        h: 'This pillar acted as a barrier as well as a guide during the crossing.',
        yr: 'SS2',
      },
      {
        q: 'At Meribah, what mistake did Moses make while obtaining water from the rock?',
        o: [
          'He refused to obtain water at all',
          'He struck the rock instead of speaking to it as God commanded',
          'He asked Aaron to do it instead',
          'He used the wrong staff',
        ],
        a: 1,
        e: 'At Meribah, God instructed Moses to speak to the rock, but in his frustration with the people, Moses struck it twice instead, which was an act of disobedience (Numbers 20:8-11).',
        h: "Recall the difference between God's instruction and what Moses actually did.",
        yr: 'SS2',
      },
      {
        q: "What does the manna's daily provision teach about God's care?",
        o: [
          'God provides only once and expects self-sufficiency afterward',
          'God provides for daily needs, encouraging ongoing trust rather than hoarding',
          'The Israelites were expected to store manna for years',
          'Manna was unreliable and inconsistent',
        ],
        a: 1,
        e: 'Manna appeared fresh each morning and generally could not be stored overnight (except before the Sabbath), teaching Israel to depend on God daily rather than relying on their own stockpiles (Exodus 16:19-26).',
        h: 'Consider why the Israelites had to gather manna every single day.',
        yr: 'SS2',
      },
      {
        q: "Why is the Red Sea crossing considered a defining act of God's protection for Israel?",
        o: [
          'It only benefited a small group of people',
          "It delivered the entire nation from certain destruction by Pharaoh's army",
          'It happened without any danger involved',
          'It occurred before Israel left Egypt',
        ],
        a: 1,
        e: "The Red Sea crossing rescued the whole nation of Israel from what seemed like certain destruction, becoming a foundational memory of God's saving power referenced throughout the rest of Scripture.",
        h: 'Consider the scale of the threat Israel faced and how completely God delivered them.',
        yr: 'SS2',
      },
      {
        q: "Which of these best describes the pattern seen in God's provision throughout the wilderness journey?",
        o: [
          'God provided once and then withdrew support entirely',
          "God consistently met Israel's needs for safety, food, water and guidance as they arose",
          'God only helped Israel when Moses complained',
          "God's provision depended on Israel's wealth",
        ],
        a: 1,
        e: 'Across many different needs — protection from enemies, food, water, and direction — God consistently and repeatedly provided for Israel throughout their wilderness journey.',
        h: 'Look at the variety of needs God met throughout the account.',
        yr: 'SS2',
      },
      {
        q: "The two separate 'water from the rock' incidents (Exodus 17 and Numbers 20) mainly differ in that:",
        o: [
          'The first happened at Meribah and the second at Rephidim',
          'The first was at the start of the wilderness journey (Rephidim) and involved striking; the second (Meribah) required speaking, but Moses struck instead',
          'Both required the exact same action with no difference',
          "Neither involved Moses' staff",
        ],
        a: 1,
        e: 'The Exodus 17 incident at Rephidim/Horeb involved God commanding Moses to strike the rock; the Numbers 20 incident at Meribah required Moses to speak to the rock, but he struck it instead, leading to consequences.',
        h: "Compare God's instructions in each incident carefully — one required striking, the other speaking.",
        yr: 'SS2',
      },
      {
        q: "What is the theological significance of God's protection in the wilderness for understanding His character?",
        o: [
          'God is distant and uninvolved in daily human need',
          "God is a faithful, providing God who actively cares for His people's practical needs",
          "God's care was limited only to spiritual matters",
          "God's protection was inconsistent and unreliable",
        ],
        a: 1,
        e: "The wilderness narratives reveal God as intimately involved in meeting His people's practical, everyday needs — not merely a distant deity concerned only with abstract spiritual matters.",
        h: "Consider what these repeated, practical provisions reveal about God's nature.",
        yr: 'SS2',
      },
      {
        q: "How does Israel's experience of God's wilderness protection relate to New Testament teaching on God's care?",
        o: [
          'It has no connection to New Testament teaching',
          "It parallels Jesus' teaching not to worry, since God knows and provides for His people's needs (Matthew 6:25-33)",
          'It contradicts New Testament teaching on provision',
          'It only applies to the physically wealthy',
        ],
        a: 1,
        e: "Jesus' teaching in Matthew 6:25-33 about not worrying over food, drink, or clothing echoes the same truth Israel experienced in the wilderness — that God knows and provides for His people's needs.",
        h: "Think of Jesus' teaching about the birds of the air and the lilies of the field.",
        yr: 'SS2',
      },
      {
        q: "Which statement best reflects a Christian application of God's wilderness protection today?",
        o: [
          'Christians should expect life to be free of all hardship',
          'Christians can trust God to provide and protect them even through difficult, uncertain circumstances',
          'Divine provision ended with the Old Testament',
          'Trusting God removes any need for personal responsibility',
        ],
        a: 1,
        e: "Just as God faithfully provided for and protected Israel through the uncertainties of the wilderness, Christians today are encouraged to trust God's care through their own difficult or uncertain circumstances.",
        h: "Think about how Israel's wilderness experience is often applied as an encouragement to believers facing hardship.",
        yr: 'SS2',
      },
      {
        q: 'Why might God have allowed Israel to experience hunger and thirst before providing manna, quail, and water?',
        o: [
          'Because He was unable to provide immediately',
          'To teach Israel to trust and depend on Him rather than on their own resources',
          'Because He was punishing them for no reason',
          'Because the wilderness had no other purpose',
        ],
        a: 1,
        e: "The pattern of need followed by provision served a teaching purpose — deepening Israel's dependence on and trust in God rather than self-sufficiency (see also Deuteronomy 8:2-3, which explains this purpose).",
        h: 'Consider the purpose behind allowing a need to arise before meeting it.',
        yr: 'SS2',
      },
      {
        q: 'Deuteronomy 8:2-3 reflects on the wilderness experience by teaching that:',
        o: [
          'Manna was unnecessary and could have been avoided',
          'God humbled and tested Israel to teach that people do not live by bread alone but by every word from God',
          'The wilderness journey served no spiritual purpose',
          'Israel should have avoided depending on God',
        ],
        a: 1,
        e: 'Moses later reflected that God allowed hunger before providing manna specifically to teach Israel that human life depends not only on physical bread but on obedience to every word from God (Deuteronomy 8:2-3).',
        h: 'This later reflection explains the deeper spiritual lesson behind the physical provision.',
        yr: 'SS2',
      },
      {
        q: "What overall lesson about God's care can Christians draw from the combination of Red Sea deliverance, manna, water from the rock, and the guiding pillar?",
        o: [
          "God's care is unpredictable and cannot be relied upon",
          "God's care is comprehensive, meeting protection, physical, and guidance needs together",
          'God only cares about spiritual needs, not physical ones',
          "God's care in the Old Testament has no bearing on believers today",
        ],
        a: 1,
        e: "Taken together, these events show that God's care for His people is comprehensive — covering protection from danger, physical sustenance, and ongoing guidance — a pattern that continues to encourage believers' trust in God today.",
        h: 'Consider the range of different needs God met for Israel across these events.',
        yr: 'SS2',
      },
      {
        q: "Which best explains why the Israelites' complaints in the wilderness, despite God's repeated care, are viewed critically in Scripture?",
        o: [
          'Because complaining was completely justified given real hardship with no help from God',
          "Because they revealed persistent unbelief despite abundant evidence of God's faithful provision",
          'Because Moses encouraged the complaints',
          'Because complaining had no spiritual significance',
        ],
        a: 1,
        e: "Scripture presents Israel's repeated grumbling as a serious failure of faith, precisely because it occurred despite clear, repeated evidence of God's provision and protection (see 1 Corinthians 10:9-10 for a New Testament reflection on this).",
        h: 'Consider how the complaints looked in light of how much God had already done for them.',
        yr: 'SS2',
      },
      {
        q: "A modern Christian application of the lesson of manna ('daily bread') is:",
        o: [
          'Stockpiling resources out of distrust for the future',
          "Praying and trusting God daily, as reflected in the Lord's Prayer's request for 'daily bread'",
          'Avoiding all planning for the future',
          'Ignoring physical needs entirely',
        ],
        a: 1,
        e: "The manna pattern of daily dependence is echoed in the Lord's Prayer's request, 'Give us this day our daily bread' (Matthew 6:11), encouraging ongoing daily trust in God's provision.",
        h: "Think of the line in the Lord's Prayer about daily provision.",
        yr: 'SS2',
      },
      {
        q: 'What happened to manna if the Israelites tried to keep it overnight (except before the Sabbath)?',
        o: [
          'It multiplied further',
          'It bred worms and became foul, as a rule',
          'It turned to gold',
          'It remained perfectly edible indefinitely',
        ],
        a: 1,
        e: "Except for the day before the Sabbath, manna kept overnight generally bred worms and became foul, teaching Israel to trust God's daily provision (Exodus 16:19-20).",
        h: 'This detail reinforced the lesson of daily dependence on God.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Parental Responsibility (Eli and His Sons)',
    topicCode: 'SS2-CRS-05',
    module: 'Christian Family Life',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The account of <span class="learn-keyword">Eli</span> and his sons, Hophni and Phinehas, is a sobering biblical lesson on <span class="learn-keyword">parental responsibility</span>. It shows the serious consequences that can follow when parents fail to correct and discipline their children.\n</div>\n\n<h3 class="learn-subheading">1. Who Was Eli?</h3>\n<p class="learn-p">Eli was the high priest of Israel and judge at Shiloh, where the Ark of the Covenant and tabernacle were kept. His sons, Hophni and Phinehas, served as priests under him but were described as "corrupt" and had no regard for the Lord (1 Samuel 2:12).</p>\n\n<h3 class="learn-subheading">2. The Sins of Hophni and Phinehas</h3>\n<p class="learn-p">Hophni and Phinehas abused their priestly office: they took the best portions of sacrificial meat for themselves by force before it was properly offered to God (1 Samuel 2:12-17), and they committed sexual immorality with women who served at the entrance of the tabernacle (1 Samuel 2:22).</p>\n\n<h3 class="learn-subheading">3. Eli\'s Failure as a Parent</h3>\n<p class="learn-p">Though Eli verbally rebuked his sons for their wickedness (1 Samuel 2:23-25), he failed to remove them from their priestly duties or take decisive corrective action. God later told Eli, through both a man of God and the boy Samuel, that Eli honoured his sons above God because he did not restrain them despite knowing their sin (1 Samuel 2:29; 3:13).</p>\n\n<h3 class="learn-subheading">4. The Consequences</h3>\n<p class="learn-p">Because of Eli\'s failure to discipline his sons, God pronounced judgment on his household: both Hophni and Phinehas would die on the same day, the priesthood would be taken from Eli\'s family line, and Israel would suffer defeat with the Ark of the Covenant captured by the Philistines (1 Samuel 2:34; 4:10-11, 17-18). Eli himself died from shock upon hearing the news.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Aspect</th><th>Detail</th></tr></thead>\n    <tbody>\n      <tr><td>Eli\'s role</td><td>High priest and judge of Israel at Shiloh</td></tr>\n      <tr><td>Sons\' sins</td><td>Stealing sacrificial meat; sexual immorality; contempt for God\'s offerings</td></tr>\n      <tr><td>Eli\'s failure</td><td>Rebuked but did not decisively restrain or remove them</td></tr>\n      <tr><td>Consequence</td><td>Death of both sons in one day; loss of priesthood from his line; Israel\'s defeat</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> A common exam question asks what specific sins Hophni and Phinehas committed — remember both the abuse of sacrificial meat and their immorality. Also know that it was young Samuel who delivered part of God\'s judgment message to Eli (1 Samuel 3:11-14).</strong>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Eli\'s story teaches that parents bear real responsibility to correct and restrain their children\'s wrongdoing. Mere verbal rebuke without decisive action is insufficient, and neglect of parental duty carries serious spiritual and practical consequences.\n</div>\n',
    questions: [
      {
        q: 'What position did Eli hold in Israel?',
        o: [
          'King',
          'High priest and judge at Shiloh',
          'A military commander',
          'A foreign ambassador',
        ],
        a: 1,
        e: 'Eli served as high priest and judge of Israel, based at Shiloh, where the tabernacle and the Ark of the Covenant were kept (1 Samuel 1:9; 4:18).',
        h: 'His role combined religious leadership with civic judgment.',
        yr: 'SS2',
      },
      {
        q: "What were the names of Eli's two sons who served as priests?",
        o: ['Samuel and Saul', 'Hophni and Phinehas', 'Nadab and Abihu', 'Ahimelech and Abiathar'],
        a: 1,
        e: "Eli's sons, Hophni and Phinehas, served as priests at Shiloh under their father but were described as corrupt and having no regard for the Lord (1 Samuel 1:3; 2:12).",
        h: "These two brothers are named early in 1 Samuel as Eli's sons.",
        yr: 'SS2',
      },
      {
        q: 'How did Hophni and Phinehas abuse the sacrificial offerings brought to the tabernacle?',
        o: [
          'They refused to accept any offerings',
          'They took the best portions of meat for themselves by force before it was properly offered to God',
          'They gave all the meat away to the poor',
          'They burned all offerings immediately',
        ],
        a: 1,
        e: "Contrary to the proper procedure, Hophni and Phinehas used a fork to seize the best meat for themselves before the fat was burned as an offering to God, treating the Lord's offering with contempt (1 Samuel 2:12-17).",
        h: "Their sin involved taking priestly privileges by force rather than following God's prescribed order.",
        yr: 'SS2',
      },
      {
        q: "Besides misusing sacrificial offerings, what other serious sin did Eli's sons commit?",
        o: [
          'Idol worship',
          'Sexual immorality with women who served at the tabernacle entrance',
          'Theft from the royal treasury',
          'Murder of fellow priests',
        ],
        a: 1,
        e: 'Hophni and Phinehas also engaged in sexual immorality with women who served at the entrance of the tent of meeting, a serious violation of their priestly duty (1 Samuel 2:22).',
        h: 'This sin involved improper relationships connected to their position at the tabernacle.',
        yr: 'SS2',
      },
      {
        q: "How did Eli initially respond upon learning of his sons' wrongdoing?",
        o: [
          'He immediately removed them from priestly office',
          'He verbally rebuked them but took no further decisive action',
          'He praised their conduct',
          'He reported them to the Philistines',
        ],
        a: 1,
        e: 'Eli confronted his sons verbally, warning them of the seriousness of sinning against God, but he failed to take further decisive corrective action, such as removing them from their duties (1 Samuel 2:23-25).',
        h: 'Consider the difference between speaking to someone and actually stopping their behaviour.',
        yr: 'SS2',
      },
      {
        q: "What did God's message, delivered by a 'man of God,' accuse Eli of doing?",
        o: [
          'Failing to build the tabernacle',
          'Honouring his sons above God by not restraining their sin',
          'Refusing to offer sacrifices',
          'Leading Israel into idol worship personally',
        ],
        a: 1,
        e: 'The unnamed man of God told Eli that he honoured his sons above God because he allowed them to continue in sin, benefiting from the offerings, without decisively restraining them (1 Samuel 2:27-29).',
        h: "The accusation concerned Eli's priorities — his sons versus obedience to God.",
        yr: 'SS2',
      },
      {
        q: "Who else delivered part of God's judgment message concerning Eli's household?",
        o: ['The prophet Nathan', 'The young boy Samuel', 'King Saul', 'The priest Ahimelech'],
        a: 1,
        e: "God spoke to young Samuel at night, revealing that He was about to judge Eli's house because Eli knew of his sons' sin and failed to restrain them (1 Samuel 3:11-14).",
        h: "This messenger was a child being raised in the tabernacle under Eli's care.",
        yr: 'SS2',
      },
      {
        q: 'What judgment did God pronounce regarding Hophni and Phinehas?',
        o: [
          'They would be exiled but live long lives',
          'Both would die on the same day',
          'They would be forgiven without consequence',
          'Only one of them would die',
        ],
        a: 1,
        e: "God declared that both Hophni and Phinehas would die on the same day as a sign confirming the judgment against Eli's household (1 Samuel 2:34), which was fulfilled during the battle with the Philistines (1 Samuel 4:11).",
        h: 'The judgment specified a shared fate for both brothers occurring simultaneously.',
        yr: 'SS2',
      },
      {
        q: 'What happened to the Ark of the Covenant during the battle in which Hophni and Phinehas died?',
        o: [
          'It was destroyed by fire',
          'It was captured by the Philistines',
          'It was hidden safely by Eli',
          'It remained untouched in Shiloh',
        ],
        a: 1,
        e: 'During the battle with the Philistines, Israel was defeated, Hophni and Phinehas were killed, and the Ark of the Covenant was captured by the Philistines (1 Samuel 4:10-11).',
        h: "This sacred object, central to Israel's worship, was taken as a spoil of war.",
        yr: 'SS2',
      },
      {
        q: 'How did Eli die upon hearing the news of the battle?',
        o: [
          'He was killed in the battle himself',
          'He fell backward off his seat, broke his neck, and died upon hearing the Ark was captured',
          'He died of old age unrelated to the news',
          'He was executed by the Philistines',
        ],
        a: 1,
        e: 'When Eli heard that the Ark of God had been captured, he fell backward off his seat by the gate, broke his neck, and died — he was old and heavy, and the shock of the news was fatal (1 Samuel 4:17-18).',
        h: 'The specific cause of death was linked directly to the shocking news he received.',
        yr: 'SS2',
      },
      {
        q: "What long-term consequence did God's judgment have on Eli's priestly line?",
        o: [
          'It had no lasting effect',
          "The priesthood was taken away from Eli's descendants",
          "Eli's descendants became kings instead",
          "Eli's family was given greater honour afterward",
        ],
        a: 1,
        e: "God declared that the priesthood would be removed from Eli's family line as a consequence of his failure to restrain his sons' wickedness (1 Samuel 2:30-33), a judgment fulfilled generations later.",
        h: "Consider what happened to the priestly privilege that had belonged to Eli's family.",
        yr: 'SS2',
      },
      {
        q: "What is the central parental failure illustrated by Eli's story?",
        o: [
          'Failing to provide financially for his sons',
          'Failing to decisively discipline and restrain his sons despite knowing their sin',
          'Failing to educate his sons in a trade',
          'Failing to arrange marriages for his sons',
        ],
        a: 1,
        e: "Eli's central failure was not ignorance of his sons' wrongdoing but his failure to take firm, decisive action to stop it despite knowing about it and even warning them verbally.",
        h: 'The issue was not lack of awareness, but lack of decisive correction.',
        yr: 'SS2',
      },
      {
        q: "What broader lesson does Eli's story teach about parental responsibility?",
        o: [
          "Parents bear no responsibility for their grown children's actions",
          'Parents are responsible for actively correcting wrongdoing, not merely expressing disapproval',
          'Verbal warnings alone are always sufficient discipline',
          'Parental discipline is unnecessary once children are adults',
        ],
        a: 1,
        e: 'The narrative teaches that genuine parental responsibility requires decisive correction and restraint of wrongdoing, not merely verbal disapproval that is not backed by action.',
        h: 'Consider what Eli did versus what God expected him to do.',
        yr: 'SS2',
      },
      {
        q: "How does Eli's failure compare with the description of Samuel's later role in raising his own sons (1 Samuel 8:1-3)?",
        o: [
          "Samuel's sons were perfectly righteous, unlike Eli's sons",
          "Interestingly, Samuel's own sons also turned out corrupt, showing this is a recurring human struggle",
          'Samuel had no children of his own',
          "Samuel's failure was worse than Eli's",
        ],
        a: 1,
        e: "Notably, even Samuel's own sons, Joel and Abijah, turned to dishonest gain and perverted justice as judges (1 Samuel 8:1-3), showing that raising godly children faithfully remains a challenge even for godly leaders.",
        h: "This connects Eli's story to a similar later account involving Samuel's own sons.",
        yr: 'SS2',
      },
      {
        q: "Why is the phrase 'you honour your sons more than me' (1 Samuel 2:29) significant?",
        o: [
          'It shows Eli openly worshipped his sons as gods',
          'It reveals that failing to correct sin, even out of parental affection, can amount to placing family above obedience to God',
          'It has no real theological significance',
          "It refers only to Eli's financial generosity toward his sons",
        ],
        a: 1,
        e: "This phrase highlights that Eli's failure to restrain his sons, even if motivated by parental affection, effectively placed loyalty to his sons above obedience to God's standards — a serious spiritual failure.",
        h: 'Consider what it means to value a relationship above obedience to God.',
        yr: 'SS2',
      },
      {
        q: "What Christian principle about discipline is illustrated by contrasting Eli's rebuke with his lack of action?",
        o: [
          'Rebuke alone, without corrective action, is often insufficient to address serious wrongdoing',
          'Rebuke is unnecessary if action is taken',
          'Discipline should never involve any verbal correction',
          'Only physical punishment counts as discipline',
        ],
        a: 0,
        e: "Eli's story shows that words of correction without follow-through action were inadequate to address serious sin — genuine discipline requires both instruction and decisive enforcement.",
        h: 'Think about the gap between what Eli said to his sons and what he actually did.',
        yr: 'SS2',
      },
      {
        q: "How is Eli's failure relevant to Christian parents today?",
        o: [
          'It suggests parents should be harsh and unloving toward their children',
          "It warns parents against excusing or tolerating their children's wrongdoing out of misplaced affection or convenience",
          'It suggests discipline has no spiritual consequence',
          'It only applies to parents whose children serve in religious roles',
        ],
        a: 1,
        e: "Eli's account warns contemporary Christian parents against tolerating or excusing their children's wrongdoing out of affection, convenience, or reluctance to confront, since such neglect carries real spiritual and practical consequences.",
        h: 'Consider what timeless warning this ancient story offers to parents in any era.',
        yr: 'SS2',
      },
      {
        q: "What does the death of Hophni and Phinehas 'on the same day' primarily signify within the narrative?",
        o: [
          "A coincidence unrelated to God's judgment",
          "The precise fulfilment of the prophecy God had given as a sign of judgment on Eli's household",
          "A punishment unrelated to their father's failure",
          'An accident during unrelated combat',
        ],
        a: 1,
        e: "Their simultaneous deaths fulfilled the specific sign God had given earlier through the man of God, confirming that the judgment on Eli's household was indeed from God (1 Samuel 2:34; 4:11).",
        h: 'Recall that this specific detail had been predicted in advance as confirmation of judgment.',
        yr: 'SS2',
      },
      {
        q: 'Which best summarizes the overall theological lesson of the Eli narrative?',
        o: [
          'God is indifferent to how parents raise their children',
          'Neglecting to correct wrongdoing, even by respected leaders, brings serious consequences before God',
          'Only children bear responsibility for their own sins, never their parents',
          "Priestly office guarantees protection from God's judgment",
        ],
        a: 1,
        e: 'The Eli narrative teaches that neglecting the responsibility to correct wrongdoing — even by an otherwise respected religious leader — brings serious consequences, underscoring that God holds parents and leaders accountable for how they exercise their responsibility.',
        h: 'Consider the overall message the narrative conveys about accountability before God.',
        yr: 'SS2',
      },
      {
        q: "What was Eli's physical reaction described alongside his death, beyond falling backward?",
        o: [
          'He was very old and heavy',
          'He was young and athletic',
          'He had been fasting for days',
          'He had just returned from battle',
        ],
        a: 0,
        e: 'Scripture notes Eli was old and heavy, which contributed to his death when he fell backward off his seat upon hearing the shocking news (1 Samuel 4:18).',
        h: 'This physical detail explains why the fall proved fatal.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Consequences of Obedience and Disobedience',
    topicCode: 'SS2-CRS-06',
    module: 'Christian Family Life',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The Bible presents numerous examples showing that <span class="learn-keyword">obedience</span> to God brings blessing, while <span class="learn-keyword">disobedience</span> brings punishment or loss. This topic surveys key biblical examples of both outcomes.\n</div>\n\n<h3 class="learn-subheading">1. Examples of Obedience and Its Blessings</h3>\n<p class="learn-p">Abraham\'s willingness to obey God\'s call to leave his homeland (Genesis 12:1-4) and his readiness to offer Isaac (Genesis 22:1-18) led to God\'s covenant blessing upon him and his descendants. Noah\'s obedience in building the ark exactly as God instructed preserved his family and animal life through the flood (Genesis 6:22; 7:5). The Israelites\' obedient march around Jericho led to victory (Joshua 6).</p>\n\n<h3 class="learn-subheading">2. Examples of Disobedience and Its Punishment</h3>\n<p class="learn-p">Adam and Eve\'s disobedience in eating the forbidden fruit brought sin, suffering, and expulsion from Eden into the world (Genesis 3:6-24). King Saul\'s disobedience in not fully destroying the Amalekites and their livestock, as God commanded, led to his rejection as king (1 Samuel 15:1-23). Achan\'s disobedience in taking devoted plunder from Jericho brought defeat and death upon himself and his family (Joshua 7).</p>\n\n<h3 class="learn-subheading">3. The Principle Behind Blessing and Punishment</h3>\n<p class="learn-p">Scripture consistently presents obedience to God as the pathway to blessing, protection, and fruitfulness, while disobedience brings judgment, loss, or separation from God\'s favour. Deuteronomy 28 sets out this principle explicitly, listing blessings for obedience and curses for disobedience.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Person(s)</th><th>Action</th><th>Result</th></tr></thead>\n    <tbody>\n      <tr><td>Noah</td><td>Obeyed and built the ark as instructed</td><td>Preserved from the flood</td></tr>\n      <tr><td>Abraham</td><td>Obeyed God\'s call and command regarding Isaac</td><td>Received covenant blessing</td></tr>\n      <tr><td>Adam and Eve</td><td>Disobeyed by eating forbidden fruit</td><td>Sin, suffering, expulsion from Eden</td></tr>\n      <tr><td>Saul</td><td>Disobeyed by sparing Amalekite king and livestock</td><td>Rejected as king by God</td></tr>\n      <tr><td>Achan</td><td>Disobeyed by keeping devoted plunder</td><td>Israel\'s defeat at Ai; his own death</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Deuteronomy 28 is a key reference for "blessings for obedience, curses for disobedience" questions. Also be ready to cite Samuel\'s statement to Saul: "To obey is better than sacrifice" (1 Samuel 15:22).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Throughout the Bible, obedience to God consistently brings blessing while disobedience brings serious consequences, teaching believers the importance of wholehearted submission to God\'s instructions.\n</div>\n',
    questions: [
      {
        q: "What was the outcome of Noah's obedience in building the ark exactly as God instructed?",
        o: [
          'He and his family were destroyed with everyone else',
          'He and his family, along with the animals, were preserved through the flood',
          'He was rewarded with material wealth only',
          'He became king after the flood',
        ],
        a: 1,
        e: "Noah's exact obedience to God's detailed instructions for building the ark resulted in the preservation of his family and representative animal life through the flood (Genesis 6:22; 7:1, 5).",
        h: 'Recall what happened to those inside the ark when the flood came.',
        yr: 'SS2',
      },
      {
        q: "What blessing did Abraham receive as a result of his obedience to God's call and command?",
        o: [
          'Immediate wealth with no further promise',
          'A covenant promise of descendants, land, and blessing for all nations through him',
          'Exemption from all future hardship',
          'Kingship over Egypt',
        ],
        a: 1,
        e: "Because of Abraham's obedience — leaving his homeland and later being willing to offer Isaac — God confirmed His covenant promise of numerous descendants, land, and blessing to all nations through him (Genesis 12:1-3; 22:15-18).",
        h: 'This promise extended beyond Abraham to future generations and even other nations.',
        yr: 'SS2',
      },
      {
        q: "What was the direct consequence of Adam and Eve's disobedience in the Garden of Eden?",
        o: [
          'They received greater wisdom with no negative effect',
          'Sin entered the world, bringing suffering, death, and expulsion from Eden',
          'They were immediately forgiven with no consequence',
          'God rewarded their curiosity',
        ],
        a: 1,
        e: "Adam and Eve's disobedience in eating the forbidden fruit brought sin into the world along with suffering, broken relationship with God, and expulsion from the Garden of Eden (Genesis 3:6-24).",
        h: 'This first act of disobedience had far-reaching, lasting consequences.',
        yr: 'SS2',
      },
      {
        q: 'Why was King Saul rejected as king by God?',
        o: [
          'He refused to fight the Amalekites at all',
          "He disobeyed God's command to completely destroy the Amalekites and their livestock, sparing the king and the best animals",
          'He built an unauthorized temple',
          'He appointed the wrong priests',
        ],
        a: 1,
        e: "God commanded Saul to utterly destroy the Amalekites and their livestock, but Saul spared King Agag and the best of the animals, disobeying God's explicit instruction. As a result, God rejected him as king (1 Samuel 15:1-23).",
        h: 'His disobedience involved sparing things God had commanded to be destroyed.',
        yr: 'SS2',
      },
      {
        q: 'What memorable statement did Samuel make to Saul regarding obedience?',
        o: [
          "'Sacrifice is better than obedience'",
          "'To obey is better than sacrifice'",
          "'Kings need not obey prophets'",
          "'Obedience is unnecessary for leaders'",
        ],
        a: 1,
        e: "Samuel rebuked Saul with the well-known statement, 'To obey is better than sacrifice, and to heed than the fat of rams,' emphasizing that obedience matters more to God than ritual offerings offered instead of compliance (1 Samuel 15:22).",
        h: 'This famous line contrasts obedience with sacrificial ritual.',
        yr: 'SS2',
      },
      {
        q: "What led to Israel's unexpected defeat at Ai under Joshua's leadership?",
        o: [
          'Poor battle strategy alone',
          "Achan's disobedience in keeping devoted plunder from Jericho for himself",
          'A lack of soldiers',
          "Joshua's failure to pray before battle",
        ],
        a: 1,
        e: "Achan secretly kept some of the devoted items from Jericho that God had commanded to be destroyed or given to the treasury, and this disobedience brought God's displeasure, resulting in Israel's defeat at Ai (Joshua 7:1, 11-12).",
        h: "One man's hidden disobedience affected the outcome for the whole army.",
        yr: 'SS2',
      },
      {
        q: 'What ultimately happened to Achan and his family because of his disobedience?',
        o: [
          'They were forgiven with no consequence',
          'They were stoned to death and their belongings burned',
          'They were exiled but allowed to live',
          'They were made servants in the tabernacle',
        ],
        a: 1,
        e: "Once Achan's sin was exposed, he and his family were stoned and his possessions burned, removing the sin from the camp so Israel could resume God's favour and defeat Ai (Joshua 7:24-26).",
        h: 'The judgment was severe, aimed at removing sin from the whole community.',
        yr: 'SS2',
      },
      {
        q: 'Which Old Testament book most systematically lists blessings for obedience and curses for disobedience?',
        o: ['Genesis', 'Deuteronomy (chapter 28)', 'Ruth', 'Esther'],
        a: 1,
        e: "Deuteronomy 28 provides an extensive, systematic list of blessings that would follow obedience to God's covenant and curses that would follow disobedience.",
        h: "This chapter appears in the book associated with Moses' final instructions to Israel before entering Canaan.",
        yr: 'SS2',
      },
      {
        q: "According to Deuteronomy 28, what generally happens to those who obey God's commandments?",
        o: [
          'Nothing changes in their circumstances',
          'They receive blessings in various areas of life, including family, harvest, and protection from enemies',
          'They are guaranteed to become wealthy kings',
          'They are exempt from all future testing',
        ],
        a: 1,
        e: 'Deuteronomy 28:1-14 promises a range of blessings for obedience, including blessings on family, agricultural produce, livestock, and protection from enemies.',
        h: 'This passage lists specific, practical areas of life that would be blessed.',
        yr: 'SS2',
      },
      {
        q: "According to Deuteronomy 28, what generally happens to those who persistently disobey God's commandments?",
        o: [
          'They receive automatic forgiveness regardless of their actions',
          'They experience curses affecting various areas of life, including defeat, disease, and hardship',
          'Nothing negative results from disobedience',
          'Only their descendants are affected, never themselves',
        ],
        a: 1,
        e: 'Deuteronomy 28:15-68 lists numerous curses that would follow persistent disobedience, including defeat by enemies, disease, and general hardship.',
        h: 'This section balances the earlier blessings by listing corresponding negative consequences.',
        yr: 'SS2',
      },
      {
        q: 'What common principle links the stories of Noah, Abraham, Adam and Eve, Saul, and Achan?',
        o: [
          "God's response to human choices is random and unpredictable",
          'Obedience to God consistently brings blessing, while disobedience brings serious consequences',
          'Only prominent leaders face consequences for disobedience',
          'Consequences only apply in the Old Testament, not today',
        ],
        a: 1,
        e: "Across all these accounts, a consistent biblical principle emerges: obedience to God's instructions leads to blessing and preservation, while disobedience leads to judgment or loss, regardless of the person's status.",
        h: 'Look at what unites these very different stories from across the Old Testament.',
        yr: 'SS2',
      },
      {
        q: 'Why is partial obedience, as shown by Saul, treated seriously as disobedience in Scripture?',
        o: [
          'Partial obedience is considered equal to full obedience',
          'Partial obedience still falls short of what God explicitly commanded, and God requires complete compliance',
          'God does not notice partial compliance',
          'Partial obedience is always acceptable if done for good reasons',
        ],
        a: 1,
        e: "Saul's case shows that doing part of what God commanded, while disregarding the rest — even with seemingly good intentions like sacrificing the spared animals — is still treated as disobedience because it falls short of full compliance (1 Samuel 15:13-23).",
        h: "Consider Saul's excuse that he spared the animals 'to sacrifice to the Lord' — was this accepted?",
        yr: 'SS2',
      },
      {
        q: "What does Achan's story teach about the effect of hidden or secret disobedience?",
        o: [
          'Hidden sin has no real consequence as long as it is not discovered',
          'Hidden sin can still bring real, serious consequences, even affecting others beyond the individual',
          'Only publicly known sins matter to God',
          'Secret disobedience is impossible to detect or judge',
        ],
        a: 1,
        e: "Though Achan kept his disobedience secret at first, it still brought God's displeasure and affected the entire nation's fortunes in battle, showing that hidden sin still carries real consequences before God.",
        h: "Consider that Achan's sin was not publicly known until after Israel's defeat.",
        yr: 'SS2',
      },
      {
        q: 'How does the principle of obedience and disobedience in the Old Testament relate to New Testament teaching?',
        o: [
          'The New Testament rejects the importance of obedience entirely',
          'The New Testament likewise teaches that genuine faith is demonstrated through obedience (e.g., James 2:17-18; John 14:15)',
          'Obedience is only relevant to the Old Testament covenant',
          'New Testament believers are exempt from any consequence of disobedience',
        ],
        a: 1,
        e: "The New Testament continues to emphasize obedience as evidence of genuine faith and love for God — for example, Jesus' statement, 'If you love me, keep my commandments' (John 14:15), and James' teaching that faith without works is dead (James 2:17-18).",
        h: 'Consider how obedience continues to matter for believers even after the Old Testament.',
        yr: 'SS2',
      },
      {
        q: 'What practical lesson can a Christian family draw from the contrasting outcomes of obedience and disobedience in Scripture?',
        o: [
          "Obedience to God's principles brings blessing to a household, while ignoring them invites hardship",
          'Family outcomes have nothing to do with obedience to God',
          'Only individuals, never families, are affected by obedience or disobedience',
          'Obedience matters only in worship settings, not daily family life',
        ],
        a: 0,
        e: "The consistent biblical pattern — where obedience brought blessing to households (like Noah's and Abraham's) and disobedience brought hardship (like Achan's family) — teaches that a family's faithfulness to God's principles shapes the wellbeing of the whole household.",
        h: 'Consider how these stories often affected entire households, not just individuals.',
        yr: 'SS2',
      },
      {
        q: "Why does Scripture value obedience 'better than sacrifice' as Samuel told Saul?",
        o: [
          'Because sacrifices were unimportant rituals with no meaning',
          "Because obedience reflects genuine submission to God's will, while sacrifice offered instead of obedience can become an empty substitute",
          'Because sacrifices were forbidden under the old covenant',
          'Because Samuel disapproved of all sacrificial worship',
        ],
        a: 1,
        e: "Samuel's statement highlights that ritual sacrifice cannot substitute for genuine obedience; true worship flows from a heart submitted to God's will, not merely external religious performance offered in place of compliance.",
        h: 'Consider what Saul was trying to justify by pointing to the sacrifice he intended to offer.',
        yr: 'SS2',
      },
      {
        q: "Which of these best illustrates 'obedience under difficult circumstances' among the examples studied?",
        o: [
          'Achan hiding plunder for personal gain',
          "Abraham's willingness to offer Isaac despite the personal cost",
          'Saul sparing Agag for political convenience',
          'Adam and Eve eating the forbidden fruit',
        ],
        a: 1,
        e: "Abraham's willingness to obey God's difficult command to offer his son Isaac, despite the immense personal cost, stands out as an example of obedience even under the most difficult circumstances (Genesis 22:1-12).",
        h: 'Think about which example involved the greatest personal sacrifice in obeying God.',
        yr: 'SS2',
      },
      {
        q: 'What is the overall theological message conveyed by studying both obedience and disobedience together?',
        o: [
          'God is indifferent to human choices',
          "God's blessings and judgments consistently follow from human response to His commands, underscoring human responsibility",
          'Consequences described in the Bible are purely symbolic, not real',
          'Obedience and disobedience produce identical outcomes',
        ],
        a: 1,
        e: 'Studying both obedience and disobedience together highlights a consistent theological message: human beings are morally responsible before God, and their response to His commands has real, meaningful consequences.',
        h: 'Consider what these paired examples, taken together, teach about human responsibility before God.',
        yr: 'SS2',
      },
      {
        q: 'What did Saul claim as his reason for sparing the best of the Amalekite livestock?',
        o: [
          'He wanted to sell them for profit',
          'He intended to sacrifice them to the Lord',
          "He forgot God's command entirely",
          'He wanted to keep them as pets',
        ],
        a: 1,
        e: 'Saul claimed the soldiers spared the best sheep and cattle in order to sacrifice them to the Lord, an excuse Samuel firmly rejected as disobedience (1 Samuel 15:15, 21-22).',
        h: "Consider Saul's stated justification when confronted by Samuel.",
        yr: 'SS2',
      },
      {
        q: "What term describes Saul's error of doing part, but not all, of what God commanded?",
        o: ['Full obedience', 'Partial obedience', 'Total disobedience', 'Blind faith'],
        a: 1,
        e: "Saul's sparing of Agag and the best livestock, while destroying the rest, is described as partial obedience — following some but not all of God's explicit instruction, which Scripture still treats as disobedience.",
        h: 'This term describes doing only part of what was commanded.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Friendship',
    topicCode: 'SS2-CRS-07',
    module: 'Christian Relationships',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The friendship of <span class="learn-keyword">David and Jonathan</span> is one of the most celebrated examples of loyal friendship in the Bible. This topic examines their relationship as a model for understanding true friendship from a Christian perspective.\n</div>\n\n<h3 class="learn-subheading">1. The Beginning of David and Jonathan\'s Friendship</h3>\n<p class="learn-p">After David killed Goliath, Jonathan, son of King Saul, formed an immediate deep bond with David: "the soul of Jonathan was knit to the soul of David, and Jonathan loved him as his own soul" (1 Samuel 18:1). Jonathan made a covenant with David and gave him his robe, armour, sword, bow and belt, symbolically sharing his royal status and honour with David (1 Samuel 18:3-4).</p>\n\n<h3 class="learn-subheading">2. Jonathan\'s Loyalty Despite Personal Cost</h3>\n<p class="learn-p">As King Saul grew jealous and sought to kill David, Jonathan repeatedly protected his friend, warning him of danger, pleading with his father on David\'s behalf, and helping him escape (1 Samuel 19:1-7; 20:1-42). Jonathan did this even though David\'s rise threatened his own claim to the throne, showing that Jonathan valued friendship and God\'s will above personal ambition.</p>\n\n<h3 class="learn-subheading">3. The Covenant Between David and Jonathan</h3>\n<p class="learn-p">David and Jonathan renewed their covenant of friendship multiple times, asking God to be witness between them and their descendants forever (1 Samuel 20:14-17, 42). After Jonathan\'s death in battle, David honoured this covenant by showing kindness to Jonathan\'s disabled son, Mephibosheth, restoring his grandfather\'s land and giving him a permanent place at David\'s table (2 Samuel 9:1-13).</p>\n\n<h3 class="learn-subheading">4. Christian Perspective on Loyalty and Sacrifice</h3>\n<p class="learn-p">David and Jonathan\'s friendship illustrates biblical loyalty (Hebrew: <em>hesed</em>, covenant faithfulness), self-sacrifice, and putting another\'s wellbeing before personal ambition — qualities Jesus later commended: "Greater love has no one than this, that someone lay down his life for his friends" (John 15:13).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Quality Shown</th><th>Biblical Example</th></tr></thead>\n    <tbody>\n      <tr><td>Immediate deep bond</td><td>Jonathan\'s soul "knit" to David\'s after Goliath (1 Samuel 18:1)</td></tr>\n      <tr><td>Selfless generosity</td><td>Jonathan gave David his robe, armour, sword, bow, belt (1 Samuel 18:4)</td></tr>\n      <tr><td>Loyalty despite personal cost</td><td>Jonathan protected David despite Saul\'s jealousy and his own claim to the throne</td></tr>\n      <tr><td>Covenant faithfulness beyond death</td><td>David\'s kindness to Mephibosheth (2 Samuel 9)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Remember the specific items Jonathan gave David (robe, armour, sword, bow, belt) and the name of Jonathan\'s son whom David later honoured (Mephibosheth) — these details are often tested directly.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> David and Jonathan\'s friendship models covenant loyalty, selfless generosity, and sacrifice — qualities that reflect the New Testament ideal of laying down one\'s life for a friend.\n</div>\n',
    questions: [
      {
        q: 'What immediate response did Jonathan have toward David after David killed Goliath?',
        o: [
          "Jonathan was jealous of David's fame",
          "Jonathan's soul was knit to David's, and he loved him as his own soul",
          'Jonathan ignored David completely',
          'Jonathan reported David to King Saul',
        ],
        a: 1,
        e: "Scripture records that after David's victory over Goliath, Jonathan immediately formed a deep bond with David, described as his soul being 'knit' to David's, loving him as himself (1 Samuel 18:1).",
        h: 'This description uses the imagery of two souls being tightly bound together.',
        yr: 'SS2',
      },
      {
        q: 'What symbolic gifts did Jonathan give to David to seal their friendship?',
        o: [
          'Gold and silver only',
          'His robe, armour, sword, bow, and belt',
          'A herd of cattle',
          'The keys to the royal palace',
        ],
        a: 1,
        e: 'Jonathan gave David his robe, armour, sword, bow, and belt — items closely tied to his royal and military status — symbolically sharing his position and honour with David (1 Samuel 18:3-4).',
        h: 'These were personal royal and military items, not livestock or money.',
        yr: 'SS2',
      },
      {
        q: "Why is it significant that Jonathan, as Saul's son and heir to the throne, remained loyal to David?",
        o: [
          'It shows Jonathan had no interest in ruling at all',
          "It shows Jonathan valued friendship and God's will above his own claim to the throne",
          'It proves Jonathan disliked his father',
          'It shows Jonathan was forced into the friendship',
        ],
        a: 1,
        e: "As the natural heir to Saul's throne, Jonathan's continued loyalty to David — whom he knew God had chosen to be king instead — demonstrates remarkable selflessness, valuing friendship and God's will over personal ambition.",
        h: 'Consider what Jonathan stood to lose politically by supporting David.',
        yr: 'SS2',
      },
      {
        q: "How did Jonathan help protect David from King Saul's jealousy and anger?",
        o: [
          'He refused to get involved',
          'He warned David of danger, pleaded with Saul on his behalf, and helped him escape',
          "He reported David's location to Saul",
          'He convinced David to attack Saul first',
        ],
        a: 1,
        e: "Jonathan repeatedly intervened for David's safety — speaking to Saul on David's behalf, warning David of Saul's murderous intentions, and helping arrange his escape (1 Samuel 19:1-7; 20:1-42).",
        h: "Jonathan actively worked against his own father's wishes to protect his friend.",
        yr: 'SS2',
      },
      {
        q: 'What did David and Jonathan ask God to be witness to in their covenant?',
        o: [
          'Their military strategy',
          'Their covenant of friendship, extending to their descendants forever',
          'A business partnership',
          'Their agreement to divide the kingdom',
        ],
        a: 1,
        e: 'David and Jonathan made a covenant before God, asking Him to be witness to their friendship and to hold it as binding even between their future descendants (1 Samuel 20:14-17, 42).',
        h: 'Their agreement was meant to extend beyond their own lifetimes to their families.',
        yr: 'SS2',
      },
      {
        q: "How did David later honour his covenant with Jonathan after Jonathan's death?",
        o: [
          "He ignored Jonathan's family entirely",
          "He showed kindness to Jonathan's disabled son, Mephibosheth, restoring his land and giving him a place at his table",
          "He exiled Jonathan's descendants",
          "He had Jonathan's descendants executed",
        ],
        a: 1,
        e: "After becoming king, David sought out Jonathan's surviving son, Mephibosheth, restored his grandfather Saul's land to him, and gave him a permanent place at the king's table, fulfilling his covenant loyalty to Jonathan (2 Samuel 9:1-13).",
        h: "Recall the name of Jonathan's son and how David treated him years later.",
        yr: 'SS2',
      },
      {
        q: 'What physical condition did Mephibosheth have, as recorded in Scripture?',
        o: ['He was blind', 'He was lame in both feet', 'He was mute', 'He had a chronic illness'],
        a: 1,
        e: "Mephibosheth was lame in both feet, having been injured as a child when his nurse fled with him after news of Saul and Jonathan's deaths (2 Samuel 4:4; 9:3).",
        h: 'His disability affected his ability to walk, resulting from a childhood accident.',
        yr: 'SS2',
      },
      {
        q: "Which New Testament verse expresses an ideal of friendship reflected in Jonathan's loyalty to David?",
        o: [
          "'Love your enemies' (Matthew 5:44)",
          "'Greater love has no one than this, that someone lay down his life for his friends' (John 15:13)",
          "'Do not judge, or you too will be judged' (Matthew 7:1)",
          "'Faith without works is dead' (James 2:26)",
        ],
        a: 1,
        e: "Jesus' statement in John 15:13 about laying down one's life for a friend reflects the same spirit of sacrificial loyalty that Jonathan showed toward David, even at risk to his own position and safety.",
        h: 'This verse specifically speaks about the greatest expression of love among friends.',
        yr: 'SS2',
      },
      {
        q: "What Hebrew concept, often translated 'loyal love' or 'covenant faithfulness,' best describes the quality of David and Jonathan's friendship?",
        o: ['Shalom', 'Hesed', 'Torah', 'Sheol'],
        a: 1,
        e: "The Hebrew term 'hesed,' meaning steadfast, loyal, covenant love, best captures the enduring loyalty and faithfulness that characterized David and Jonathan's friendship, extending even to Jonathan's descendants.",
        h: 'This Hebrew word describes loyal, covenant-based love and faithfulness.',
        yr: 'SS2',
      },
      {
        q: 'What personal risk did Jonathan take by defending David before his father, Saul?',
        o: [
          'No risk at all',
          "He risked Saul's anger and even physical danger to himself",
          'He risked losing his wealth only',
          'He risked being exiled to a foreign land',
        ],
        a: 1,
        e: "Jonathan's defence of David provoked Saul's fury to the point that Saul even threw a spear at Jonathan, showing the real personal danger Jonathan faced for defending his friend (1 Samuel 20:30-33).",
        h: 'Consider how Saul reacted physically when Jonathan spoke up for David.',
        yr: 'SS2',
      },
      {
        q: "What does Jonathan's willingness to give up his claim to the throne for David's sake teach about true friendship?",
        o: [
          'True friendship demands personal ambition above all else',
          "True friendship can involve selfless sacrifice of personal advantage for another's good",
          'Friendship should never involve any sacrifice',
          'True friendship requires equal social status',
        ],
        a: 1,
        e: "Jonathan's readiness to support David, even though it meant relinquishing his own likely claim to Saul's throne, illustrates that genuine friendship can call for real selfless sacrifice of one's own advantage.",
        h: "Consider what Jonathan gave up by consistently supporting David's rise.",
        yr: 'SS2',
      },
      {
        q: "How does David's kindness to Mephibosheth reflect biblical teaching on covenant loyalty?",
        o: [
          'It shows David quickly forgot his promises after gaining power',
          "It shows David honoured his covenant promise even after Jonathan's death, extending kindness to his descendant",
          'It shows David feared Mephibosheth as a political rival',
          'It shows loyalty only matters while both parties are alive',
        ],
        a: 1,
        e: "David's continued kindness to Mephibosheth years after Jonathan's death demonstrates that covenant loyalty, as modelled between David and Jonathan, is meant to endure beyond a person's lifetime and extend to their family.",
        h: 'Consider the timing — this kindness happened long after Jonathan had died.',
        yr: 'SS2',
      },
      {
        q: "What can Christians learn from David and Jonathan's friendship about handling jealousy or rivalry between people close to them?",
        o: [
          'Jealousy should always be met with jealousy in return',
          "Genuine friendship, rooted in respect for God's will, can overcome jealousy and rivalry between families",
          'Rivalry makes true friendship impossible',
          'Friendship should end whenever conflict arises among family members',
        ],
        a: 1,
        e: "Despite Saul's jealousy threatening to destroy the relationship, Jonathan's respect for God's chosen plan for David and his commitment to true friendship allowed their bond to endure and even flourish despite the tension.",
        h: 'Consider how Jonathan handled the conflict between loyalty to his father and loyalty to his friend.',
        yr: 'SS2',
      },
      {
        q: 'Which statement best reflects a Christian view of friendship as modelled by David and Jonathan?',
        o: [
          "True friendship seeks personal advantage above the friend's wellbeing",
          "True friendship involves loyalty, sacrifice, and seeking the other's good even at personal cost",
          'True friendship requires no commitment or covenant',
          'True friendship is only possible between social equals',
        ],
        a: 1,
        e: "David and Jonathan's relationship models the Christian ideal of friendship as loyal, sacrificial, and committed to the other's wellbeing, even when it comes at significant personal cost.",
        h: 'Consider the overall pattern of behaviour Jonathan and David showed toward one another.',
        yr: 'SS2',
      },
      {
        q: "How did David react upon learning of Jonathan's death, as recorded in his lament (2 Samuel 1:26)?",
        o: [
          'He expressed indifference',
          "He mourned deeply, describing Jonathan's love as 'wonderful, surpassing the love of women'",
          'He blamed Jonathan for his own death',
          'He rejoiced at becoming king unopposed',
        ],
        a: 1,
        e: "David composed a heartfelt lament for Saul and Jonathan, describing his grief over Jonathan specifically as profound, calling his love 'wonderful, surpassing the love of women' (2 Samuel 1:17-27, especially v.26).",
        h: "David's lament reflects deep personal grief specifically for his friend.",
        yr: 'SS2',
      },
      {
        q: 'What lesson does the David-Jonathan friendship offer regarding friendships that cross social or family boundaries?',
        o: [
          'Friendship should be limited strictly to social equals',
          'True friendship can transcend social rank, family loyalty conflicts, and political rivalry',
          'Family loyalty should always override friendship',
          'Friendship across social classes is discouraged in Scripture',
        ],
        a: 1,
        e: 'Despite the vast difference in status — Jonathan a prince, David a shepherd turned warrior, and their families in political tension — their friendship transcended these boundaries, teaching that true friendship can rise above social and familial complications.',
        h: 'Consider the difference in their backgrounds and family situations.',
        yr: 'SS2',
      },
      {
        q: "What role did covenant-making play in David and Jonathan's friendship?",
        o: [
          'It played no significant role',
          'It formalized and deepened their commitment to loyalty, invoking God as witness',
          'It was only a legal formality with no personal meaning',
          'It replaced any need for personal trust between them',
        ],
        a: 1,
        e: 'Their repeated covenant-making, invoking God as witness, formalized and deepened their mutual commitment, showing that their friendship was rooted not just in emotion but in solemn, God-witnessed loyalty (1 Samuel 18:3; 20:16-17, 42).',
        h: 'Consider why they specifically involved God as a witness to their agreement.',
        yr: 'SS2',
      },
      {
        q: "Which of these best describes how David and Jonathan's friendship is relevant to Christians building friendships today?",
        o: [
          'It suggests friendship should be avoided if it risks personal loss',
          "It encourages believers to build friendships marked by loyalty, sacrifice, and commitment to one another's wellbeing before God",
          'It suggests friendships need no spiritual dimension',
          'It implies close friendships are unnecessary for Christians',
        ],
        a: 1,
        e: "David and Jonathan's example encourages Christians to pursue friendships characterized by loyalty, willingness to sacrifice for one another, and mutual commitment grounded in shared faith and reverence for God.",
        h: 'Consider what timeless principles about friendship this ancient account offers for today.',
        yr: 'SS2',
      },
      {
        q: 'What did David do for Mephibosheth regarding meals, according to 2 Samuel 9?',
        o: [
          'He denied him any provision',
          "He gave him a permanent place to eat at the king's own table",
          'He sent him away from Jerusalem',
          'He required him to work as a servant',
        ],
        a: 1,
        e: "David restored Saul's land to Mephibosheth and gave him a permanent place to eat at the king's own table, like one of the king's sons (2 Samuel 9:7-11).",
        h: "This provision reflected the depth of David's covenant kindness to Jonathan's family.",
        yr: 'SS2',
      },
      {
        q: "What does the term 'covenant' mean in the context of David and Jonathan's relationship?",
        o: [
          'A casual, temporary agreement',
          'A solemn, binding commitment often made before God as witness',
          'A business contract for profit',
          'A one-time gift exchange',
        ],
        a: 1,
        e: 'In the context of David and Jonathan, a covenant refers to a solemn, binding commitment of loyalty made before God as witness, intended to be lasting and honoured even by their descendants.',
        h: 'Consider the formal, God-witnessed nature of the agreements David and Jonathan repeatedly made.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Trust in God (Submission to the Will of God)',
    topicCode: 'SS2-CRS-08',
    module: 'Faith and Trust',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  This topic examines biblical characters who exemplify <span class="learn-keyword">trust in God</span> and <span class="learn-keyword">submission</span> to His will, even in the face of uncertainty, hardship, or personal cost — namely <span class="learn-keyword">Abraham</span>, <span class="learn-keyword">Job</span>, and <span class="learn-keyword">Jesus</span>.\n</div>\n\n<h3 class="learn-subheading">1. Abraham\'s Trust in God</h3>\n<p class="learn-p">Abraham left his home in Ur for an unknown land simply because God commanded him to (Genesis 12:1-4; Hebrews 11:8). His greatest test of trust came when God commanded him to offer his only promised son, Isaac, as a sacrifice. Abraham obeyed, trusting that God would provide, and was stopped by an angel at the last moment, with a ram provided as a substitute (Genesis 22:1-14).</p>\n\n<h3 class="learn-subheading">2. Job\'s Trust Amid Suffering</h3>\n<p class="learn-p">Job, a righteous and blameless man, lost his wealth, children, and health in swift succession through calamities permitted by God. Despite intense suffering and the pressure of his wife and friends to curse God, Job maintained his trust, declaring, "Though he slay me, yet will I trust in him" (Job 13:15) and, "The Lord gave, and the Lord has taken away; blessed be the name of the Lord" (Job 1:21).</p>\n\n<h3 class="learn-subheading">3. Jesus\' Submission to God\'s Will</h3>\n<p class="learn-p">In the Garden of Gethsemane, facing the agony of the coming crucifixion, Jesus prayed, "Father, if you are willing, take this cup from me; yet not my will, but yours be done" (Luke 22:42). His complete submission to the Father\'s will, even unto death, models the ultimate example of trust and obedience.</p>\n\n<h3 class="learn-subheading">4. The Importance of Submission to God\'s Will</h3>\n<p class="learn-p">These examples show that trust in God does not mean the absence of hardship, fear, or difficult questions, but a settled confidence that God\'s will and purposes are ultimately good and trustworthy, even when circumstances are painful or unclear.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Person</th><th>Test of Trust</th><th>Response</th></tr></thead>\n    <tbody>\n      <tr><td>Abraham</td><td>Command to offer Isaac as a sacrifice</td><td>Obeyed, trusting God would provide</td></tr>\n      <tr><td>Job</td><td>Loss of wealth, children, and health</td><td>Maintained trust despite intense suffering</td></tr>\n      <tr><td>Jesus</td><td>The agony of the cross</td><td>Submitted to the Father\'s will in Gethsemane</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Be able to quote key trust statements exactly: Abraham\'s faith journey (Hebrews 11:8), Job\'s declaration (Job 13:15; 1:21), and Jesus\' Gethsemane prayer (Luke 22:42).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Abraham, Job, and Jesus each modelled deep trust in and submission to God\'s will, even amid uncertainty, suffering, or fear — teaching that true faith holds firm regardless of circumstances.\n</div>\n',
    questions: [
      {
        q: 'What did God command Abraham to do that first tested his trust and obedience?',
        o: [
          'To build an altar in Egypt',
          "To leave his country, people, and father's household for an unknown land",
          'To fight against the Philistines',
          'To marry a foreign wife',
        ],
        a: 1,
        e: "God's initial call to Abraham required him to leave his familiar homeland, people, and family for a land God would show him later, requiring significant trust in an unknown outcome (Genesis 12:1; Hebrews 11:8).",
        h: 'This first test involved leaving everything familiar for an undisclosed destination.',
        yr: 'SS2',
      },
      {
        q: "What was Abraham's greatest test of trust and submission to God's will?",
        o: [
          'Being asked to build a large altar',
          'Being commanded to offer his son Isaac as a sacrifice',
          'Being asked to fight a war',
          'Being asked to give away all his wealth',
        ],
        a: 1,
        e: "God's command that Abraham offer his only promised son, Isaac, as a sacrifice on Mount Moriah represented the ultimate test of his trust and submission to God's will (Genesis 22:1-2).",
        h: "This test involved the son through whom God's covenant promises were to be fulfilled.",
        yr: 'SS2',
      },
      {
        q: 'How did God ultimately resolve the test involving Isaac?',
        o: [
          'He allowed Isaac to be sacrificed',
          'He stopped Abraham at the last moment and provided a ram as a substitute',
          'He punished Abraham for even considering it',
          "He removed Isaac from Abraham's life permanently",
        ],
        a: 1,
        e: 'As Abraham raised the knife to sacrifice Isaac, an angel of the Lord stopped him, and Abraham saw a ram caught in a thicket, which he offered instead of his son (Genesis 22:10-13).',
        h: 'An animal was found nearby and used as a substitute offering.',
        yr: 'SS2',
      },
      {
        q: 'What kind of man was Job before his calamities began, according to Scripture?',
        o: [
          'Wicked and dishonest',
          'Blameless, upright, feared God, and turned away from evil',
          'A foreigner with no relationship to God',
          'A king with no personal faith',
        ],
        a: 1,
        e: 'Job is introduced as blameless and upright, a man who feared God and turned away from evil, establishing that his subsequent suffering was not due to personal wrongdoing (Job 1:1).',
        h: 'His character is described before any of his troubles are mentioned.',
        yr: 'SS2',
      },
      {
        q: 'What losses did Job experience in quick succession?',
        o: [
          'Only financial loss',
          'Loss of his wealth, his children, and later his health',
          'Loss of his reputation only',
          'Loss of his home but nothing else',
        ],
        a: 1,
        e: 'In a series of devastating events, Job lost his livestock and servants, then all ten of his children in a single disaster, and later suffered painful sores over his entire body (Job 1:13-19; 2:7).',
        h: 'His losses affected his possessions, his family, and eventually his own body.',
        yr: 'SS2',
      },
      {
        q: 'What did Job declare after learning of the loss of his children and wealth?',
        o: [
          "'I curse the day I was born'",
          "'The Lord gave, and the Lord has taken away; blessed be the name of the Lord'",
          "'God has abandoned me forever'",
          "'I will never trust God again'",
        ],
        a: 1,
        e: "Despite his devastating losses, Job responded with worship rather than cursing God, declaring, 'The Lord gave, and the Lord has taken away; blessed be the name of the Lord' (Job 1:21).",
        h: 'This response shows worship rather than complaint immediately after the losses.',
        yr: 'SS2',
      },
      {
        q: 'What statement by Job reflects his deep trust even amid extreme suffering?',
        o: [
          "'Though he slay me, yet will I trust in him'",
          "'I will curse God and die'",
          "'God does not care about my suffering'",
          "'I no longer believe in God'",
        ],
        a: 0,
        e: "Job's declaration in Job 13:15, 'Though he slay me, yet will I trust in him,' expresses profound trust in God even in the face of the most extreme suffering and uncertainty.",
        h: 'This statement expresses trust even in the worst possible scenario, death itself.',
        yr: 'SS2',
      },
      {
        q: "Who urged Job to 'curse God and die' amid his suffering?",
        o: ['One of his friends', 'His wife', 'A stranger', 'His servant'],
        a: 1,
        e: "Job's wife, overwhelmed by their calamities, urged him to curse God and die, but Job refused, responding that they should accept both good and adversity from God (Job 2:9-10).",
        h: 'This person was closest to Job and shared in his loss.',
        yr: 'SS2',
      },
      {
        q: "Where did Jesus pray in agony before His crucifixion, submitting to the Father's will?",
        o: [
          'On the Mount of Olives, specifically the Garden of Gethsemane',
          'In the temple courts',
          'On the road to Emmaus',
          'In the upper room',
        ],
        a: 0,
        e: 'Jesus prayed in the Garden of Gethsemane, on the Mount of Olives, in deep anguish before His arrest and crucifixion (Luke 22:39-44).',
        h: 'This specific garden is where Jesus prayed just before His arrest.',
        yr: 'SS2',
      },
      {
        q: 'What did Jesus pray in Gethsemane regarding His impending suffering?',
        o: [
          "'Take this cup from me, and do not ask for my will'",
          "'Father, if you are willing, take this cup from me; yet not my will, but yours be done'",
          "'I refuse to accept this suffering'",
          "'Let my disciples suffer in my place'",
        ],
        a: 1,
        e: "Jesus' prayer in Luke 22:42 expressed both His honest human desire to avoid suffering and His ultimate submission to the Father's will: 'not my will, but yours be done.'",
        h: "This prayer balances honest human feeling with complete submission to God's will.",
        yr: 'SS2',
      },
      {
        q: "What physical sign of Jesus' extreme anguish is recorded during His prayer in Gethsemane?",
        o: [
          'He fainted completely',
          'His sweat became like drops of blood',
          'He fled the garden in fear',
          'He argued with His disciples',
        ],
        a: 1,
        e: 'Luke 22:44 records that Jesus was in such anguish that His sweat became like great drops of blood falling to the ground, showing the intensity of His emotional and physical struggle.',
        h: 'This detail describes an intense physical symptom of extreme stress during His prayer.',
        yr: 'SS2',
      },
      {
        q: 'What common theme unites the trust shown by Abraham, Job, and Jesus?',
        o: [
          'Trust in God requires the absence of fear or hardship',
          'Genuine trust in God can coexist with fear, grief, or difficulty, while still submitting to His will',
          'Only sinless people can trust God fully',
          'Trust in God guarantees an easy life',
        ],
        a: 1,
        e: "Each figure experienced real fear, grief, or anguish, yet still chose to trust and submit to God's will — showing that genuine faith does not require the absence of difficult emotion, but persistent submission despite it.",
        h: 'Consider that all three figures expressed real human struggle alongside their trust.',
        yr: 'SS2',
      },
      {
        q: "What does Abraham's willingness to offer Isaac reveal about the nature of true faith?",
        o: [
          'True faith avoids difficult tests',
          'True faith is willing to obey God even when His command is difficult to understand',
          'True faith requires visible proof before obeying',
          'True faith is only tested through material loss',
        ],
        a: 1,
        e: "Abraham's readiness to obey God's difficult command, even without fully understanding it, illustrates that true faith involves obedience to God even amid uncertainty or apparent contradiction with earlier promises.",
        h: "Consider that sacrificing Isaac seemed to contradict God's earlier promise of descendants through him.",
        yr: 'SS2',
      },
      {
        q: "What does Job's story teach about the relationship between suffering and personal sin?",
        o: [
          'All suffering is always a direct punishment for personal sin',
          'Suffering can occur even to righteous, blameless people, not only as punishment for sin',
          "Job's suffering proved he was secretly wicked",
          'Suffering only affects the wicked',
        ],
        a: 1,
        e: "Job's story directly challenges the assumption that suffering always results from personal sin, since Job was explicitly described as blameless and upright before his calamities began (Job 1:1, 8).",
        h: "Recall how Job's character was described before his troubles began.",
        yr: 'SS2',
      },
      {
        q: "What does Jesus' example in Gethsemane teach Christians about expressing honest emotion in prayer?",
        o: [
          'Believers should hide all fear or distress from God in prayer',
          'It is appropriate to honestly express fear or distress to God while still submitting to His will',
          'Prayer should only ever express confidence, never struggle',
          'Expressing distress in prayer shows a lack of faith',
        ],
        a: 1,
        e: "Jesus' honest request that the cup be taken from Him, followed by His submission to the Father's will, models that believers can honestly express fear or struggle in prayer while still ultimately submitting to God's purposes.",
        h: "Consider how Jesus balanced honesty about His feelings with obedience to God's will.",
        yr: 'SS2',
      },
      {
        q: "How is 'trust in God' distinguished from mere passive resignation in these examples?",
        o: [
          'Trust involves active obedience and continued relationship with God despite hardship, not simple passive acceptance',
          'Trust means doing nothing and expecting God to act alone',
          'Trust and resignation are identical concepts',
          "Trust requires understanding every reason behind one's suffering",
        ],
        a: 0,
        e: "Unlike passive resignation, the trust shown by Abraham, Job, and Jesus involved active engagement with God — obedience, honest prayer, and continued relationship — even without full understanding of God's purposes.",
        h: 'Consider the difference between simply giving up and actively continuing to relate to God through hardship.',
        yr: 'SS2',
      },
      {
        q: "What New Testament passage commends Abraham's faith as an example for believers?",
        o: ['Hebrews 11', 'Revelation 21', '1 Corinthians 13', 'Ephesians 6'],
        a: 0,
        e: "Hebrews 11, often called the 'faith chapter,' commends Abraham's faith journey — including his willingness to leave his homeland and offer Isaac — as a model of trust in God for believers to follow (Hebrews 11:8-19).",
        h: 'This chapter is known for listing examples of faith throughout biblical history.',
        yr: 'SS2',
      },
      {
        q: 'Which best summarizes the overall lesson Christians can draw from studying Abraham, Job, and Jesus together on this theme?',
        o: [
          "Trusting and submitting to God's will, even through uncertainty, suffering, or fear, is central to a life of genuine faith",
          'Only perfect people can trust God fully',
          'Trusting God guarantees the removal of all hardship immediately',
          "Submission to God's will is unnecessary for genuine faith",
        ],
        a: 0,
        e: "Taken together, these three examples teach that genuine faith involves trusting and submitting to God's will even through uncertainty, suffering, or personal anguish — not the absence of these difficulties, but steadfastness despite them.",
        h: 'Consider the overarching lesson these three very different accounts share in common.',
        yr: 'SS2',
      },
      {
        q: 'What did Abraham tell his servants he and Isaac would do together on the mountain, showing his trust that God would provide?',
        o: [
          "'We will go and never return'",
          "'We will worship and then we will come back to you'",
          "'We will build a permanent settlement'",
          "'We will abandon the journey'",
        ],
        a: 1,
        e: 'Abraham told his servants that he and Isaac would go to worship and then come back to them, reflecting his faith that God would somehow provide a way for both of them to return (Genesis 22:5).',
        h: "Consider what this statement to his servants reveals about Abraham's expectation of the outcome.",
        yr: 'SS2',
      },
      {
        q: 'What did the ram caught in the thicket ultimately provide for Abraham at the crucial moment?',
        o: [
          'Nothing of significance',
          'A substitute sacrifice in place of Isaac',
          'Meat for a celebratory feast unrelated to worship',
          'Wool for making clothing',
        ],
        a: 1,
        e: "The ram caught in the thicket by its horns provided Abraham with a substitute sacrifice to offer in place of Isaac, demonstrating God's provision at the crucial moment (Genesis 22:13).",
        h: 'Consider what animal appeared at the critical moment and how Abraham used it.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Making Decisions as a Christian',
    topicCode: 'SS2-CRS-09',
    module: 'Christian Living',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Every day, Christians face choices — some small, some life-changing. This topic explores the <span class="learn-keyword">factors</span> that influence decision-making and <span class="learn-keyword">biblical guidelines</span> for making right decisions that honour God.\n</div>\n\n<h3 class="learn-subheading">1. Factors That Influence Decision-Making</h3>\n<p class="learn-p">Decisions are shaped by many factors: personal desires and emotions, peer or family pressure, cultural and societal expectations, available information, past experiences, and — for the Christian — the desire to honour God. Recognizing these influences helps believers evaluate whether a decision is truly guided by godly wisdom or merely by pressure or emotion.</p>\n\n<h3 class="learn-subheading">2. Biblical Guidelines for Right Decision-Making</h3>\n<p class="learn-p">Scripture offers clear guidance for believers: seeking God\'s will through prayer (Philippians 4:6-7), relying on Scripture as a guide ("Your word is a lamp to my feet and a light to my path," Psalm 119:105), seeking godly counsel (Proverbs 15:22), trusting God rather than one\'s own limited understanding (Proverbs 3:5-6), and considering whether a decision aligns with God\'s character and commands.</p>\n\n<h3 class="learn-subheading">3. Biblical Examples of Decision-Making</h3>\n<p class="learn-p">Solomon\'s request for wisdom rather than wealth or long life (1 Kings 3:5-14) shows a decision aligned with godly priorities. By contrast, Lot\'s choice of the well-watered plain of Sodom, based purely on material advantage without regard for spiritual danger, led to disaster (Genesis 13:10-13; 19:1-29).</p>\n\n<h3 class="learn-subheading">4. Practical Steps for Christian Decision-Making</h3>\n<p class="learn-p">A Christian approach to decisions typically involves: prayerful reflection, checking Scripture for relevant principles, seeking counsel from mature believers, considering long-term consequences (spiritual and practical), and being willing to act in obedience once God\'s direction becomes clear.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Guideline</th><th>Scripture</th></tr></thead>\n    <tbody>\n      <tr><td>Pray about the decision</td><td>Philippians 4:6-7</td></tr>\n      <tr><td>Let Scripture guide the choice</td><td>Psalm 119:105</td></tr>\n      <tr><td>Seek godly counsel</td><td>Proverbs 15:22</td></tr>\n      <tr><td>Trust God, not self-understanding alone</td><td>Proverbs 3:5-6</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Be ready to contrast Solomon\'s wise request for wisdom with Lot\'s decision based purely on material gain — a common comparison in exam questions on decision-making.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Christian decision-making should be guided by prayer, Scripture, godly counsel, and trust in God, rather than emotion, pressure, or material gain alone.\n</div>\n',
    questions: [
      {
        q: "Which of the following is a common factor that can negatively influence a Christian's decision-making?",
        o: [
          'Prayerful reflection',
          'Peer or family pressure disconnected from godly wisdom',
          'Seeking godly counsel',
          'Reference to Scripture',
        ],
        a: 1,
        e: "While godly guidance such as prayer, counsel and Scripture should shape decisions, peer or family pressure that disregards biblical wisdom can negatively distort a Christian's decision-making process.",
        h: 'Consider which of the listed options is not a biblically recommended guide.',
        yr: 'SS2',
      },
      {
        q: 'According to Psalm 119:105, what role does Scripture play in decision-making?',
        o: [
          'It is irrelevant to daily choices',
          "It serves as 'a lamp to my feet and a light to my path,' guiding decisions",
          'It replaces the need for prayer',
          'It only applies to religious ceremonies',
        ],
        a: 1,
        e: "Psalm 119:105 describes God's word as a lamp and light, guiding the believer's steps and decisions in daily life.",
        h: "This verse uses the imagery of light guiding one's path.",
        yr: 'SS2',
      },
      {
        q: 'What does Proverbs 3:5-6 teach about the basis for making decisions?',
        o: [
          'Trust in your own understanding above all else',
          'Trust in the Lord with all your heart and do not lean on your own understanding',
          "Avoid seeking God's guidance for practical matters",
          'Rely solely on human wisdom',
        ],
        a: 1,
        e: 'Proverbs 3:5-6 instructs believers to trust in the Lord completely rather than relying solely on their own limited understanding, promising that God will direct their paths.',
        h: 'This well-known proverb contrasts trusting God with leaning on personal understanding.',
        yr: 'SS2',
      },
      {
        q: 'What does Proverbs 15:22 teach about seeking advice before making decisions?',
        o: [
          'Plans fail for lack of counsel, but with many advisers they succeed',
          'Advice from others is unnecessary for good decisions',
          'Only leaders should seek counsel',
          'Counsel should be avoided to maintain independence',
        ],
        a: 0,
        e: 'Proverbs 15:22 teaches that plans fail without counsel but succeed with many advisers, emphasizing the value of seeking godly guidance from others before making important decisions.',
        h: 'This proverb speaks about the value of multiple advisers for successful plans.',
        yr: 'SS2',
      },
      {
        q: 'What did Solomon request from God when given the opportunity to ask for anything?',
        o: [
          'Wealth and long life',
          "Wisdom to govern God's people well",
          'Military victory over his enemies',
          'Fame among neighbouring nations',
        ],
        a: 1,
        e: "When God offered to give Solomon whatever he asked, Solomon requested wisdom to discern between good and evil in order to govern God's people well, rather than wealth, long life, or victory over enemies (1 Kings 3:5-9).",
        h: "Solomon's request focused on a quality needed for good governance, not material gain.",
        yr: 'SS2',
      },
      {
        q: "How did God respond to Solomon's request for wisdom?",
        o: [
          'God refused the request',
          'God granted wisdom and also gave him wealth and honour because he did not ask for these selfishly',
          'God granted only wisdom with no additional blessing',
          'God punished Solomon for asking',
        ],
        a: 1,
        e: 'Pleased that Solomon did not ask for wealth, long life, or victory, God granted him wisdom and also gave him riches and honour that he had not requested (1 Kings 3:10-13).',
        h: 'Consider the additional blessings God gave beyond what Solomon actually asked for.',
        yr: 'SS2',
      },
      {
        q: "What factor primarily influenced Lot's decision to choose the plain of Sodom?",
        o: [
          'A direct command from God',
          'The material advantage of well-watered, fertile land, without regard for spiritual danger',
          "Concern for his family's spiritual wellbeing",
          'A desire to live near Abraham',
        ],
        a: 1,
        e: "Lot chose the plain of Sodom because it was well-watered and appeared materially advantageous, without giving due consideration to the area's wickedness and spiritual danger (Genesis 13:10-13).",
        h: 'His decision was driven by visible material benefit rather than spiritual discernment.',
        yr: 'SS2',
      },
      {
        q: 'What eventually happened to Lot and his family as a consequence of settling near Sodom?',
        o: [
          'They prospered without any difficulty',
          "They faced the destruction of Sodom, with Lot's wife turning into a pillar of salt",
          'They became rulers of Sodom',
          'Nothing negative happened at all',
        ],
        a: 1,
        e: "Sodom was later destroyed by God because of its wickedness; Lot's family barely escaped, but his wife looked back against instructions and turned into a pillar of salt (Genesis 19:24-26).",
        h: 'One family member disobeyed a specific instruction while fleeing the destruction.',
        yr: 'SS2',
      },
      {
        q: "What contrast between Solomon and Lot's decisions is often highlighted in Christian teaching on decision-making?",
        o: [
          "Solomon sought wisdom aligned with God's purposes, while Lot chose based on material gain without spiritual discernment",
          'Both made identical decisions',
          "Lot's decision was wiser than Solomon's",
          'Neither decision had any lasting consequence',
        ],
        a: 0,
        e: "Solomon's request for wisdom to serve God's people well contrasts sharply with Lot's choice based purely on visible material advantage, illustrating two very different approaches to decision-making.",
        h: 'Consider what each man prioritized when given a significant choice.',
        yr: 'SS2',
      },
      {
        q: 'According to Philippians 4:6-7, what should Christians do instead of being anxious about decisions?',
        o: [
          'Avoid thinking about the decision at all',
          'Present their requests to God in prayer with thanksgiving',
          'Rely solely on their emotions',
          'Wait passively without seeking any guidance',
        ],
        a: 1,
        e: "Philippians 4:6-7 instructs believers not to be anxious but to present their requests to God through prayer and thanksgiving, resulting in God's peace guarding their hearts and minds.",
        h: 'This passage specifically addresses anxiety and prayer together.',
        yr: 'SS2',
      },
      {
        q: 'Why is seeking godly counsel considered important in Christian decision-making, according to Proverbs?',
        o: [
          'Because personal judgment alone is always sufficient',
          'Because wise counsel from others helps ensure plans succeed and decisions are sound',
          'Because seeking advice shows weakness',
          'Because counsel replaces the need for prayer entirely',
        ],
        a: 1,
        e: 'Proverbs consistently teaches that seeking wise counsel strengthens decision-making, helping to avoid the pitfalls of relying solely on limited personal perspective (Proverbs 15:22; 11:14).',
        h: 'Consider what benefit comes from consulting multiple wise advisers rather than deciding alone.',
        yr: 'SS2',
      },
      {
        q: "What long-term principle can be drawn from comparing Solomon's and Lot's decisions?",
        o: [
          'Decisions based on godly priorities tend to bring lasting blessing, while decisions based purely on material advantage can bring disaster',
          'Material advantage should always be the primary factor in decision-making',
          'Godly priorities guarantee an easy life with no challenges',
          'There is no real difference in outcome between godly and worldly decisions',
        ],
        a: 0,
        e: 'The differing outcomes for Solomon (blessed with wisdom, wealth, and honour) and Lot (facing disaster in Sodom) illustrate that decisions rooted in godly priorities tend to bring lasting benefit, while purely material considerations can lead to serious consequences.',
        h: 'Consider what ultimately happened to each man because of how he decided.',
        yr: 'SS2',
      },
      {
        q: 'Which best describes how emotions should factor into Christian decision-making?',
        o: [
          'Emotions should be entirely ignored in every decision',
          'Emotions are a natural part of decision-making but should be weighed alongside prayer, Scripture, and counsel rather than followed blindly',
          'Emotions alone should determine every decision',
          'Christians should never feel any emotion about decisions',
        ],
        a: 1,
        e: 'While emotions are a natural part of human experience and decision-making, a balanced Christian approach weighs them alongside prayer, Scripture, and godly counsel rather than letting emotion alone dictate the outcome.',
        h: 'Consider the balanced approach suggested by combining multiple biblical guidelines.',
        yr: 'SS2',
      },
      {
        q: "How does trusting God 'with all your heart' (Proverbs 3:5) differ from trusting one's own understanding?",
        o: [
          'There is no real difference between the two',
          'Trusting God involves surrendering ultimate confidence to His wisdom rather than relying solely on limited human insight',
          "Trusting one's own understanding is always more reliable",
          'Trusting God means avoiding all personal reasoning',
        ],
        a: 1,
        e: "Proverbs 3:5-6 contrasts wholehearted trust in God's wisdom with reliance on limited human understanding, encouraging believers to submit their reasoning to God's guidance rather than depending on it exclusively.",
        h: 'Consider the specific contrast the proverb draws between two sources of guidance.',
        yr: 'SS2',
      },
      {
        q: 'What practical first step is often recommended for a Christian facing an important decision?',
        o: [
          'Making an immediate decision without reflection',
          "Prayerful reflection, seeking God's guidance before deciding",
          'Ignoring the decision until it resolves itself',
          'Consulting only non-believers for advice',
        ],
        a: 1,
        e: "A biblically grounded approach to decision-making typically begins with prayerful reflection and seeking God's guidance before moving forward with a choice, consistent with passages like Philippians 4:6-7.",
        h: 'Consider what step is typically emphasized first among the biblical guidelines studied.',
        yr: 'SS2',
      },
      {
        q: "Why might Lot's decision be described as shortsighted from a Christian perspective?",
        o: [
          'Because it considered long-term spiritual consequences carefully',
          'Because it focused on immediate material benefit while ignoring the spiritual danger of the surrounding environment',
          'Because it involved consulting too many advisers',
          'Because it required too much prayer beforehand',
        ],
        a: 1,
        e: "Lot's decision is considered shortsighted because it prioritized the immediate visible benefit of fertile land without weighing the serious spiritual and moral dangers of living near Sodom.",
        h: 'Consider what Lot failed to take into account when he made his choice.',
        yr: 'SS2',
      },
      {
        q: "What does Solomon's story suggest about the relationship between godly priorities and material blessing?",
        o: [
          'Godly priorities always guarantee immediate material wealth for everyone',
          'Prioritizing godly wisdom, rather than material gain, can result in God granting additional blessings as well',
          'Material blessing is unrelated to spiritual priorities',
          'Seeking wisdom prevents any material blessing from following',
        ],
        a: 1,
        e: 'Although Solomon did not ask for wealth, God granted him riches and honour in addition to wisdom precisely because he prioritized what was godly rather than material request (1 Kings 3:10-13) — though this is not a universal formula guaranteeing wealth for every faithful decision.',
        h: "Consider what additional blessings followed Solomon's request, even though he did not ask for them.",
        yr: 'SS2',
      },
      {
        q: 'A Christian young person deciding on a career path, applying these biblical guidelines, should primarily:',
        o: [
          'Choose based solely on projected income',
          "Pray, consult Scripture principles and godly counsel, and consider how the career aligns with God's purposes for their life",
          'Avoid seeking any advice from others',
          'Decide impulsively without reflection',
        ],
        a: 1,
        e: 'Applying the biblical guidelines studied, a Christian facing a major decision like a career choice should combine prayer, scriptural reflection, and godly counsel rather than deciding on material factors or impulse alone.',
        h: 'Consider how the combined guidelines studied would apply to a real-life major decision.',
        yr: 'SS2',
      },
      {
        q: 'What overall principle unites the biblical guidelines for Christian decision-making studied in this topic?',
        o: [
          "Decisions should be made independently of God's guidance",
          "God desires to guide His people's decisions through prayer, Scripture, godly counsel, and trust in Him",
          "Only religious leaders need to seek God's guidance for decisions",
          'Biblical guidance is irrelevant to everyday practical choices',
        ],
        a: 1,
        e: "Across all the guidelines studied — prayer, Scripture, counsel, and trust — the unifying principle is that God desires to actively guide His people's decisions in every area of life, not just religious matters.",
        h: 'Consider what common thread runs through all the different guidelines discussed.',
        yr: 'SS2',
      },
      {
        q: 'What theme from this topic is echoed in James 1:5, regarding seeking wisdom?',
        o: [
          'That wisdom should never be requested from God',
          'That anyone lacking wisdom should ask God, who gives generously to all without finding fault',
          'That wisdom is reserved only for the wealthy',
          'That asking God for wisdom is unnecessary',
        ],
        a: 1,
        e: "James 1:5 teaches that if anyone lacks wisdom, they should ask God, who gives generously to all without finding fault — echoing the same principle behind Solomon's request for wisdom.",
        h: 'This New Testament verse parallels the Old Testament example of asking God for wisdom.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Greed and its Effect',
    topicCode: 'SS2-CRS-10',
    module: 'Christian Living',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Greed</span> — an excessive, selfish desire for more than one needs, especially wealth or possessions — is repeatedly condemned in Scripture. This topic examines biblical examples of greed and its destructive consequences.\n</div>\n\n<h3 class="learn-subheading">1. Achan\'s Greed</h3>\n<p class="learn-p">During the conquest of Jericho, Achan coveted and secretly took silver, gold, and a beautiful robe that had been devoted to destruction, disobeying God\'s explicit command. His greed brought Israel\'s defeat at Ai and led to his own death and that of his family (Joshua 7:1, 20-26).</p>\n\n<h3 class="learn-subheading">2. Gehazi\'s Greed</h3>\n<p class="learn-p">Gehazi, servant of the prophet Elisha, secretly pursued Naaman after Elisha had refused payment for healing Naaman\'s leprosy, deceitfully requesting silver and clothing for himself. As a result, Elisha pronounced that Naaman\'s leprosy would cling to Gehazi and his descendants forever (2 Kings 5:20-27).</p>\n\n<h3 class="learn-subheading">3. Judas Iscariot\'s Greed</h3>\n<p class="learn-p">Judas, one of Jesus\' twelve disciples, was known to steal from the disciples\' common money bag (John 12:6) and ultimately betrayed Jesus for thirty pieces of silver (Matthew 26:14-16). Overcome with remorse afterward, he returned the money and took his own life (Matthew 27:3-5).</p>\n\n<h3 class="learn-subheading">4. The Consequences and Lessons of Greed</h3>\n<p class="learn-p">In each case, greed led to deception, betrayal of trust, and severe personal consequences — death, disease, or destruction. The Bible warns, "For the love of money is a root of all kinds of evil" (1 Timothy 6:10), and Jesus taught, "Watch out! Be on your guard against all kinds of greed; life does not consist in an abundance of possessions" (Luke 12:15).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Person</th><th>Act of Greed</th><th>Consequence</th></tr></thead>\n    <tbody>\n      <tr><td>Achan</td><td>Took devoted silver, gold, and a robe</td><td>Israel\'s defeat at Ai; his death and his family\'s death</td></tr>\n      <tr><td>Gehazi</td><td>Deceitfully collected payment from Naaman</td><td>Struck with leprosy for himself and his descendants</td></tr>\n      <tr><td>Judas</td><td>Stole from the money bag; betrayed Jesus for silver</td><td>Remorse and suicide</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Remember exact figures where relevant — Judas received thirty pieces of silver (Matthew 26:15) — and be able to quote 1 Timothy 6:10 and Luke 12:15 as key verses on greed.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Achan, Gehazi, and Judas each illustrate how greed leads to disobedience, deception, and severe consequences, warning believers to guard their hearts against the love of money and possessions.\n</div>\n',
    questions: [
      {
        q: 'What items did Achan covet and secretly take from the devoted plunder of Jericho?',
        o: [
          'A herd of cattle',
          'Silver, gold, and a beautiful robe',
          'Weapons and armour',
          'Grain and oil',
        ],
        a: 1,
        e: "Achan admitted to taking a beautiful Babylonian robe, silver, and gold from the plunder of Jericho, all of which had been devoted to destruction or the treasury by God's command (Joshua 7:21).",
        h: 'His confession specifically names three items he coveted and hid.',
        yr: 'SS2',
      },
      {
        q: "What was the direct military consequence of Achan's greed?",
        o: [
          'Israel won an even greater victory',
          'Israel suffered an unexpected defeat at the small city of Ai',
          "Nothing changed for Israel's army",
          'Israel was forced to make peace with Jericho',
        ],
        a: 1,
        e: "Because of Achan's disobedience, God's favour was withdrawn from Israel's army, resulting in an unexpected defeat at Ai, a much smaller city than Jericho (Joshua 7:1-5).",
        h: 'This defeat occurred at a city that should have been an easy victory.',
        yr: 'SS2',
      },
      {
        q: "What was Achan's ultimate punishment for his greed?",
        o: [
          'Exile from Israel',
          'He and his family were stoned to death, and his possessions burned',
          'A fine paid to the priests',
          'Temporary imprisonment',
        ],
        a: 1,
        e: "Once Achan's sin was exposed by lot, he and his family were stoned to death, and his belongings were burned, removing the sin from the camp (Joshua 7:24-26).",
        h: 'The punishment was severe and affected his entire household.',
        yr: 'SS2',
      },
      {
        q: 'Who was Gehazi, and to whom did he serve as a servant?',
        o: [
          "A king's servant; served King Solomon",
          'Servant of the prophet Elisha',
          "A priest's assistant; served Eli",
          "A soldier in Joshua's army",
        ],
        a: 1,
        e: 'Gehazi served as the personal servant/attendant of the prophet Elisha (2 Kings 5:20).',
        h: 'This character assisted a well-known Old Testament prophet.',
        yr: 'SS2',
      },
      {
        q: 'Why did Elisha initially refuse payment from Naaman after healing him of leprosy?',
        o: [
          'Because Naaman had no money to offer',
          'Because Elisha wanted to show that the healing was a free gift from God, not something to be purchased',
          'Because Elisha disliked Naaman personally',
          "Because Naaman's gifts were considered unclean",
        ],
        a: 1,
        e: 'Elisha refused any payment from Naaman to make clear that his healing was a gracious act of God, not something that could be bought or sold (2 Kings 5:15-16).',
        h: 'Consider the theological point Elisha was making by declining a reward.',
        yr: 'SS2',
      },
      {
        q: 'What deceitful act did Gehazi commit after Naaman departed?',
        o: [
          'He returned to warn Naaman of danger',
          'He secretly pursued Naaman and lied to obtain silver and clothing for himself',
          "He reported Naaman's healing to the king",
          "He asked Naaman to convert to Israel's religion",
        ],
        a: 1,
        e: "Gehazi ran after Naaman, fabricated a story about visitors needing provisions, and deceitfully obtained silver and clothing for himself, contrary to Elisha's refusal of payment (2 Kings 5:20-24).",
        h: 'His deception involved inventing a false reason to request payment.',
        yr: 'SS2',
      },
      {
        q: 'What punishment did Elisha pronounce on Gehazi for his greed and deception?',
        o: [
          'A fine of silver',
          "That Naaman's leprosy would cling to Gehazi and his descendants forever",
          'Temporary exile',
          'Loss of his position as servant only',
        ],
        a: 1,
        e: 'Elisha pronounced that the leprosy that had afflicted Naaman would now cling to Gehazi and his descendants forever as a consequence of his greed and deceit (2 Kings 5:27).',
        h: 'The judgment involved the same disease that had just been healed.',
        yr: 'SS2',
      },
      {
        q: "What role did Judas Iscariot hold among Jesus' followers before his betrayal?",
        o: [
          'He was a Pharisee who opposed Jesus',
          'He was one of the twelve disciples who kept the money bag',
          'He was a Roman soldier',
          'He was a priest in the temple',
        ],
        a: 1,
        e: "Judas Iscariot was one of Jesus' twelve chosen disciples and was entrusted with keeping the disciples' common money bag (John 12:6; 13:29).",
        h: "This person held a position of trust regarding the group's finances.",
        yr: 'SS2',
      },
      {
        q: "According to John 12:6, what was revealed about Judas' character even before his betrayal?",
        o: [
          'He was the most generous of the disciples',
          'He was a thief who used to help himself to what was in the money bag',
          'He was known for his humility',
          'He gave away all his own possessions',
        ],
        a: 1,
        e: 'John 12:6 reveals that Judas was a thief who used to pilfer from the common money bag entrusted to him, showing a pattern of greed even before his ultimate betrayal.',
        h: 'This verse describes a habitual dishonest practice, not just a single incident.',
        yr: 'SS2',
      },
      {
        q: 'For how much money did Judas agree to betray Jesus to the chief priests?',
        o: [
          'Ten pieces of silver',
          'Thirty pieces of silver',
          'Fifty pieces of silver',
          'One hundred pieces of silver',
        ],
        a: 1,
        e: 'Judas agreed to betray Jesus for thirty pieces of silver, a price the chief priests offered him (Matthew 26:14-16).',
        h: 'This specific amount is a commonly tested detail in this account.',
        yr: 'SS2',
      },
      {
        q: 'What did Judas do with the money after realizing the consequences of his betrayal?',
        o: [
          'He kept it and lived comfortably',
          'He returned it to the chief priests, expressing remorse, then took his own life',
          'He gave it to the poor',
          'He used it to start a business',
        ],
        a: 1,
        e: 'Overcome with remorse, Judas returned the thirty pieces of silver to the chief priests, declaring he had betrayed innocent blood, and then went and hanged himself (Matthew 27:3-5).',
        h: 'His response involved both an attempt to undo the deed and a tragic final act.',
        yr: 'SS2',
      },
      {
        q: "According to 1 Timothy 6:10, what is described as 'a root of all kinds of evil'?",
        o: ['Poverty', 'The love of money', 'Hard work', 'Generosity'],
        a: 1,
        e: "1 Timothy 6:10 teaches that 'the love of money is a root of all kinds of evil,' warning that an excessive desire for wealth can lead people into wandering from the faith and many griefs.",
        h: 'This verse specifically targets an attitude toward money, not money itself.',
        yr: 'SS2',
      },
      {
        q: 'What warning did Jesus give in Luke 12:15 regarding greed?',
        o: [
          "'Life consists in an abundance of possessions'",
          "'Watch out! Be on your guard against all kinds of greed; life does not consist in an abundance of possessions'",
          "'Greed is a minor issue not worth addressing'",
          "'Only the wealthy should be concerned about greed'",
        ],
        a: 1,
        e: "Jesus warned His listeners to guard against all kinds of greed, teaching that true life and value are not found in the abundance of one's possessions (Luke 12:15).",
        h: 'This teaching directly contrasts material abundance with true life.',
        yr: 'SS2',
      },
      {
        q: 'What common pattern links the stories of Achan, Gehazi, and Judas?',
        o: [
          'Each acted openly and was praised for honesty',
          'Each acted in secret, driven by greed, and faced severe consequences when exposed',
          'Each was rewarded for their actions',
          'None of them faced any consequence',
        ],
        a: 1,
        e: 'In each case, the person secretly pursued personal material gain against what was right, and each faced severe consequences once their greed-driven actions were exposed or their remorse set in.',
        h: 'Consider the shared elements of secrecy, motive, and consequence across all three accounts.',
        yr: 'SS2',
      },
      {
        q: 'How did greed lead to broken trust in each of the three accounts studied?',
        o: [
          'Trust was unaffected in all three cases',
          'Achan broke trust with the nation, Gehazi broke trust with his master Elisha, and Judas broke trust with Jesus and the disciples',
          "Only Judas' actions involved any broken trust",
          'Greed had no relational impact in any case',
        ],
        a: 1,
        e: "In each case, greed led the person to violate a relationship of trust — Achan with the whole nation's obedience to God, Gehazi with his master's integrity, and Judas with Jesus and his fellow disciples.",
        h: "Consider who was betrayed or let down by each person's greedy action.",
        yr: 'SS2',
      },
      {
        q: "What lesson about hidden sin can be drawn from Achan's and Gehazi's stories?",
        o: [
          'Hidden sin has no consequence if undiscovered',
          'Sin committed in secret can still be exposed and bring serious consequences',
          'Only public sins matter to God',
          'Secrecy guarantees escape from consequence',
        ],
        a: 1,
        e: 'Both Achan and Gehazi attempted to hide their greedy actions, yet both were exposed and faced serious consequences, teaching that secrecy does not shield one from the consequences of sin before God.',
        h: 'Consider that both men initially believed their actions were hidden from others.',
        yr: 'SS2',
      },
      {
        q: "What contrast exists between Elisha's attitude toward payment and Gehazi's attitude?",
        o: [
          'Both men desired payment equally',
          "Elisha refused payment to honour God's grace, while Gehazi greedily sought payment for himself through deception",
          'Elisha secretly wanted the payment Gehazi obtained',
          'Neither man cared about payment at all',
        ],
        a: 1,
        e: "Elisha deliberately refused Naaman's gifts to emphasize that the healing was God's free gift, while Gehazi's greed led him to seek personal gain through deceit, directly contradicting his master's example.",
        h: 'Consider the difference in motive and action between master and servant in this account.',
        yr: 'SS2',
      },
      {
        q: 'What broader Christian principle do these accounts of greed teach believers today?',
        o: [
          'Material wealth should be pursued at any cost',
          'Contentment and integrity should take priority over the pursuit of material gain, especially through dishonest means',
          'Only religious leaders need to avoid greed',
          'Greed has no relevance to modern Christian living',
        ],
        a: 1,
        e: 'These accounts collectively warn believers to prioritize contentment, integrity, and trust in God over the pursuit of material gain, particularly gain sought through dishonest or disobedient means.',
        h: 'Consider what value these stories, taken together, encourage believers to prioritize instead of material gain.',
        yr: 'SS2',
      },
      {
        q: "Which best captures why Judas' betrayal for money is considered a particularly tragic example of greed?",
        o: [
          'Because it involved a stranger rather than someone close to Jesus',
          'Because it involved betraying someone he knew intimately as a close companion and teacher, for a relatively small sum',
          'Because it had no lasting spiritual significance',
          'Because Judas gained great lasting wealth from it',
        ],
        a: 1,
        e: "Judas' betrayal is especially tragic because it involved selling out a close companion and teacher he had walked with for years, for a relatively modest sum of money, showing how deeply greed can corrupt even close relationships.",
        h: 'Consider the relationship Judas had with Jesus before his act of betrayal.',
        yr: 'SS2',
      },
      {
        q: 'What did the chief priests do with the thirty pieces of silver Judas returned to them?',
        o: [
          'They returned it to the temple treasury directly',
          'They used it to buy a field to bury foreigners in, called the Field of Blood',
          'They kept it for themselves personally',
          'They gave it to the Roman governor',
        ],
        a: 1,
        e: "Since it was considered blood money, the chief priests used the returned silver to buy the potter's field as a burial place for foreigners, which became known as the Field of Blood (Matthew 27:6-8).",
        h: 'This detail explains what became of the money after Judas returned it.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'The Supremacy of God',
    topicCode: 'SS2-CRS-11',
    module: 'Nature of God',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The Bible consistently portrays God as <span class="learn-keyword">supreme</span> — sovereign over nations, rulers, and all creation. This topic examines biblical evidence of God\'s supremacy and its implications for Christian life.\n</div>\n\n<h3 class="learn-subheading">1. God\'s Supremacy Over Nations and Rulers</h3>\n<p class="learn-p">Nebuchadnezzar, the powerful king of Babylon, was humbled by God after boasting of his own greatness, driven to live like an animal for seven years until he acknowledged, "the Most High rules the kingdom of men, and gives it to whomever He chooses" (Daniel 4:28-37). Similarly, Pharaoh\'s repeated resistance to God\'s demand to free Israel ultimately failed, demonstrating that no earthly ruler can withstand God\'s will (Exodus 5-14).</p>\n\n<h3 class="learn-subheading">2. God\'s Supremacy Over Creation</h3>\n<p class="learn-p">Genesis 1 presents God as the sole Creator of the heavens, the earth, and all living things, simply by His spoken word. The Psalms repeatedly celebrate God\'s sovereign control over nature: "The earth is the Lord\'s, and everything in it" (Psalm 24:1), and, "He who sits enthroned above the circle of the earth" (Isaiah 40:22).</p>\n\n<h3 class="learn-subheading">3. God\'s Supremacy Demonstrated Through Miracles</h3>\n<p class="learn-p">God\'s power over nature is displayed in events like the parting of the Red Sea (Exodus 14), the plagues of Egypt that overpowered Egyptian gods associated with the Nile, sun, and other natural forces (Exodus 7-12), and Jesus calming the storm, prompting His disciples to ask, "What kind of man is this? Even the winds and the waves obey him!" (Matthew 8:23-27).</p>\n\n<h3 class="learn-subheading">4. Lessons for Christians Today</h3>\n<p class="learn-p">Recognizing God\'s supremacy calls Christians to humility before Him, confidence that no earthly power can ultimately thwart God\'s purposes, and worship that acknowledges Him alone as sovereign over all creation and history.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Area of Supremacy</th><th>Biblical Example</th></tr></thead>\n    <tbody>\n      <tr><td>Over rulers/nations</td><td>Nebuchadnezzar\'s humbling (Daniel 4); Pharaoh\'s defeat (Exodus)</td></tr>\n      <tr><td>Over creation</td><td>Genesis 1 creation account; Psalm 24:1</td></tr>\n      <tr><td>Over nature (miracles)</td><td>Red Sea parting; Jesus calming the storm</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Remember that Nebuchadnezzar was humbled for seven years and lived like an animal (eating grass, hair growing like eagle\'s feathers) before acknowledging God\'s supremacy (Daniel 4:33).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> God\'s supremacy over nations, rulers, and all creation teaches Christians to humble themselves before Him and to trust confidently in His ultimate sovereignty over every circumstance.\n</div>\n',
    questions: [
      {
        q: 'What lesson did King Nebuchadnezzar learn after boasting about his own greatness?',
        o: [
          'That his kingdom would last forever unopposed',
          'That the Most High rules over the kingdoms of men and gives them to whomever He chooses',
          'That human rulers are equal to God',
          'That boasting has no spiritual consequence',
        ],
        a: 1,
        e: 'After being humbled and living like an animal for seven years, Nebuchadnezzar acknowledged that the Most High God rules over human kingdoms and gives authority to whomever He wills (Daniel 4:28-37).',
        h: "This lesson directly addressed Nebuchadnezzar's earlier pride about his own achievements.",
        yr: 'SS2',
      },
      {
        q: "How long did Nebuchadnezzar's period of humbling last, according to Daniel 4?",
        o: ['One year', 'Seven years', 'Forty days', 'Three years'],
        a: 1,
        e: "Nebuchadnezzar's humbling, during which he lived like an animal, lasted seven years ('seven times' — periods) until he acknowledged God's sovereignty (Daniel 4:16, 25, 32).",
        h: "This period is described using the phrase 'seven times.'",
        yr: 'SS2',
      },
      {
        q: 'What physical description is given of Nebuchadnezzar during his period of humbling?',
        o: [
          'He grew wings and flew away',
          "His hair grew like eagle's feathers and his nails like bird's claws, and he ate grass like an ox",
          'He lost the ability to speak forever',
          'He became invisible',
        ],
        a: 1,
        e: "Scripture describes Nebuchadnezzar during this period as eating grass like an ox, with his hair growing like eagle's feathers and his nails like a bird's claws (Daniel 4:33).",
        h: 'This vivid description compares his condition to that of an animal.',
        yr: 'SS2',
      },
      {
        q: 'How did God ultimately demonstrate His supremacy over Pharaoh and Egypt?',
        o: [
          'By making a peace treaty with Pharaoh',
          "Through the ten plagues and the eventual deliverance of Israel despite Pharaoh's repeated resistance",
          'By giving Pharaoh greater power',
          "By ignoring Pharaoh's actions entirely",
        ],
        a: 1,
        e: "Despite Pharaoh's repeated hardening of heart and refusal to let Israel go, God's power through the ten plagues ultimately forced Pharaoh's hand, demonstrating God's supremacy over the most powerful ruler of the time (Exodus 7-14).",
        h: "Consider the series of judgments that eventually broke Pharaoh's resistance.",
        yr: 'SS2',
      },
      {
        q: 'According to Psalm 24:1, to whom does the earth and everything in it belong?',
        o: ['To human rulers', 'To the Lord', 'To no one in particular', 'To the angels'],
        a: 1,
        e: "Psalm 24:1 declares, 'The earth is the Lord's, and everything in it, the world, and all who live in it,' affirming God's ownership and supremacy over all creation.",
        h: 'This verse begins by naming the ultimate owner of the earth.',
        yr: 'SS2',
      },
      {
        q: "How does Isaiah 40:22 describe God's position relative to the earth?",
        o: [
          'As a distant, uninvolved observer',
          'As one who sits enthroned above the circle of the earth',
          'As equal in power to earthly kings',
          'As limited to a specific geographic location',
        ],
        a: 1,
        e: "Isaiah 40:22 describes God as the One who 'sits enthroned above the circle of the earth,' emphasizing His transcendence and supreme authority over creation.",
        h: 'This verse uses royal imagery of a throne positioned above the earth.',
        yr: 'SS2',
      },
      {
        q: 'What happened when Jesus calmed the storm on the Sea of Galilee?',
        o: [
          'The disciples were unimpressed',
          'The wind and waves obeyed His command, leading the disciples to marvel at who He was',
          'The storm grew worse',
          'Jesus refused to intervene',
        ],
        a: 1,
        e: "When Jesus rebuked the storm, the wind and waves immediately became calm, prompting the amazed disciples to ask, 'What kind of man is this? Even the winds and the waves obey him!' (Matthew 8:23-27).",
        h: 'This miracle involved Jesus directly commanding the weather to obey Him.',
        yr: 'SS2',
      },
      {
        q: 'According to Genesis 1, how did God create the heavens and the earth?',
        o: [
          'Through a long process of trial and error',
          'By His spoken word',
          'With the help of other beings',
          'By using pre-existing materials given by another god',
        ],
        a: 1,
        e: "Genesis 1 repeatedly shows God creating through His spoken word ('And God said... and it was so'), demonstrating His supreme power to bring all things into existence.",
        h: 'Consider the repeated phrase used throughout the Genesis 1 creation account.',
        yr: 'SS2',
      },
      {
        q: "The plagues of Egypt are often understood as demonstrating God's supremacy over what?",
        o: [
          "Only Pharaoh's personal wealth",
          'The Egyptian gods associated with natural forces such as the Nile and the sun',
          'Roman gods',
          'The gods of Babylon',
        ],
        a: 1,
        e: "Many scholars note that the plagues specifically targeted natural elements associated with Egyptian deities — for example, the Nile turning to blood challenged the god Hapi, and darkness over the land challenged the sun god Ra — showing God's supremacy over Egypt's entire religious system.",
        h: 'Consider what natural elements the plagues targeted and their religious associations in Egypt.',
        yr: 'SS2',
      },
      {
        q: 'What overarching theme unites the accounts of Nebuchadnezzar, Pharaoh, creation, and Jesus calming the storm?',
        o: [
          "God's power is limited to spiritual matters only",
          "God's supreme authority extends over rulers, nations, creation, and nature itself",
          'Human rulers are ultimately more powerful than God',
          'These accounts have no thematic connection',
        ],
        a: 1,
        e: "Together, these accounts demonstrate that God's supremacy is comprehensive — extending over powerful human rulers, the natural world, and creation itself, leaving no realm outside His sovereign authority.",
        h: 'Consider what single theme links all four examples studied in this topic.',
        yr: 'SS2',
      },
      {
        q: "What does Nebuchadnezzar's restoration after his humbling teach about God's supremacy?",
        o: [
          "That God's judgment is always permanent with no restoration",
          "That God's supremacy includes both the power to humble and the mercy to restore those who acknowledge Him",
          "That Nebuchadnezzar's kingdom was restored without any change in him",
          'That earthly rulers cannot be humbled by God',
        ],
        a: 1,
        e: "After Nebuchadnezzar humbled himself and acknowledged God's sovereignty, his sanity and kingdom were restored, showing that God's supremacy includes both the power to bring low and the grace to restore the humble (Daniel 4:34-37).",
        h: "Consider what happened to Nebuchadnezzar after he acknowledged God's rule.",
        yr: 'SS2',
      },
      {
        q: "How does recognizing God's supremacy over creation shape a Christian's view of the natural world?",
        o: [
          'It teaches that nature operates independently of God',
          "It affirms that all of creation, including nature's laws, remains under God's sovereign authority and control",
          'It suggests nature should be worshipped instead of God',
          'It has no practical implication for daily life',
        ],
        a: 1,
        e: "Understanding God's supremacy over creation teaches Christians that even the forces of nature remain under His sovereign control, encouraging trust in God even amid natural uncertainties or dangers.",
        h: "Consider how belief in God's control over creation might shape one's confidence during difficult natural circumstances.",
        yr: 'SS2',
      },
      {
        q: "What is a key difference between Pharaoh's and Nebuchadnezzar's responses to God's supremacy?",
        o: [
          'Both refused to acknowledge God at all',
          'Pharaoh persistently resisted until forced by judgment, while Nebuchadnezzar eventually humbled himself and acknowledged God willingly',
          'Nebuchadnezzar never acknowledged God, unlike Pharaoh',
          'Both responded identically with immediate humility',
        ],
        a: 1,
        e: "Pharaoh repeatedly hardened his heart and resisted God despite mounting judgments, while Nebuchadnezzar, after his humbling experience, willingly and openly acknowledged God's supremacy (compare Exodus 8:15; 9:34 with Daniel 4:34-37).",
        h: "Consider the different attitudes each ruler ultimately displayed toward God's authority.",
        yr: 'SS2',
      },
      {
        q: "Why might the disciples' question, 'What kind of man is this?' after the storm was calmed, be theologically significant?",
        o: [
          'It shows the disciples doubted Jesus entirely',
          'It reflects their growing recognition that Jesus possessed authority that only God has over creation',
          'It shows the disciples were unaffected by the miracle',
          "It has no connection to Jesus' identity",
        ],
        a: 1,
        e: "The disciples' astonished question reflects their dawning recognition that Jesus exercised an authority over nature that, in Jewish understanding, belonged to God alone — pointing toward His divine identity.",
        h: 'Consider what kind of authority only God was traditionally understood to have over nature.',
        yr: 'SS2',
      },
      {
        q: "How does the theme of God's supremacy encourage Christians facing powerful opposition or difficult circumstances today?",
        o: [
          'It suggests believers should give up when facing powerful opposition',
          "It offers confidence that no earthly power, however great, can ultimately thwart God's sovereign purposes",
          'It implies that opposition to Christians never occurs',
          "It teaches that God's power is limited by human rulers",
        ],
        a: 1,
        e: "Recognizing that God's supremacy proved greater than even the most powerful ancient rulers gives Christians today confidence that no earthly opposition can ultimately overcome God's sovereign purposes.",
        h: 'Consider how the examples of Pharaoh and Nebuchadnezzar might encourage believers facing their own difficulties.',
        yr: 'SS2',
      },
      {
        q: "What response does recognizing God's supremacy call for from human beings, according to the lessons of this topic?",
        o: [
          'Indifference toward God',
          'Humility and worship, acknowledging Him as sovereign over all things',
          "Attempts to rival God's authority",
          'Fear without any accompanying trust or worship',
        ],
        a: 1,
        e: "The consistent biblical response to encountering God's supremacy is humility and worship, as seen in Nebuchadnezzar's eventual praise of God and the disciples' awe at Jesus' authority over the storm.",
        h: "Consider the appropriate human response modelled by those who encountered God's supreme power in these accounts.",
        yr: 'SS2',
      },
      {
        q: 'Which best explains why the plagues are considered a more decisive demonstration of supremacy than a single miracle?',
        o: [
          "Because they occurred as an escalating series that progressively overcame Pharaoh's resistance and Egypt's entire religious system",
          'Because only one plague actually occurred',
          'Because they had no cumulative effect on Pharaoh',
          'Because they targeted only Pharaoh personally, not the nation',
        ],
        a: 0,
        e: "Unlike a single isolated miracle, the ten plagues formed an escalating series that systematically challenged Egypt's gods and Pharaoh's resistance until his power was thoroughly broken, making it a sustained and comprehensive demonstration of God's supremacy.",
        h: 'Consider why a series of ten distinct judgments would have a different impact than one single miracle.',
        yr: 'SS2',
      },
      {
        q: "What is the overall theological conclusion drawn from studying God's supremacy over nations, creation, and nature?",
        o: [
          "God's authority is confined to spiritual, non-physical matters",
          'God is sovereign over every dimension of existence — political, natural, and physical — leaving nothing outside His authority',
          'Human rulers share equal authority with God',
          "God's supremacy applied only in ancient times, not today",
        ],
        a: 1,
        e: "Taken together, the biblical evidence teaches that God's sovereignty is total and comprehensive, extending over political powers, the natural world, and physical events alike — a truth that remains unchanged today.",
        h: 'Consider the breadth of areas — political, natural, physical — covered by the examples studied in this topic.',
        yr: 'SS2',
      },
      {
        q: "What image does Isaiah use elsewhere to further stress God's supreme, incomparable greatness over the nations?",
        o: [
          'That the nations are like a drop in a bucket before Him',
          'That the nations are equal in power to God',
          'That God depends on the nations for His authority',
          "That the nations control God's decisions",
        ],
        a: 0,
        e: 'Isaiah 40:15 describes the nations as being like a drop in a bucket and regarded as dust on the scales before God, powerfully emphasizing His supremacy over all earthly powers.',
        h: "Consider the vivid comparison Isaiah uses in the same chapter about God's greatness relative to nations.",
        yr: 'SS2',
      },
      {
        q: "What title is commonly used in the Psalms and Isaiah to express God's supreme rule over all creation and history?",
        o: ['Sovereign / Most High', 'Servant', 'Wanderer', 'Apprentice'],
        a: 0,
        e: "Scripture frequently uses titles such as 'Most High' or describes God as sovereign to express His supreme rule over all creation, nations, and history.",
        h: 'Consider the kind of title that would express supreme authority over everything.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Religious Reforms',
    topicCode: 'SS2-CRS-12',
    module: 'Worship and Reform',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Throughout Israel\'s history, godly kings occasionally led <span class="learn-keyword">religious reforms</span> to remove idolatry and restore true worship of God. This topic examines the reforms of <span class="learn-keyword">Josiah</span> and <span class="learn-keyword">Nehemiah</span> as examples of restoring true worship.\n</div>\n\n<h3 class="learn-subheading">1. Josiah\'s Religious Reform</h3>\n<p class="learn-p">Josiah became king of Judah at just eight years old and, as a young man, began seeking God. During temple repairs, the Book of the Law was rediscovered, and upon hearing it read, Josiah tore his clothes in distress at how far the nation had strayed from God\'s commands (2 Kings 22:8-13). He then led a thorough reform: destroying idols and pagan altars, removing corrupt priests, restoring the Passover celebration, and renewing the covenant with God before the people (2 Kings 23:1-25).</p>\n\n<h3 class="learn-subheading">2. Nehemiah\'s Religious Reform</h3>\n<p class="learn-p">After rebuilding Jerusalem\'s walls, Nehemiah, as governor, worked with Ezra the priest to lead the people in a renewed covenant with God, including public reading and teaching of the Law (Nehemiah 8). Nehemiah also enforced reforms such as stopping trade on the Sabbath, addressing intermarriage with pagan nations that led to idolatry, and ensuring the temple and its services were properly supported (Nehemiah 13:1-31).</p>\n\n<h3 class="learn-subheading">3. Common Features of True Reform</h3>\n<p class="learn-p">Both reforms involved returning to Scripture as the standard for right worship, removing practices that compromised loyalty to God, public repentance and covenant renewal, and decisive leadership willing to confront long-standing wrongs.</p>\n\n<h3 class="learn-subheading">4. Importance of True Worship and Reform in Christian Life</h3>\n<p class="learn-p">These reforms teach that true worship must be continually guarded and, when compromised, requires courageous correction. They remind Christians of the ongoing need to examine their faith practices against God\'s word and to repent and realign when they have drifted.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Leader</th><th>Key Reform Action</th></tr></thead>\n    <tbody>\n      <tr><td>Josiah</td><td>Destroyed idols; restored Passover; renewed the covenant after rediscovering the Book of the Law</td></tr>\n      <tr><td>Nehemiah</td><td>Renewed covenant reading with Ezra; enforced Sabbath observance; addressed intermarriage; supported temple worship</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Remember that Josiah\'s reform was triggered by the rediscovery of the Book of the Law during temple repairs — a frequently tested detail (2 Kings 22:8).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Josiah and Nehemiah\'s reforms show that returning to Scripture, decisive leadership, and public renewal of covenant with God are essential elements of restoring true worship after periods of spiritual decline.\n</div>\n',
    questions: [
      {
        q: 'At what age did Josiah become king of Judah?',
        o: ['Eighteen years old', 'Eight years old', 'Twenty-eight years old', 'Forty years old'],
        a: 1,
        e: 'Josiah became king of Judah at the young age of eight and began seeking God earnestly as he grew (2 Kings 22:1; 2 Chronicles 34:1, 3).',
        h: 'He was still a child when he began his reign.',
        yr: 'SS2',
      },
      {
        q: "What significant discovery during temple repairs led to Josiah's major religious reform?",
        o: [
          'A hidden treasure of gold',
          'The Book of the Law',
          'An ancient idol',
          'A record of past kings',
        ],
        a: 1,
        e: 'While repairing the temple, Hilkiah the high priest found the Book of the Law, which was brought to Josiah and read to him, triggering deep conviction and reform (2 Kings 22:8-11).',
        h: "This discovery was a written document central to Israel's covenant with God.",
        yr: 'SS2',
      },
      {
        q: 'How did Josiah react upon hearing the words of the Book of the Law read to him?',
        o: [
          'He ignored it completely',
          "He tore his clothes in distress, realizing how far Judah had strayed from God's commands",
          'He ordered the book destroyed',
          'He laughed at its contents',
        ],
        a: 1,
        e: 'Upon hearing the Law read, Josiah tore his clothes, a sign of deep grief and repentance, because he recognized how greatly Judah had departed from what God required (2 Kings 22:11).',
        h: 'This reaction is a traditional sign of mourning or distress in ancient Israelite culture.',
        yr: 'SS2',
      },
      {
        q: "Which of these was part of Josiah's religious reform?",
        o: [
          'Building more pagan altars',
          'Destroying idols and pagan altars throughout the land',
          'Ending temple worship entirely',
          'Cancelling the Passover celebration',
        ],
        a: 1,
        e: "Josiah's reform included destroying idols, pagan altars, and other objects of false worship throughout Judah and even in parts of the former northern kingdom (2 Kings 23:4-20).",
        h: 'His reform actively removed, rather than added, objects of worship.',
        yr: 'SS2',
      },
      {
        q: 'What important festival did Josiah restore as part of his reform?',
        o: [
          'The Feast of Tabernacles',
          'The Passover',
          'The Day of Atonement only',
          'The Feast of Trumpets',
        ],
        a: 1,
        e: 'Josiah restored the celebration of the Passover according to what was written in the Book of the Covenant, a celebration that had not been kept in this manner since the days of the judges (2 Kings 23:21-23).',
        h: "This festival commemorates Israel's deliverance from slavery in Egypt.",
        yr: 'SS2',
      },
      {
        q: 'What did Josiah do before the people as part of renewing the covenant?',
        o: [
          'He appointed new foreign gods',
          'He read the Book of the Covenant aloud and led the people in renewing their covenant with God',
          'He abolished the covenant entirely',
          'He forbade any public gatherings',
        ],
        a: 1,
        e: "Josiah gathered the people, read the Book of the Covenant to them, and led them in publicly renewing their commitment to follow God's commands (2 Kings 23:1-3).",
        h: "This act involved a public, communal recommitment to God's covenant.",
        yr: 'SS2',
      },
      {
        q: 'Who assisted Nehemiah in leading the people in a renewed covenant through public reading of the Law?',
        o: [
          'Elisha the prophet',
          'Ezra the priest and scribe',
          'King Solomon',
          'Isaiah the prophet',
        ],
        a: 1,
        e: "Nehemiah worked alongside Ezra the priest, who read and explained the Law to the people, leading to their renewed commitment to God's covenant (Nehemiah 8:1-8).",
        h: 'This partner was a priest and scribe known for teaching the Law.',
        yr: 'SS2',
      },
      {
        q: 'What Sabbath-related reform did Nehemiah enforce among the people?',
        o: [
          'He abolished the Sabbath entirely',
          'He stopped merchants from trading and working on the Sabbath',
          'He required extra work on the Sabbath',
          'He moved the Sabbath to a different day',
        ],
        a: 1,
        e: 'Nehemiah observed that traders were conducting business on the Sabbath and took decisive action to close the gates of Jerusalem and stop this practice, restoring proper Sabbath observance (Nehemiah 13:15-22).',
        h: 'His reform addressed commercial activity happening on the day of rest.',
        yr: 'SS2',
      },
      {
        q: 'What issue involving marriage did Nehemiah address as part of his reform?',
        o: [
          'Forbidding marriage entirely',
          'Intermarriage with pagan nations that was leading Israelites into idolatry',
          'Requiring arranged marriages for all citizens',
          'Allowing unrestricted polygamy',
        ],
        a: 1,
        e: 'Nehemiah confronted the problem of Israelites intermarrying with people from surrounding pagan nations, which was drawing them away from exclusive worship of God, as it had done even to Solomon (Nehemiah 13:23-27).',
        h: "This issue concerned marriages that compromised the nation's spiritual loyalty.",
        yr: 'SS2',
      },
      {
        q: "What common starting point did both Josiah's and Nehemiah's reforms share?",
        o: [
          'A military victory',
          "A return to and public reading of God's written Law as the standard for right living",
          'A foreign alliance',
          'An economic crisis',
        ],
        a: 1,
        e: "Both reforms were grounded in returning to and publicly reading God's Law, using it as the authoritative standard for correcting the people's worship and conduct (2 Kings 22:8-11; Nehemiah 8:1-8).",
        h: "Consider what document played a central triggering role in both leaders' reforms.",
        yr: 'SS2',
      },
      {
        q: 'What leadership quality did both Josiah and Nehemiah display in carrying out their reforms?',
        o: [
          'Passivity in the face of wrongdoing',
          'Decisive courage to confront long-standing wrong practices',
          'Avoidance of confrontation',
          "Indifference to the people's spiritual condition",
        ],
        a: 1,
        e: 'Both leaders showed decisive courage — Josiah in tearing down deeply entrenched idolatrous practices, and Nehemiah in confronting the wealthy and influential over Sabbath violations and improper intermarriage — despite likely resistance.',
        h: 'Consider the boldness required to confront practices that had likely existed for a long time.',
        yr: 'SS2',
      },
      {
        q: 'Why is public covenant renewal, as practiced by both Josiah and Nehemiah, significant?',
        o: [
          "It had no real effect on the people's behaviour",
          "It publicly affirmed the nation's collective commitment to obey God, strengthening communal accountability",
          'It replaced the need for personal faith',
          'It was purely symbolic with no practical follow-through',
        ],
        a: 1,
        e: "Public covenant renewal created a shared, communal commitment before God and one another, reinforcing accountability for the nation to live according to God's Law rather than leaving obedience to isolated individuals.",
        h: 'Consider the value of a public, communal commitment versus a purely private one.',
        yr: 'SS2',
      },
      {
        q: "What does the rediscovery of the Book of the Law during Josiah's reign suggest about the spiritual state of Judah before his reform?",
        o: [
          "Judah had been faithfully following God's Law all along",
          "Judah had neglected God's Law to the point that it had been lost or forgotten in the temple",
          'The Law had never existed before this point',
          'The Law was rediscovered but considered unimportant',
        ],
        a: 1,
        e: "The fact that the Book of the Law had apparently been lost or neglected within the temple itself reveals just how far Judah's spiritual life had declined before Josiah's reform began.",
        h: 'Consider what it implies that such an important document had gone missing or unused.',
        yr: 'SS2',
      },
      {
        q: "How does Nehemiah's approach to reform differ somewhat from Josiah's, in terms of his position?",
        o: [
          'Nehemiah was a king, like Josiah',
          'Nehemiah served as governor and layperson, working alongside the priest Ezra, rather than as a king',
          'Nehemiah had no official position at all',
          'Nehemiah was a foreign ruler over Judah',
        ],
        a: 1,
        e: 'Unlike Josiah, who reformed as king, Nehemiah led reform as governor of Judah, working in partnership with Ezra the priest, showing that reform could be led through different types of God-given authority and cooperation.',
        h: 'Consider the specific role Nehemiah held, as distinct from a king like Josiah.',
        yr: 'SS2',
      },
      {
        q: 'What underlying spiritual problem did both reforms ultimately seek to correct?',
        o: [
          'Poor agricultural practices',
          'The compromise of true worship of God through idolatry and disregard for His Law',
          'Excessive military spending',
          'Disputes over land boundaries',
        ],
        a: 1,
        e: "At the heart of both reforms was the need to correct Israel's compromise of true, exclusive worship of God — whether through idolatry (Josiah's context) or neglect of the Law and improper alliances (Nehemiah's context).",
        h: 'Consider what deeper spiritual issue both leaders were ultimately trying to address.',
        yr: 'SS2',
      },
      {
        q: "What lesson do Josiah's and Nehemiah's reforms teach about the ongoing need for spiritual vigilance?",
        o: [
          'Once true worship is established, it never requires further attention',
          'True worship can gradually be compromised over time and periodically requires intentional correction and renewal',
          "Reform is only necessary once in a nation's entire history",
          'Spiritual vigilance is unnecessary if leaders are godly',
        ],
        a: 1,
        e: "Both reforms occurred after periods when true worship had been significantly compromised, teaching that maintaining faithful worship requires ongoing vigilance and periodic intentional correction, even among God's people.",
        h: 'Consider why reform was needed at all if worship had once been rightly established.',
        yr: 'SS2',
      },
      {
        q: "How might Josiah's and Nehemiah's reforms be applied to a Christian congregation today that has drifted from biblical teaching?",
        o: [
          'The congregation should ignore the drift and continue as usual',
          "The congregation should return to Scripture, honestly confront the drift, and pursue renewed, decisive commitment to God's standards",
          'Reform should be avoided to prevent conflict',
          'Only external religious authorities can initiate reform',
        ],
        a: 1,
        e: "Applying the pattern of Josiah and Nehemiah, a congregation recognizing spiritual drift should return to Scripture as its standard, honestly confront areas of compromise, and pursue a renewed, decisive commitment to God's ways.",
        h: 'Consider the pattern followed by both leaders when they recognized their nation had drifted from true worship.',
        yr: 'SS2',
      },
      {
        q: 'Which best summarizes the overall significance of studying religious reform through Josiah and Nehemiah?',
        o: [
          'Reform is unnecessary once a nation has a temple or place of worship',
          'True worship must be actively guarded and, when compromised, requires courageous, Scripture-grounded correction',
          'Only kings can lead legitimate religious reform',
          'Reform always fails and produces no lasting benefit',
        ],
        a: 1,
        e: "The combined lessons of Josiah's and Nehemiah's reforms emphasize that true worship is not automatically self-sustaining; it must be actively guarded, and when it drifts, requires courageous correction grounded firmly in God's word.",
        h: 'Consider the overarching principle these two related but distinct reform accounts teach together.',
        yr: 'SS2',
      },
      {
        q: 'Besides destroying idols, what did Josiah do regarding corrupt priests who had served false gods?',
        o: [
          'He promoted them to higher positions',
          'He removed them from their positions',
          'He ignored their activities entirely',
          'He appointed them as royal advisors',
        ],
        a: 1,
        e: 'As part of his reform, Josiah removed the corrupt priests who had been burning incense to Baal, the sun, moon, and other false gods (2 Kings 23:5).',
        h: 'Consider what action Josiah took against religious leaders who had promoted false worship.',
        yr: 'SS2',
      },
      {
        q: 'What did Nehemiah do when he discovered the temple storerooms were being misused, as recorded in Nehemiah 13:4-9?',
        o: [
          'He ignored the misuse of the storerooms',
          'He removed the offending items and restored the rooms for their proper temple use',
          'He gave the storerooms away permanently',
          'He built new storerooms elsewhere instead',
        ],
        a: 1,
        e: "Nehemiah discovered that a room in the temple courts had been improperly given to Tobiah for his personal use; he removed Tobiah's belongings and had the room purified and restored for its proper temple purpose (Nehemiah 13:4-9).",
        h: "Consider Nehemiah's decisive action upon discovering misuse of a sacred space.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: "Concern for One's Nation",
    topicCode: 'SS2-CRS-13',
    module: 'Christian Citizenship',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Nehemiah</span> stands as a model of deep patriotism and concern for the wellbeing of his nation. This topic examines his example and what it teaches about the responsibilities of Christians toward their nation.\n</div>\n\n<h3 class="learn-subheading">1. Nehemiah\'s Concern for Jerusalem</h3>\n<p class="learn-p">While serving as cupbearer to King Artaxerxes of Persia, Nehemiah learned that Jerusalem\'s walls lay broken and its gates burned, leaving the city and its people vulnerable and in disgrace. This news deeply grieved him, leading him to weep, mourn, fast, and pray for days (Nehemiah 1:1-4).</p>\n\n<h3 class="learn-subheading">2. Nehemiah\'s Prayer for His Nation</h3>\n<p class="learn-p">Nehemiah\'s prayer combined confession of Israel\'s collective sin, remembrance of God\'s promises, and a specific request for favour before the king (Nehemiah 1:5-11). This model prayer reflects genuine concern that moves from grief to intercession to action.</p>\n\n<h3 class="learn-subheading">3. Nehemiah\'s Action for His Nation</h3>\n<p class="learn-p">Nehemiah requested permission and resources from King Artaxerxes to return to Jerusalem and rebuild its walls (Nehemiah 2:1-8). Despite opposition from Sanballat, Tobiah, and Geshem, he organized the people, and the wall was rebuilt in just fifty-two days (Nehemiah 4; 6:15).</p>\n\n<h3 class="learn-subheading">4. Responsibilities of Christians Toward Their Nation</h3>\n<p class="learn-p">Nehemiah\'s example teaches believers to genuinely care about their nation\'s condition, to pray earnestly for it, to use their position and resources to contribute to its wellbeing, and to persevere through opposition in pursuing what benefits the wider community. This reflects broader biblical teaching, such as Jeremiah\'s instruction to "seek the peace and prosperity of the city" (Jeremiah 29:7).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Stage</th><th>Nehemiah\'s Response</th></tr></thead>\n    <tbody>\n      <tr><td>Hearing bad news</td><td>Wept, mourned, fasted, and prayed (Nehemiah 1:4)</td></tr>\n      <tr><td>Praying</td><td>Confessed sin, recalled God\'s promises, requested favour (Nehemiah 1:5-11)</td></tr>\n      <tr><td>Acting</td><td>Sought royal permission, organized rebuilding, overcame opposition (Nehemiah 2, 4, 6)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Remember Nehemiah\'s official position (cupbearer to King Artaxerxes) and the exact number of days it took to complete the wall (fifty-two days, Nehemiah 6:15) — both are frequently tested details.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Nehemiah\'s genuine grief over his nation\'s condition, his earnest prayer, and his decisive action provide a model for Christian patriotism — caring, praying, and working for the wellbeing of one\'s country.\n</div>\n',
    questions: [
      {
        q: 'What official position did Nehemiah hold in the Persian royal court?',
        o: [
          'Military general',
          'Cupbearer to King Artaxerxes',
          'High priest',
          'Provincial tax collector',
        ],
        a: 1,
        e: 'Nehemiah served as cupbearer to King Artaxerxes of Persia, a position of trust close to the king (Nehemiah 1:11; 2:1).',
        h: "This role involved serving the king's drink and required a high level of personal trust.",
        yr: 'SS2',
      },
      {
        q: 'What news deeply grieved Nehemiah at the beginning of his account?',
        o: [
          'That the temple had been rebuilt successfully',
          "That Jerusalem's walls were broken down and its gates burned with fire",
          'That King Artaxerxes had died',
          'That Ezra had returned to Babylon',
        ],
        a: 1,
        e: "Nehemiah learned from his brother Hanani and other men from Judah that the remnant in Jerusalem were in great distress, with the city's walls broken down and its gates destroyed by fire (Nehemiah 1:2-3).",
        h: "This report described the physical condition of Jerusalem's defensive structures.",
        yr: 'SS2',
      },
      {
        q: 'How did Nehemiah initially respond upon hearing this distressing news?',
        o: [
          'He ignored it and continued his duties as usual',
          'He sat down, wept, mourned, fasted, and prayed for days',
          'He immediately resigned from his position',
          'He blamed the king for the situation',
        ],
        a: 1,
        e: "Nehemiah's response was one of deep grief — he sat down and wept, mourned for days, and continued fasting and praying before God (Nehemiah 1:4).",
        h: 'His reaction combined emotional grief with an extended period of prayer and fasting.',
        yr: 'SS2',
      },
      {
        q: "What elements did Nehemiah's prayer in Nehemiah 1:5-11 include?",
        o: [
          'Only a request for personal wealth',
          "Confession of Israel's sin, remembrance of God's promises, and a specific request for favour before the king",
          'Praise with no confession or request',
          'A complaint against God',
        ],
        a: 1,
        e: "Nehemiah's prayer combined confession of the sins of Israel (including his own family and himself), recalling God's covenant promises, and a specific petition asking for success and favour before the king (Nehemiah 1:5-11).",
        h: 'His prayer moved through several distinct elements — confession, remembrance, and a specific request.',
        yr: 'SS2',
      },
      {
        q: 'What specific request did Nehemiah eventually make to King Artaxerxes?',
        o: [
          'Permission to become a Persian citizen',
          'Permission and resources to return to Jerusalem and rebuild its walls',
          'A larger salary as cupbearer',
          'Permission to build a new palace',
        ],
        a: 1,
        e: 'Nehemiah asked the king for permission to travel to Judah to rebuild the city of his ancestors, along with letters ensuring safe passage and timber for the work (Nehemiah 2:1-8).',
        h: "His request focused specifically on Jerusalem's physical restoration.",
        yr: 'SS2',
      },
      {
        q: "Who were among the main opponents who tried to hinder Nehemiah's rebuilding efforts?",
        o: [
          'Ezra and the priests',
          'Sanballat, Tobiah, and Geshem',
          'King Artaxerxes himself',
          'The prophet Haggai',
        ],
        a: 1,
        e: "Sanballat the Horonite, Tobiah the Ammonite, and Geshem the Arab were among the chief opponents who mocked, threatened, and schemed against Nehemiah's rebuilding project (Nehemiah 2:19; 4:1-8; 6:1-14).",
        h: 'These three figures repeatedly opposed Nehemiah throughout the rebuilding process.',
        yr: 'SS2',
      },
      {
        q: 'How did Nehemiah organize the people to defend against opposition while rebuilding?',
        o: [
          'He disbanded the work entirely until the threat passed',
          'He had workers carry tools in one hand and weapons in the other',
          'He hired foreign mercenaries exclusively',
          'He ignored the threats completely without any precaution',
        ],
        a: 1,
        e: 'Nehemiah organized the workers so that half continued building while the other half stood guard, and workers carried building tools with one hand while holding a weapon in the other (Nehemiah 4:16-18).',
        h: 'His strategy combined continued work with active defensive preparation.',
        yr: 'SS2',
      },
      {
        q: "How many days did it take to complete the rebuilding of Jerusalem's wall?",
        o: ['Twelve days', 'Fifty-two days', 'One hundred days', 'One year'],
        a: 1,
        e: "Despite fierce opposition, the wall of Jerusalem was completed in just fifty-two days, a remarkable achievement that even impressed Israel's enemies (Nehemiah 6:15-16).",
        h: 'This specific figure highlights the remarkable speed of the project despite opposition.',
        yr: 'SS2',
      },
      {
        q: "What was the reaction of Israel's enemies once the wall was completed so quickly?",
        o: [
          'They were unimpressed and unaffected',
          'They were afraid and recognized that the work had been accomplished with the help of God',
          'They immediately attacked the completed wall',
          'They praised themselves for allowing it to happen',
        ],
        a: 1,
        e: 'When the surrounding nations heard the wall was finished, they were afraid and lost their self-confidence, recognizing that the work had been done with the help of God (Nehemiah 6:16).',
        h: 'Their response acknowledged a power greater than mere human effort behind the achievement.',
        yr: 'SS2',
      },
      {
        q: 'What does Jeremiah 29:7 instruct exiled Israelites to do regarding the foreign city they lived in?',
        o: [
          'To oppose and undermine the city at every opportunity',
          'To seek the peace and prosperity of the city, praying to the Lord for it',
          "To avoid all interaction with the city's residents",
          'To immediately attempt to overthrow its government',
        ],
        a: 1,
        e: 'Jeremiah 29:7 instructs the exiles to seek the peace and prosperity of the city where they were living and to pray to the Lord on its behalf, since its welfare would also benefit them.',
        h: 'This instruction concerns how exiles should relate to their surrounding, even foreign, community.',
        yr: 'SS2',
      },
      {
        q: "Which best describes the progression of Nehemiah's concern from grief to action?",
        o: [
          'Grief immediately led to action with no prayer involved',
          'Grief led first to prayer and confession, then to specific planning and decisive action',
          'Nehemiah acted first and prayed only afterward',
          'Nehemiah remained in grief without ever taking action',
        ],
        a: 1,
        e: "Nehemiah's response followed a clear progression: he grieved over the bad news, turned to sustained prayer and confession, and then moved to specific, practical action in seeking the king's permission and organizing the rebuilding.",
        h: 'Consider the sequence of steps Nehemiah followed from the moment he heard the bad news.',
        yr: 'SS2',
      },
      {
        q: "What does Nehemiah's willingness to leave a comfortable royal position to help his nation reveal about his character?",
        o: [
          'He was primarily interested in personal advancement',
          "He was willing to sacrifice comfort and security for the sake of his people's wellbeing",
          'He resented his position as cupbearer',
          'He acted only under royal compulsion',
        ],
        a: 1,
        e: "Nehemiah's readiness to leave the security and comfort of his position in the Persian court to undertake the difficult, uncertain task of rebuilding Jerusalem reveals genuine self-sacrificing concern for his nation.",
        h: 'Consider what Nehemiah gave up by requesting to leave his comfortable royal role.',
        yr: 'SS2',
      },
      {
        q: "How did Nehemiah's approach to opposition demonstrate wisdom in national service?",
        o: [
          'He avoided all confrontation and abandoned the project',
          'He combined prayer, practical defensive measures, and continued diligent work despite threats',
          'He relied solely on military aggression against his opponents',
          "He gave in to his opponents' demands to avoid conflict",
        ],
        a: 1,
        e: "Nehemiah's response to opposition combined prayer, wise practical defensive organization, and continued perseverance in the actual work, modelling balanced, faithful leadership under pressure (Nehemiah 4:9, 16-18).",
        h: 'Consider the combination of spiritual and practical responses Nehemiah used to handle threats.',
        yr: 'SS2',
      },
      {
        q: "What responsibility toward one's nation does Nehemiah's example primarily model for Christians today?",
        o: [
          'Complete detachment from national affairs',
          "Active concern, prayer, and willingness to contribute practically to the nation's wellbeing",
          'Blind loyalty regardless of moral considerations',
          'Opposition to all forms of government',
        ],
        a: 1,
        e: "Nehemiah's example encourages Christians to actively care about, pray for, and contribute practically to the wellbeing of their nation, rather than remaining detached or indifferent to its condition.",
        h: "Consider what overall pattern of engagement Nehemiah's life models for believers today.",
        yr: 'SS2',
      },
      {
        q: "Why is Nehemiah's confession of Israel's sin, including his own, in his prayer significant?",
        o: [
          "It shows he blamed only others for the nation's troubles",
          "It shows genuine humility, recognizing his own connection to and responsibility for his nation's spiritual condition",
          'It had no bearing on the effectiveness of his prayer',
          'It suggests Nehemiah believed himself entirely without sin',
        ],
        a: 1,
        e: "By including himself and his family in the confession of Israel's sins ('I and my father's house have sinned,' Nehemiah 1:6), Nehemiah displayed humility and genuine solidarity with his nation's spiritual condition, rather than self-righteous distancing.",
        h: 'Consider whose sins, specifically, Nehemiah included in his confession.',
        yr: 'SS2',
      },
      {
        q: "What role did prayer play in Nehemiah's successful request to King Artaxerxes?",
        o: [
          'Prayer played no role in the interaction',
          'Nehemiah briefly prayed silently to God for favour immediately before answering the king',
          'Nehemiah refused to pray, relying solely on his own persuasive skill',
          'Prayer occurred only after the king had already granted his request',
        ],
        a: 1,
        e: 'When the king asked what Nehemiah wanted, Nehemiah records that he prayed silently to God before answering, showing his continual reliance on God even in a brief, high-pressure moment (Nehemiah 2:4-5).',
        h: "Consider the brief but significant action Nehemiah took just before responding to the king's question.",
        yr: 'SS2',
      },
      {
        q: "How does Nehemiah's balance of dependence on God and practical action serve as a model for national service?",
        o: [
          'It suggests prayer alone is sufficient, without any need for action',
          'It shows that genuine faith combines earnest prayer with diligent, practical effort',
          'It shows that practical planning is unnecessary if one prays enough',
          'It implies faith and action are unrelated to each other',
        ],
        a: 1,
        e: "Nehemiah's life demonstrates that effective service to one's nation involves both earnest reliance on God through prayer and diligent, practical effort — neither faith nor action alone was sufficient on its own.",
        h: 'Consider how Nehemiah combined both spiritual dependence and hands-on organizational effort throughout the account.',
        yr: 'SS2',
      },
      {
        q: "What broader Christian principle about citizenship can be drawn from Nehemiah's example and Jeremiah 29:7?",
        o: [
          'Christians should remain entirely indifferent to the wellbeing of the communities and nations they live in',
          'Christians are called to actively seek the wellbeing of their community or nation, praying and working for its benefit',
          'Christians should only be concerned with their own personal spiritual condition',
          'National concern is contrary to Christian values',
        ],
        a: 1,
        e: "Both Nehemiah's example and Jeremiah's instruction to the exiles teach that believers have a responsibility to actively seek and contribute to the wellbeing of the community or nation in which they live, even under difficult circumstances.",
        h: "Consider what shared lesson both Nehemiah's story and Jeremiah's instruction to the exiles convey about civic responsibility.",
        yr: 'SS2',
      },
      {
        q: "Which best summarizes the overall lesson of Nehemiah's concern for his nation for Christians facing challenges in their own countries today?",
        o: [
          'Christians should give up hope when their nation faces serious problems',
          'Genuine concern, grounded in prayer and translated into decisive, faithful action, can bring meaningful change even against strong opposition',
          'National problems are always too large for any individual to meaningfully address',
          'Only political leaders bear any responsibility for national wellbeing',
        ],
        a: 1,
        e: "Nehemiah's account encourages Christians that genuine concern for their nation, combined with earnest prayer and decisive, faithful action, can bring about meaningful positive change, even when facing significant opposition.",
        h: "Consider the overall encouragement Nehemiah's success against strong opposition offers to believers today.",
        yr: 'SS2',
      },
      {
        q: 'What did Nehemiah do regarding the reproach the surviving remnant faced in Jerusalem, beyond rebuilding the wall?',
        o: [
          'He ignored their social distress entirely',
          'He addressed economic injustice among the people, such as unfair lending practices burdening the poor',
          'He increased taxes on the poor',
          'He encouraged the wealthy to exploit the poor further',
        ],
        a: 1,
        e: 'Nehemiah also addressed internal social injustice, confronting nobles and officials who were charging excessive interest and burdening poorer Israelites, and required the practice to stop (Nehemiah 5:1-13).',
        h: "Consider Nehemiah's concern extended beyond the physical walls to social and economic fairness among his people.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Faith in God',
    topicCode: 'SS2-CRS-14',
    module: 'Faith and Trust',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Hebrews chapter 11, often called the "faith chapter," presents a roll call of men and women who lived by <span class="learn-keyword">faith in God</span>. This topic explores this chapter\'s examples and the benefits of faith in Christian living.\n</div>\n\n<h3 class="learn-subheading">1. The Definition and Nature of Faith</h3>\n<p class="learn-p">Hebrews 11:1 defines faith as "the substance of things hoped for, the evidence of things not seen" — a confident trust in God\'s promises and character even without physical proof. Hebrews 11:6 further teaches that "without faith it is impossible to please God," since anyone who comes to Him must believe He exists and rewards those who seek Him.</p>\n\n<h3 class="learn-subheading">2. Examples of Faith in Hebrews 11</h3>\n<p class="learn-p">The chapter lists numerous examples: Abel offered a better sacrifice by faith; Enoch walked with God and did not experience death; Noah built the ark in faith though he had never seen rain; Abraham obeyed God\'s call and offered Isaac by faith; Moses\' parents hid him by faith, and Moses himself chose to suffer with God\'s people rather than enjoy Egypt\'s pleasures; and by faith the Israelites crossed the Red Sea and the walls of Jericho fell (Hebrews 11:4-31).</p>\n\n<h3 class="learn-subheading">3. Faith Amid Suffering and Without Receiving the Promise</h3>\n<p class="learn-p">Hebrews 11:13 notes that many of these figures died without receiving what was promised, having only seen it "from afar," yet they remained faithful. Hebrews 11:32-38 also mentions others who suffered greatly — some tortured, others facing mocking, chains, and even death — yet did not renounce their faith.</p>\n\n<h3 class="learn-subheading">4. Benefits of Faith in Christian Living</h3>\n<p class="learn-p">Faith pleases God (Hebrews 11:6), provides the confidence to obey God even without full understanding, sustains believers through suffering and uncertainty, and connects believers to God\'s promises even when fulfilment is not immediately visible.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Person</th><th>Act of Faith</th></tr></thead>\n    <tbody>\n      <tr><td>Abel</td><td>Offered a better sacrifice than Cain</td></tr>\n      <tr><td>Noah</td><td>Built the ark though he had never seen rain</td></tr>\n      <tr><td>Abraham</td><td>Obeyed God\'s call and was willing to offer Isaac</td></tr>\n      <tr><td>Moses</td><td>Chose to suffer with God\'s people rather than enjoy Egypt\'s pleasures</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Be ready to quote Hebrews 11:1 and 11:6 exactly, and to list at least four to five named examples of faith from the chapter with their specific acts of faith.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Hebrews 11 presents faith as confident trust in God\'s promises, demonstrated through obedience even without seeing the full outcome — a quality essential for pleasing God and sustaining believers through life\'s uncertainties.\n</div>\n',
    questions: [
      {
        q: 'How does Hebrews 11:1 define faith?',
        o: [
          'Faith is blind acceptance without any basis',
          'Faith is the substance of things hoped for, the evidence of things not seen',
          'Faith is only relevant to religious rituals',
          'Faith is a feeling with no relation to trust in God',
        ],
        a: 1,
        e: "Hebrews 11:1 defines faith as 'the substance of things hoped for, the evidence of things not seen,' describing confident trust in God's promises even without physical proof.",
        h: 'This verse gives the classic biblical definition often quoted in this topic.',
        yr: 'SS2',
      },
      {
        q: 'According to Hebrews 11:6, what is impossible without faith?',
        o: ['Performing miracles', 'Pleasing God', 'Reading Scripture', 'Building a temple'],
        a: 1,
        e: "Hebrews 11:6 states, 'without faith it is impossible to please God,' emphasizing that faith is essential for anyone seeking a right relationship with God.",
        h: 'This verse specifically addresses what cannot happen without faith.',
        yr: 'SS2',
      },
      {
        q: 'According to Hebrews 11:6, what two things must a person believe in order to come to God?',
        o: [
          'That He exists and that He rewards those who earnestly seek Him',
          'That He is distant and uninvolved',
          'That good works alone are sufficient',
          'That faith is unnecessary for salvation',
        ],
        a: 0,
        e: 'Hebrews 11:6 explains that anyone who comes to God must believe that He exists and that He rewards those who earnestly seek Him.',
        h: 'This verse lists two specific beliefs necessary for approaching God.',
        yr: 'SS2',
      },
      {
        q: 'What act of faith is attributed to Abel in Hebrews 11?',
        o: [
          'He built an ark',
          'He offered God a better sacrifice than his brother Cain',
          'He led Israel out of Egypt',
          'He built the walls of Jerusalem',
        ],
        a: 1,
        e: 'Hebrews 11:4 records that by faith, Abel offered God a better sacrifice than Cain, through which he was commended as righteous.',
        h: "This example involves an offering made to God that was considered superior to another's.",
        yr: 'SS2',
      },
      {
        q: "What is notable about Enoch's faith, according to Hebrews 11:5?",
        o: [
          'He built the first altar',
          'He walked with God and did not experience death, being taken up by God',
          'He led the Israelites through the wilderness',
          'He was the first high priest',
        ],
        a: 1,
        e: 'Hebrews 11:5 notes that by faith, Enoch was taken up so that he did not experience death, because he had walked closely with God.',
        h: 'This example is unique in that the person did not die in the usual way.',
        yr: 'SS2',
      },
      {
        q: "What is significant about Noah's faith in building the ark?",
        o: [
          'He built the ark after seeing the flood begin',
          'He built the ark in faith, being warned about things not yet seen (rain had never fallen before)',
          'He refused to build the ark at all',
          'He built the ark for personal profit',
        ],
        a: 1,
        e: "Hebrews 11:7 highlights that Noah, being warned by God about things not yet seen, built the ark in reverent faith, trusting God's word despite having no prior experience of a worldwide flood.",
        h: 'Consider that this act of faith involved preparing for something entirely unprecedented.',
        yr: 'SS2',
      },
      {
        q: 'What choice did Moses make, according to Hebrews 11:24-26, that demonstrated his faith?',
        o: [
          "He chose to remain in Pharaoh's palace permanently",
          "He chose to suffer affliction with God's people rather than enjoy the fleeting pleasures of Egypt",
          'He chose wealth over leading Israel',
          'He refused to leave Egypt at all',
        ],
        a: 1,
        e: "Hebrews 11:24-26 records that by faith, Moses refused to be known as the son of Pharaoh's daughter, choosing instead to suffer with God's people rather than enjoy the temporary pleasures of sin in Egypt.",
        h: 'Consider the contrast between royal comfort and identifying with an oppressed people.',
        yr: 'SS2',
      },
      {
        q: 'According to Hebrews 11, what happened to the walls of Jericho by faith?',
        o: [
          'They were never actually conquered',
          'They fell after the Israelites marched around them for seven days as instructed',
          'They were rebuilt stronger than before',
          "They collapsed due to an earthquake unrelated to Israel's actions",
        ],
        a: 1,
        e: "Hebrews 11:30 records that by faith, the walls of Jericho fell after the Israelites had marched around them for seven days, following God's specific instructions.",
        h: "This example connects directly to the earlier account of Joshua's conquest of Jericho.",
        yr: 'SS2',
      },
      {
        q: 'What does Hebrews 11:13 say about many of the faith heroes listed in the chapter?',
        o: [
          'They all received the full promise during their lifetime',
          'They died without receiving what was promised, having only seen it from afar',
          'They abandoned their faith before death',
          'They were disappointed and gave up on God',
        ],
        a: 1,
        e: 'Hebrews 11:13 notes that many of these figures died in faith without receiving what had been promised, having seen and welcomed it only from a distance, yet they remained faithful.',
        h: "This verse addresses the outcome for many of the figures listed, even though they didn't see full fulfillment.",
        yr: 'SS2',
      },
      {
        q: 'What kinds of suffering does Hebrews 11:32-38 mention that some faithful people endured?',
        o: [
          'No suffering at all — only success and comfort',
          'Torture, mocking, chains, imprisonment, and even death, yet without renouncing their faith',
          'Only minor inconveniences',
          'Wealth and honour with no hardship',
        ],
        a: 1,
        e: 'Hebrews 11:32-38 describes some who were tortured, faced jeers and flogging, were chained and imprisoned, and some who were killed — yet held firmly to their faith despite severe suffering.',
        h: 'This passage describes the more difficult, painful side of living by faith.',
        yr: 'SS2',
      },
      {
        q: "What is a key benefit of faith according to Hebrews 11's overall teaching?",
        o: [
          'Faith guarantees an easy, trouble-free life',
          'Faith enables believers to please God and to persevere in obedience even without seeing the full outcome',
          'Faith is unrelated to obedience',
          'Faith is only necessary for those facing no hardship',
        ],
        a: 1,
        e: 'The overall teaching of Hebrews 11 is that faith enables believers to please God through obedient trust, sustaining them through uncertainty or hardship, even when the promised outcome is not yet visible.',
        h: "Consider the consistent thread running through all of the chapter's examples.",
        yr: 'SS2',
      },
      {
        q: "Why is Abraham's example of faith particularly significant in Hebrews 11?",
        o: [
          'Because he never had to trust God for anything difficult',
          "Because he obeyed God's call to leave his homeland and later trusted God even in the difficult command to offer Isaac",
          'Because he refused every command God gave him',
          'Because his faith had no connection to obedience',
        ],
        a: 1,
        e: "Abraham's example is emphasized extensively in Hebrews 11:8-19 because his faith was demonstrated through concrete acts of obedience — leaving his homeland for an unknown destination and later being willing to offer his promised son.",
        h: "Consider the specific acts of obedience linked to Abraham's faith in this chapter.",
        yr: 'SS2',
      },
      {
        q: "What does the phrase 'evidence of things not seen' in Hebrews 11:1 suggest about the nature of biblical faith?",
        o: [
          'Faith requires visible, tangible proof before trusting God',
          'Faith involves confident trust in realities not yet visible or fully experienced',
          'Faith is irrational and has no connection to evidence at all',
          'Faith only applies to things already seen and confirmed',
        ],
        a: 1,
        e: "This phrase suggests that biblical faith is not blind but is a confident, well-grounded trust in realities — God's promises and character — that are not yet visible or fully experienced.",
        h: 'Consider what it means to have confidence in something before it is visibly confirmed.',
        yr: 'SS2',
      },
      {
        q: "How does the example of those who suffered without seeing the promise fulfilled (Hebrews 11:13, 32-38) challenge a purely 'faith equals immediate reward' view?",
        o: [
          'It confirms that faith always results in immediate, visible reward',
          'It shows that faith can remain steadfast even when the promised outcome is delayed or not seen in this life',
          'It suggests faith is pointless if reward is delayed',
          'It implies only unfaithful people experience suffering',
        ],
        a: 1,
        e: "These passages challenge any simplistic notion that faith always results in immediate visible blessing, showing instead that genuine faith can remain steadfast even through prolonged suffering or delayed fulfilment of God's promises.",
        h: 'Consider what these examples teach about the relationship between faith and immediate reward.',
        yr: 'SS2',
      },
      {
        q: 'What connection exists between faith and obedience as illustrated throughout Hebrews 11?',
        o: [
          'Faith and obedience are entirely unrelated concepts',
          "Genuine faith in Hebrews 11 is consistently demonstrated through concrete acts of obedience to God's instructions",
          'Obedience replaces the need for faith entirely',
          'Faith is shown only through feelings, never through action',
        ],
        a: 1,
        e: "Throughout Hebrews 11, faith is consistently demonstrated not merely as an internal belief but through concrete, often costly, acts of obedience — building an ark, offering a sacrifice, leaving one's homeland, and so on.",
        h: 'Consider how each example in the chapter connects an inward faith to an outward action.',
        yr: 'SS2',
      },
      {
        q: "How might a Christian today apply the lesson of Noah's faith in building the ark 'though he had never seen rain'?",
        o: [
          'By refusing to prepare for anything not already experienced',
          "By trusting and obeying God's instructions even when circumstances have not yet visibly confirmed the need",
          "By ignoring God's warnings until visible proof appears",
          'By assuming personal experience is the only reliable guide',
        ],
        a: 1,
        e: "Noah's example encourages believers to trust and obediently act on God's word even when circumstances have not yet visibly confirmed the need, just as Noah prepared for a flood he had never personally witnessed before.",
        h: "Consider what it would mean to trust God's word ahead of visible confirmation, as Noah did.",
        yr: 'SS2',
      },
      {
        q: 'Why might Hebrews 11 be particularly encouraging to Christians facing hardship or persecution today?',
        o: [
          'Because it shows that faithful people throughout history have endured suffering while remaining faithful to God',
          'Because it promises complete freedom from any hardship for believers',
          'Because it discourages any expectation of difficulty in the Christian life',
          'Because it has no relevance to those experiencing hardship',
        ],
        a: 0,
        e: 'By showing that many faithful figures throughout history endured great hardship, suffering, and even death while remaining faithful, Hebrews 11 offers encouragement and solidarity to Christians facing their own difficulties today.',
        h: 'Consider how knowing others endured similar or greater struggles while remaining faithful might encourage someone facing hardship.',
        yr: 'SS2',
      },
      {
        q: 'Which best summarizes the overarching purpose of the examples listed throughout Hebrews 11?',
        o: [
          'To discourage readers from pursuing faith due to its difficulty',
          "To encourage readers to persevere in faith by showing a 'cloud of witnesses' who trusted God despite uncertainty or hardship",
          'To prove that faith guarantees material wealth',
          'To argue that faith is unnecessary for pleasing God',
        ],
        a: 1,
        e: "Hebrews 11, followed by Hebrews 12:1's reference to a 'great cloud of witnesses,' is designed to encourage readers to persevere in their own faith by presenting a long history of people who trusted God despite uncertainty, hardship, or delayed fulfilment of His promises.",
        h: "Consider how this chapter connects to the following chapter's call to run the race of faith with endurance.",
        yr: 'SS2',
      },
      {
        q: 'What example from Hebrews 11 involves parents hiding their child by faith?',
        o: [
          'Abraham and Sarah',
          "Moses' parents, who hid him for three months because they saw he was no ordinary child",
          'Noah and his wife',
          'Joseph and Mary',
        ],
        a: 1,
        e: "Hebrews 11:23 records that by faith, Moses' parents hid him for three months after his birth because they saw he was no ordinary child, and they were not afraid of the king's edict.",
        h: "This example connects to the earlier study of Moses' birth and rescue from Pharaoh's decree.",
        yr: 'SS2',
      },
      {
        q: "What does Hebrews 11 collectively demonstrate about faith's relationship to God's unseen promises across many generations?",
        o: [
          "That God's promises are meaningless without immediate fulfilment",
          "That faith allows believers across generations to trust God's promises even without seeing them fully realized in their own lifetime",
          'That only one generation ever needed to exercise faith',
          "That God's promises apply only to a single individual",
        ],
        a: 1,
        e: "Taken together, the examples in Hebrews 11 show that faith enables believers across many generations to trust God's promises, even when the full realization of those promises extends beyond their own lifetime.",
        h: 'Consider what the chapter, read as a whole spanning many generations, teaches about ongoing trust in unseen promises.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'True Religion and Social Justice',
    topicCode: 'SS2-CRS-15',
    module: 'Justice and Righteousness',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The Hebrew prophets <span class="learn-keyword">Amos</span>, <span class="learn-keyword">Micah</span>, and <span class="learn-keyword">Isaiah</span> spoke boldly against empty religious ritual that ignored justice and righteousness. This topic examines their teaching on true religion and social justice.\n</div>\n\n<h3 class="learn-subheading">1. Amos on Justice</h3>\n<p class="learn-p">Amos condemned Israel\'s exploitation of the poor — selling the needy for a pair of sandals, trampling the heads of the poor into the dust (Amos 2:6-7) — while they maintained elaborate religious festivals. He declared God\'s rejection of empty worship: "Let justice roll on like a river, righteousness like a never-failing stream" (Amos 5:21-24).</p>\n\n<h3 class="learn-subheading">2. Micah on True Religion</h3>\n<p class="learn-p">Micah summarized God\'s true requirement in one of Scripture\'s most quoted verses: "He has shown you, O mortal, what is good. And what does the Lord require of you? To act justly and to love mercy and to walk humbly with your God" (Micah 6:8), directly rejecting the idea that multiplying sacrifices could substitute for justice and mercy.</p>\n\n<h3 class="learn-subheading">3. Isaiah on True Fasting and Worship</h3>\n<p class="learn-p">Isaiah rebuked the people for fasting while continuing to oppress workers and quarrel with one another, declaring that the fasting God desires involves loosing the chains of injustice, sharing food with the hungry, providing shelter for the poor, and clothing the naked (Isaiah 58:3-7).</p>\n\n<h3 class="learn-subheading">4. Implications for Christians Today</h3>\n<p class="learn-p">These prophets teach that authentic faith must be expressed in just, compassionate treatment of others, not merely in religious observance. Ritual and worship divorced from justice and mercy are considered offensive to God.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Prophet</th><th>Key Teaching</th><th>Reference</th></tr></thead>\n    <tbody>\n      <tr><td>Amos</td><td>Let justice roll like a river; rejects empty festivals</td><td>Amos 5:21-24</td></tr>\n      <tr><td>Micah</td><td>Act justly, love mercy, walk humbly with God</td><td>Micah 6:8</td></tr>\n      <tr><td>Isaiah</td><td>True fasting frees the oppressed and feeds the hungry</td><td>Isaiah 58:3-7</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Memorize Micah 6:8 word-for-word — it is one of the most commonly quoted verses in WAEC/NECO CRS examinations on this theme.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Amos, Micah, and Isaiah teach that true religion cannot be separated from justice, mercy, and compassionate care for the poor and oppressed — ritual without righteousness is unacceptable to God.\n</div>\n',
    questions: [
      {
        q: 'What social injustice did Amos specifically condemn among the wealthy in Israel?',
        o: [
          'Excessive generosity toward the poor',
          'Selling the needy for a pair of sandals and trampling the poor into the dust',
          'Refusing to attend religious festivals',
          'Building too many temples',
        ],
        a: 1,
        e: "Amos condemned Israel's wealthy for exploiting the poor, even selling the needy for something as trivial as a pair of sandals and trampling the heads of the poor into the dust of the ground (Amos 2:6-7).",
        h: 'This condemnation involves treating vulnerable people as objects for minimal material gain.',
        yr: 'SS2',
      },
      {
        q: 'What famous declaration did Amos make about justice and righteousness?',
        o: [
          "'Let justice roll on like a river, righteousness like a never-failing stream'",
          "'Justice is unnecessary for true worship'",
          "'Sacrifices are more important than justice'",
          "'The poor deserve their poverty'",
        ],
        a: 0,
        e: "Amos 5:24 declares, 'Let justice roll on like a river, righteousness like a never-failing stream,' emphasizing that true worship must be accompanied by ongoing, consistent justice.",
        h: 'This memorable image compares justice to a continuously flowing river.',
        yr: 'SS2',
      },
      {
        q: "According to Amos 5:21-23, how did God respond to Israel's religious festivals and offerings while injustice persisted?",
        o: [
          'God delighted in them regardless of injustice',
          'God rejected and despised them, refusing to accept their offerings',
          'God was indifferent to them',
          'God increased their festivals as a reward',
        ],
        a: 1,
        e: "Amos 5:21-23 records God's declaration that He hated and despised Israel's religious feasts and would not accept their offerings, because their worship was disconnected from justice.",
        h: "God's response here is strongly negative, expressing rejection rather than acceptance.",
        yr: 'SS2',
      },
      {
        q: 'What three requirements does Micah 6:8 summarize as what the Lord requires of His people?',
        o: [
          'To build temples, offer sacrifices, and observe festivals',
          'To act justly, love mercy, and walk humbly with God',
          'To acquire wealth, gain power, and seek fame',
          'To avoid all forms of worship',
        ],
        a: 1,
        e: "Micah 6:8 summarizes God's requirement as: 'To act justly and to love mercy and to walk humbly with your God' — a concise statement of true religion's ethical and spiritual core.",
        h: 'This well-known verse lists three specific requirements, not ritual practices.',
        yr: 'SS2',
      },
      {
        q: 'What does Micah 6:8 imply about the sufficiency of multiplying sacrifices without justice?',
        o: [
          'Multiplying sacrifices alone is fully sufficient to please God',
          'Multiplying sacrifices cannot substitute for justice, mercy, and humility before God',
          'Sacrifices are entirely unnecessary in any circumstance',
          'God prefers sacrifices over any ethical behaviour',
        ],
        a: 1,
        e: 'The preceding verses in Micah 6:6-7 rhetorically ask whether even extravagant sacrifices could please God, and verse 8 answers that what God truly requires is justice, mercy, and humility — not merely ritual offerings.',
        h: 'Consider the rhetorical questions about sacrifices that come just before this famous verse.',
        yr: 'SS2',
      },
      {
        q: 'According to Isaiah 58, what kind of fasting did the people practice that God rejected?',
        o: [
          'Fasting that included generous care for the poor',
          'Fasting accompanied by continued oppression of workers and quarrelling',
          'No fasting at all',
          'Fasting that lasted too many days',
        ],
        a: 1,
        e: 'Isaiah 58:3-4 describes the people fasting while still exploiting their workers and engaging in quarrels and strife, showing that their religious practice was disconnected from genuine righteousness.',
        h: 'Consider what negative behaviours accompanied their outward religious observance.',
        yr: 'SS2',
      },
      {
        q: 'According to Isaiah 58:6-7, what does the fasting God truly desires involve?',
        o: [
          'Avoiding all food for extended periods with no other requirement',
          'Loosing the chains of injustice, sharing food with the hungry, and providing shelter for the poor',
          'Building larger temples',
          'Increasing the frequency of animal sacrifices',
        ],
        a: 1,
        e: 'Isaiah 58:6-7 describes the fasting God desires as one that frees the oppressed, shares food with the hungry, provides shelter for the homeless poor, and clothes the naked — actions of practical justice and mercy.',
        h: 'This passage describes concrete acts of compassion rather than mere abstention from food.',
        yr: 'SS2',
      },
      {
        q: "What common critique do Amos, Micah, and Isaiah share regarding Israel's religious practice?",
        o: [
          'That Israel practiced too little ritual worship',
          "That Israel's ritual worship was disconnected from justice, mercy, and care for the vulnerable",
          'That Israel should abandon all forms of worship entirely',
          'That ritual worship should be increased regardless of ethical behaviour',
        ],
        a: 1,
        e: "All three prophets share a common critique: Israel's religious rituals, however elaborate, were meaningless or even offensive to God because they were disconnected from justice, mercy, and compassionate treatment of the poor and oppressed.",
        h: "Consider the underlying theme that unites all three prophets' messages studied in this topic.",
        yr: 'SS2',
      },
      {
        q: 'How does the teaching of these prophets challenge a purely ritualistic view of religion?',
        o: [
          'It affirms that ritual alone, regardless of ethics, fully satisfies God',
          'It insists that authentic worship must be accompanied by ethical living, especially justice toward the vulnerable',
          'It rejects all forms of ritual worship as inherently wrong',
          'It suggests ethics and worship are entirely separate concerns',
        ],
        a: 1,
        e: 'These prophets challenge any view that reduces religion to ritual performance, insisting instead that authentic worship must be integrated with ethical living, particularly justice and mercy toward the vulnerable.',
        h: 'Consider what these prophets are ultimately arguing about the relationship between ritual and ethics.',
        yr: 'SS2',
      },
      {
        q: 'What specific vulnerable groups are mentioned as deserving care in Isaiah 58:7?',
        o: [
          'The wealthy and politically powerful',
          'The hungry, the homeless poor, and the naked',
          'Foreign kings and rulers',
          'Religious leaders exclusively',
        ],
        a: 1,
        e: 'Isaiah 58:7 specifically mentions sharing food with the hungry, providing shelter for the homeless poor, and clothing the naked as expressions of the fasting and worship God desires.',
        h: 'This verse names several specific categories of vulnerable people needing practical help.',
        yr: 'SS2',
      },
      {
        q: "How might 'walking humbly with God,' as stated in Micah 6:8, relate to acting justly and loving mercy?",
        o: [
          'It is entirely unrelated to ethical behaviour',
          'It grounds ethical action (justice and mercy) in an ongoing, humble relationship with God, rather than mere external rule-following',
          'It suggests humility replaces the need for justice',
          'It applies only to religious leaders, not ordinary believers',
        ],
        a: 1,
        e: 'Walking humbly with God provides the relational and spiritual foundation for genuinely acting justly and loving mercy, showing that true ethical living flows from an authentic, humble relationship with God rather than mere external compliance.',
        h: 'Consider how the third requirement in Micah 6:8 might relate to and support the first two.',
        yr: 'SS2',
      },
      {
        q: "What does the imagery of justice 'rolling like a river' in Amos 5:24 suggest about the nature of true justice?",
        o: [
          'That justice should be occasional and infrequent',
          'That justice should be continuous, abundant, and consistently flowing, not sporadic',
          'That justice is unimportant compared to other values',
          'That justice should only be pursued during religious festivals',
        ],
        a: 1,
        e: 'The image of a continuously flowing river suggests that true justice should be a constant, abundant, and ongoing reality in society, not an occasional or token gesture.',
        h: 'Consider what qualities a continuously flowing river has compared to something occasional or brief.',
        yr: 'SS2',
      },
      {
        q: 'How does the message of Amos, Micah, and Isaiah relate to New Testament teaching, such as James 1:27?',
        o: [
          'The New Testament rejects any connection between religion and caring for the vulnerable',
          "James 1:27 similarly defines pure religion as caring for orphans and widows, echoing the prophets' emphasis on practical compassion",
          'There is no meaningful connection between these Old Testament prophets and New Testament ethics',
          'New Testament teaching focuses exclusively on ritual practices',
        ],
        a: 1,
        e: "James 1:27 defines 'pure and genuine religion' as caring for orphans and widows in their distress and keeping oneself unstained by the world, closely echoing the Old Testament prophets' emphasis that true religion must be expressed through practical compassion and justice.",
        h: 'Consider a New Testament verse that similarly connects true religion with caring for vulnerable people.',
        yr: 'SS2',
      },
      {
        q: "Which best explains why the prophets' message on social justice remains relevant to Christians today?",
        o: [
          "Because social justice was only relevant to ancient Israel's specific historical situation",
          'Because the call to combine authentic worship with justice and compassion for the vulnerable applies across all times and cultures',
          'Because modern societies no longer contain any vulnerable or oppressed people',
          'Because Christians today are exempt from any ethical requirements',
        ],
        a: 1,
        e: "The prophetic call to combine genuine worship with justice and compassion for the vulnerable reflects a timeless principle rooted in God's unchanging character, making it as relevant to believers today as it was to ancient Israel.",
        h: 'Consider why a message about justice and worship would not be limited to one particular historical context.',
        yr: 'SS2',
      },
      {
        q: 'What practical application might a Christian congregation draw from Isaiah 58 today?',
        o: [
          'Focusing exclusively on internal religious rituals with no outward community engagement',
          'Combining spiritual disciplines like fasting and prayer with active efforts to help the hungry, homeless, and oppressed in their community',
          'Avoiding all forms of fasting or spiritual discipline',
          'Limiting compassion only to fellow church members',
        ],
        a: 1,
        e: "Applying Isaiah 58's teaching, a Christian congregation might combine spiritual disciplines such as fasting and prayer with active practical efforts to help the hungry, homeless, and oppressed within their wider community.",
        h: "Consider how a modern congregation might integrate this passage's teaching with actual practice.",
        yr: 'SS2',
      },
      {
        q: 'What warning do these prophets collectively offer to religious communities that emphasize ritual over ethics?',
        o: [
          'That ritual observance guarantees divine favour regardless of ethical conduct',
          'That God may reject or find offensive religious observance that is disconnected from justice and compassion',
          'That ethics is entirely irrelevant to true religion',
          'That religious rituals should be abolished altogether',
        ],
        a: 1,
        e: 'Amos, Micah, and Isaiah collectively warn that God may actively reject religious observance — however elaborate — when it is disconnected from justice, mercy, and compassionate care for the vulnerable.',
        h: "Consider the strong language used, particularly in Amos, about God's response to disconnected ritual.",
        yr: 'SS2',
      },
      {
        q: 'How does Micah 6:8 balance the personal (humility before God) and the social (justice and mercy) dimensions of true religion?',
        o: [
          'It focuses exclusively on personal piety with no social dimension',
          'It integrates personal humility before God with active social concern for justice and mercy, showing both are essential',
          'It focuses exclusively on social action with no spiritual dimension',
          'It suggests personal and social religion are entirely separate matters',
        ],
        a: 1,
        e: 'Micah 6:8 holds together both dimensions — humility in relationship with God and active concern for justice and mercy toward others — showing that authentic religion integrates the personal/spiritual and the social/ethical rather than separating them.',
        h: 'Consider how the three requirements in this verse combine both inward and outward dimensions of faith.',
        yr: 'SS2',
      },
      {
        q: 'Which best summarizes the central theological message shared by Amos, Micah, and Isaiah on true religion and social justice?',
        o: [
          "Ritual observance alone, regardless of ethical conduct, fully satisfies God's requirements",
          'Authentic religion must combine genuine worship of God with practical justice, mercy, and compassion toward the vulnerable',
          'Social justice is a purely secular concern unrelated to true worship',
          'God is primarily concerned with the frequency of religious festivals',
        ],
        a: 1,
        e: 'The central shared message of these three prophets is that authentic religion cannot be reduced to ritual performance; it must be expressed through practical justice, mercy, and compassionate care for the vulnerable, integrated with genuine worship of God.',
        h: 'Consider the overarching theological point that unites the specific teachings of all three prophets studied.',
        yr: 'SS2',
      },
      {
        q: "What rhetorical question does Micah 6:6-7 ask regarding the sufficiency of sacrifices before stating God's true requirement?",
        o: [
          'Whether even thousands of rams or rivers of oil could please the Lord',
          'Whether God requires no worship at all',
          'Whether sacrifices should be entirely abolished',
          'Whether only silver and gold please God',
        ],
        a: 0,
        e: "Micah 6:6-7 rhetorically asks whether the Lord would be pleased with thousands of rams or ten thousand rivers of oil, or even one's firstborn, before verse 8 answers with what God truly requires: justice, mercy, and humility.",
        h: "Consider the extreme examples of sacrifice mentioned just before Micah 6:8's famous answer.",
        yr: 'SS2',
      },
      {
        q: "Which prophet particularly emphasized God's rejection of insincere fasting practiced alongside oppression of workers?",
        o: ['Amos', 'Isaiah', 'Micah', 'Jeremiah'],
        a: 1,
        e: "Isaiah, in chapter 58, particularly emphasized God's rejection of fasting practiced alongside continued oppression of workers and quarrelling, calling instead for fasting expressed through justice and compassion.",
        h: "This prophet's chapter 58 specifically addresses the problem of insincere fasting.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'The Baptism and Temptation of Jesus Christ',
    topicCode: 'SS2-CRS-16',
    module: 'Life of Christ',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The <span class="learn-keyword">baptism</span> and <span class="learn-keyword">temptation</span> of Jesus mark the beginning of His public ministry. This topic examines both events and their significance and lessons for Christians.\n</div>\n\n<h3 class="learn-subheading">1. The Baptism of Jesus</h3>\n<p class="learn-p">Jesus came to John the Baptist at the River Jordan to be baptized. Though John initially objected, feeling unworthy to baptize Jesus, Jesus insisted, saying it was proper "to fulfil all righteousness" (Matthew 3:13-15). As Jesus came up from the water, the heavens opened, the Holy Spirit descended on Him like a dove, and a voice from heaven declared, "This is my beloved Son, in whom I am well pleased" (Matthew 3:16-17).</p>\n\n<h3 class="learn-subheading">2. The Significance of Jesus\' Baptism</h3>\n<p class="learn-p">Jesus\' baptism identified Him with sinful humanity though He Himself was without sin, publicly launched His ministry, and revealed the Trinity together — the Son being baptized, the Spirit descending, and the Father\'s voice speaking from heaven.</p>\n\n<h3 class="learn-subheading">3. The Temptation of Jesus in the Wilderness</h3>\n<p class="learn-p">Immediately after His baptism, Jesus was led by the Spirit into the wilderness, where He fasted forty days and nights before being tempted by the devil in three specific ways: to turn stones into bread to satisfy His hunger, to throw Himself from the temple to test God\'s protection, and to worship Satan in exchange for all the kingdoms of the world (Matthew 4:1-11).</p>\n\n<h3 class="learn-subheading">4. How Jesus Overcame Temptation</h3>\n<p class="learn-p">In each temptation, Jesus responded by quoting Scripture from Deuteronomy, demonstrating the importance of God\'s word in resisting temptation: "Man shall not live on bread alone, but on every word that comes from the mouth of God" (Matthew 4:4); "Do not put the Lord your God to the test" (4:7); "Worship the Lord your God, and serve him only" (4:10).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Temptation</th><th>Jesus\' Response (Scripture Quoted)</th></tr></thead>\n    <tbody>\n      <tr><td>Turn stones to bread</td><td>"Man shall not live on bread alone, but on every word that comes from the mouth of God" (Deuteronomy 8:3)</td></tr>\n      <tr><td>Throw Himself from the temple</td><td>"Do not put the Lord your God to the test" (Deuteronomy 6:16)</td></tr>\n      <tr><td>Worship Satan for the kingdoms of the world</td><td>"Worship the Lord your God, and serve him only" (Deuteronomy 6:13)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Learn all three temptations in order along with Jesus\' exact scriptural response to each — this sequence is a very common exam question format (list-and-match).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Jesus\' baptism publicly launched His ministry and revealed the Trinity, while His temptation in the wilderness modelled how to resist Satan\'s schemes through knowledge and application of Scripture.\n</div>\n',
    questions: [
      {
        q: 'Who baptized Jesus at the River Jordan?',
        o: ['Peter', 'John the Baptist', 'Andrew', 'James'],
        a: 1,
        e: 'Jesus came to John the Baptist at the River Jordan to be baptized, marking the beginning of His public ministry (Matthew 3:13).',
        h: 'This person had been preaching a baptism of repentance before Jesus arrived.',
        yr: 'SS2',
      },
      {
        q: 'Why did John the Baptist initially object to baptizing Jesus?',
        o: [
          'He did not recognize Jesus',
          'He felt unworthy, believing Jesus should baptize him instead',
          'He refused to baptize anyone that day',
          'He demanded payment first',
        ],
        a: 1,
        e: "John objected, saying he needed to be baptized by Jesus, not the other way around, recognizing Jesus' superior status (Matthew 3:14).",
        h: "John's objection concerned who should rightly baptize whom.",
        yr: 'SS2',
      },
      {
        q: "How did Jesus respond to John's objection about baptizing Him?",
        o: [
          'He agreed and asked John to be baptized instead',
          'He insisted it was proper to fulfil all righteousness',
          'He left without being baptized',
          'He rebuked John harshly',
        ],
        a: 1,
        e: 'Jesus told John to allow it for now, because it was proper for them to fulfil all righteousness in this way (Matthew 3:15), and John consented.',
        h: 'Jesus explained a specific theological reason for proceeding with the baptism.',
        yr: 'SS2',
      },
      {
        q: 'What happened immediately after Jesus came up out of the water at His baptism?',
        o: [
          'Nothing unusual occurred',
          'The heavens opened, the Spirit descended like a dove, and a voice from heaven spoke',
          'John immediately left the area',
          'A great storm arose',
        ],
        a: 1,
        e: "As Jesus came up from the water, the heavens were opened, the Spirit of God descended like a dove and lighted on Him, and a voice from heaven declared Him as God's beloved Son (Matthew 3:16-17).",
        h: 'Three distinct supernatural events occurred together at this moment.',
        yr: 'SS2',
      },
      {
        q: 'What did the voice from heaven declare about Jesus at His baptism?',
        o: [
          "'This is a prophet like others before him'",
          "'This is my beloved Son, in whom I am well pleased'",
          "'This man must prove himself before I accept him'",
          "'This is a sinner in need of repentance'",
        ],
        a: 1,
        e: "The voice from heaven declared, 'This is my beloved Son, in whom I am well pleased,' affirming Jesus' identity and God's approval at the very start of His ministry (Matthew 3:17).",
        h: "This declaration affirms both relationship ('Son') and divine approval.",
        yr: 'SS2',
      },
      {
        q: "What is theologically significant about all three persons of the Trinity appearing at Jesus' baptism?",
        o: [
          'It suggests there are actually three separate gods',
          'It provides a clear scriptural picture of Father, Son, and Holy Spirit present and active together',
          'It has no theological significance',
          'It proves Jesus was not truly divine',
        ],
        a: 1,
        e: "Jesus' baptism is often cited as one of the clearest scriptural pictures of the Trinity — the Son being baptized, the Spirit descending as a dove, and the Father's voice speaking from heaven, all present together in one event.",
        h: 'Consider how each of the three persons of the Godhead is represented in this single event.',
        yr: 'SS2',
      },
      {
        q: 'Where did the Spirit lead Jesus immediately after His baptism?',
        o: [
          'Into the temple in Jerusalem',
          'Into the wilderness to be tempted by the devil',
          'Back to Nazareth',
          'To the Sea of Galilee',
        ],
        a: 1,
        e: 'Immediately after His baptism, Jesus was led by the Spirit into the wilderness, where He fasted forty days and nights before being tempted by the devil (Matthew 4:1-2).',
        h: 'This location was uninhabited and associated with testing and solitude.',
        yr: 'SS2',
      },
      {
        q: 'How long did Jesus fast in the wilderness before being tempted?',
        o: [
          'Seven days and nights',
          'Forty days and nights',
          'One hundred days',
          'Three days and nights',
        ],
        a: 1,
        e: 'Jesus fasted for forty days and forty nights in the wilderness, after which He was hungry and the devil came to tempt Him (Matthew 4:2).',
        h: 'This specific duration echoes other significant forty-day periods in Scripture.',
        yr: 'SS2',
      },
      {
        q: 'What was the first temptation the devil presented to Jesus?',
        o: [
          "To worship him in exchange for the world's kingdoms",
          'To turn stones into bread to satisfy His hunger',
          'To throw Himself from the temple',
          'To turn water into wine',
        ],
        a: 1,
        e: "The devil's first temptation challenged Jesus, who was hungry after His long fast, to turn stones into bread to prove His identity and satisfy His physical need (Matthew 4:3).",
        h: "This temptation directly targeted Jesus' physical hunger after the long fast.",
        yr: 'SS2',
      },
      {
        q: 'How did Jesus respond to the temptation to turn stones into bread?',
        o: [
          'He immediately performed the miracle',
          "He quoted Scripture: 'Man shall not live on bread alone, but on every word that comes from the mouth of God'",
          'He ignored the devil completely without responding',
          'He asked the devil for proof of his power',
        ],
        a: 1,
        e: "Jesus responded by quoting Deuteronomy 8:3, affirming that human life depends not on physical bread alone but on every word that comes from God's mouth (Matthew 4:4).",
        h: 'His response emphasized spiritual sustenance over merely satisfying physical hunger.',
        yr: 'SS2',
      },
      {
        q: 'What was the second temptation, and where did it take place?',
        o: [
          'To turn water into wine, at a wedding',
          "To throw Himself down from the highest point of the temple, testing God's protection",
          'To feed a crowd with a small amount of food',
          'To walk on water across the Sea of Galilee',
        ],
        a: 1,
        e: 'The devil took Jesus to the highest point of the temple in Jerusalem and tempted Him to throw Himself down, citing Scripture out of context to suggest angels would protect Him (Matthew 4:5-6).',
        h: 'This temptation involved a high, public location associated with worship.',
        yr: 'SS2',
      },
      {
        q: 'How did Jesus respond to the temptation to throw Himself from the temple?',
        o: [
          'He performed the act to prove His power',
          "He responded, 'Do not put the Lord your God to the test'",
          'He asked the devil to leave without any response',
          'He agreed the temptation was reasonable',
        ],
        a: 1,
        e: "Jesus responded by quoting Deuteronomy 6:16, 'Do not put the Lord your God to the test,' refusing to force God's hand through a reckless, presumptuous act (Matthew 4:7).",
        h: 'His response addressed the danger of testing God rather than trusting Him.',
        yr: 'SS2',
      },
      {
        q: 'What was the third and final temptation the devil offered Jesus?',
        o: [
          'To turn stones into bread again',
          'To worship the devil in exchange for all the kingdoms of the world and their splendour',
          'To fast for another forty days',
          'To perform a miracle before a crowd',
        ],
        a: 1,
        e: 'The devil showed Jesus all the kingdoms of the world and their splendour, offering them to Him in exchange for worship (Matthew 4:8-9).',
        h: 'This final temptation involved worldly power and glory offered in exchange for worship.',
        yr: 'SS2',
      },
      {
        q: 'How did Jesus respond to the temptation to worship Satan for worldly power?',
        o: [
          'He accepted the offer to gain influence quickly',
          "He commanded, 'Away from me, Satan! Worship the Lord your God, and serve him only'",
          'He negotiated for a smaller portion of the kingdoms',
          'He remained silent without responding',
        ],
        a: 1,
        e: 'Jesus firmly rejected this temptation, quoting Deuteronomy 6:13, and commanded Satan to leave, affirming that worship and service belong to God alone (Matthew 4:10).',
        h: 'His response was the most direct and forceful of the three, involving a command to leave.',
        yr: 'SS2',
      },
      {
        q: 'What happened after Jesus successfully resisted all three temptations?',
        o: [
          'The devil continued tempting Him indefinitely',
          'The devil left Him, and angels came and attended Him',
          'Jesus was immediately arrested',
          'Nothing further is recorded',
        ],
        a: 1,
        e: 'After Jesus resisted all three temptations, the devil left Him, and angels came and ministered to Him (Matthew 4:11), providing care after His long ordeal.',
        h: 'Consider what supernatural care Jesus received immediately following His victory over temptation.',
        yr: 'SS2',
      },
      {
        q: 'What common method did Jesus use to resist each of the three temptations?',
        o: [
          'Ignoring the devil completely without engaging',
          'Quoting specific Scripture from the Book of Deuteronomy',
          'Performing miracles to prove His power',
          'Arguing based on His own personal authority alone',
        ],
        a: 1,
        e: 'In each of the three temptations, Jesus responded by quoting specific verses from Deuteronomy, demonstrating the power and importance of knowing and applying Scripture when facing temptation (Matthew 4:4, 7, 10).',
        h: 'Consider the consistent pattern in how Jesus responded to all three temptations.',
        yr: 'SS2',
      },
      {
        q: "What does the timing of Jesus' temptation — immediately after His baptism and before His ministry — suggest?",
        o: [
          'That spiritual highs are always immediately followed by significant testing',
          'That temptation and testing can occur even right after significant spiritual affirmation, requiring ongoing vigilance',
          'That temptation only occurs to those who are spiritually weak',
          'That baptism removes any possibility of future temptation',
        ],
        a: 1,
        e: 'The fact that Jesus faced intense temptation immediately after the affirming experience of His baptism suggests that spiritual highs do not exempt believers from subsequent testing, teaching the importance of continued vigilance.',
        h: 'Consider the significance of the sequence — affirmation at baptism followed immediately by severe testing.',
        yr: 'SS2',
      },
      {
        q: "How does Jesus' example of using Scripture to resist temptation apply to Christians today?",
        o: [
          'Scripture is irrelevant to resisting temptation today',
          'Believers can similarly use knowledge and application of Scripture as a powerful tool for resisting temptation',
          'Only religious leaders can use Scripture this way',
          'Resisting temptation requires no spiritual preparation',
        ],
        a: 1,
        e: "Jesus' consistent use of specific, relevant Scripture to counter each temptation models for Christians today the importance of knowing and applying God's word as a practical, powerful tool for resisting temptation in their own lives.",
        h: "Consider what practical lesson believers can draw from Jesus' consistent method of response.",
        yr: 'SS2',
      },
      {
        q: "Why might the devil's temptations be understood as targeting legitimate needs or desires in illegitimate ways?",
        o: [
          'Because hunger, safety, and power are inherently sinful desires',
          'Because each temptation twisted a legitimate need (food, security, authority) into an illegitimate shortcut that bypassed trust in and obedience to God',
          'Because the temptations had nothing to do with genuine human needs',
          'Because Jesus had no real needs being tempted',
        ],
        a: 1,
        e: "Each temptation targeted something not inherently wrong — the need for food, desire for security, or hope for authority — but offered it through an illegitimate shortcut that would have required Jesus to act independently of, or in defiance of, God's proper way.",
        h: 'Consider what each temptation was fundamentally offering, and what made accepting it wrong.',
        yr: 'SS2',
      },
      {
        q: "Which best summarizes the combined significance of Jesus' baptism and temptation for understanding His ministry?",
        o: [
          'Both events were unrelated incidents with no bearing on His ministry',
          "Together, they reveal Jesus' identity as God's beloved Son and His resolve to remain faithful and obedient to God even under severe testing, launching His public ministry",
          'The baptism was significant but the temptation had no lasting importance',
          "Both events primarily demonstrate John the Baptist's authority over Jesus",
        ],
        a: 1,
        e: "Together, Jesus' baptism (revealing His identity and divine approval) and His temptation (demonstrating His resolve to remain faithful to God even under severe testing) form a foundational introduction to His public ministry, establishing both who He is and how He would remain faithful throughout.",
        h: "Consider how these two connected events, taken together, set the stage for the rest of Jesus' ministry.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Call of the Disciples',
    topicCode: 'SS2-CRS-17',
    module: 'Life of Christ',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  At the start of His public ministry, Jesus called ordinary men to leave their occupations and follow Him as His <span class="learn-keyword">disciples</span>. This topic examines the call of the twelve, the reasons behind their selection, and the lessons for Christian discipleship.\n</div>\n\n<h3 class="learn-subheading">1. The Calling of the First Disciples</h3>\n<p class="learn-p">Walking along the Sea of Galilee, Jesus called Simon Peter and his brother Andrew, who were casting a net into the lake, saying, "Come, follow me, and I will send you out to fish for people" (Matthew 4:18-19). They immediately left their nets and followed Him. Jesus then called James and John, sons of Zebedee, who were mending their nets with their father; they too left immediately, leaving their father and the boat behind (Matthew 4:21-22).</p>\n\n<h3 class="learn-subheading">2. The Call of Matthew (Levi)</h3>\n<p class="learn-p">Jesus also called Matthew, a tax collector — a profession despised by many Jews for its association with Roman taxation and corruption — while he sat at his tax booth. Matthew immediately got up and followed Jesus, later hosting a dinner where Jesus ate with "tax collectors and sinners" (Matthew 9:9-13).</p>\n\n<h3 class="learn-subheading">3. The Twelve Disciples</h3>\n<p class="learn-p">After a night of prayer, Jesus chose twelve from among His followers to be His closest disciples, later called apostles: Simon (Peter), Andrew, James, John, Philip, Bartholomew, Thomas, Matthew, James son of Alphaeus, Thaddaeus, Simon the Zealot, and Judas Iscariot (Luke 6:12-16).</p>\n\n<h3 class="learn-subheading">4. Reasons for Their Call and Lessons for Christians</h3>\n<p class="learn-p">Jesus chose ordinary, often unremarkable people — fishermen, a tax collector — rather than the religiously elite, showing that God can use anyone willing to respond in obedience. Their call teaches believers that discipleship requires immediate, wholehearted response and willingness to leave behind former priorities to follow Christ.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Disciple(s)</th><th>Former Occupation</th><th>Response to the Call</th></tr></thead>\n    <tbody>\n      <tr><td>Simon Peter and Andrew</td><td>Fishermen</td><td>Left their nets immediately</td></tr>\n      <tr><td>James and John</td><td>Fishermen</td><td>Left the boat and their father immediately</td></tr>\n      <tr><td>Matthew (Levi)</td><td>Tax collector</td><td>Left his tax booth immediately</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Be able to list all twelve disciples by name (Luke 6:12-16) and know each of the first four disciples\' original occupation and family relationships.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Jesus called ordinary people from diverse and even socially despised occupations to be His disciples, and their immediate, wholehearted response models the kind of committed discipleship Jesus still calls believers to today.\n</div>\n',
    questions: [
      {
        q: 'What were Simon Peter and Andrew doing when Jesus first called them?',
        o: [
          'Mending fishing nets',
          'Casting a net into the Sea of Galilee',
          'Collecting taxes',
          'Praying in the synagogue',
        ],
        a: 1,
        e: 'Jesus found Simon Peter and his brother Andrew casting a net into the lake, since they were fishermen, and called them to follow Him (Matthew 4:18).',
        h: 'Their specific activity involved actively fishing, not repairing equipment.',
        yr: 'SS2',
      },
      {
        q: 'What memorable phrase did Jesus use when calling Peter and Andrew?',
        o: [
          "'Come, follow me, and I will send you out to fish for people'",
          "'Leave your nets and become priests'",
          "'Follow me to become wealthy'",
          "'Come and build me a temple'",
        ],
        a: 0,
        e: "Jesus called them with the words, 'Come, follow me, and I will send you out to fish for people,' using imagery connected to their profession (Matthew 4:19).",
        h: 'This phrase creatively connects their old occupation to their new calling.',
        yr: 'SS2',
      },
      {
        q: 'What were James and John doing with their father when Jesus called them?',
        o: [
          'Selling fish at the market',
          'Mending their fishing nets in a boat',
          'Collecting taxes',
          'Studying Scripture',
        ],
        a: 1,
        e: 'James and John, sons of Zebedee, were in a boat with their father, mending their nets, when Jesus called them to follow Him (Matthew 4:21).',
        h: 'This activity involved maintaining their fishing equipment, alongside a family member.',
        yr: 'SS2',
      },
      {
        q: 'What did James and John leave behind to follow Jesus?',
        o: [
          'Only their nets',
          'The boat and their father',
          'Their entire family fortune',
          'Nothing significant',
        ],
        a: 1,
        e: 'James and John immediately left the boat and their father Zebedee to follow Jesus, showing a decisive and costly response to His call (Matthew 4:22).',
        h: 'Their departure involved leaving both possessions and a close family relationship behind.',
        yr: 'SS2',
      },
      {
        q: "What was Matthew's (Levi's) occupation before Jesus called him?",
        o: ['Fisherman', 'Tax collector', 'Shepherd', 'Priest'],
        a: 1,
        e: 'Matthew, also called Levi, worked as a tax collector, sitting at his tax booth, when Jesus called him to follow Him (Matthew 9:9).',
        h: 'This profession involved collecting money on behalf of the Roman authorities.',
        yr: 'SS2',
      },
      {
        q: 'Why was the occupation of tax collector particularly despised among many Jews of that time?',
        o: [
          'Tax collectors were considered too generous',
          'Tax collectors were associated with Roman taxation and often engaged in corrupt overcharging',
          'Tax collectors had no contact with the general public',
          'Tax collectors were exempt from all social interactions',
        ],
        a: 1,
        e: 'Tax collectors worked on behalf of the occupying Roman authorities and were often viewed as traitors and extortioners, since many overcharged people for personal profit, making the profession widely despised among Jews.',
        h: 'Consider the political and moral associations tied to working for the occupying power.',
        yr: 'SS2',
      },
      {
        q: 'How did Matthew respond immediately after Jesus called him?',
        o: [
          'He refused and continued his work',
          'He got up and followed Jesus immediately',
          'He asked for time to think it over',
          'He asked Jesus for payment first',
        ],
        a: 1,
        e: "Matthew got up immediately and followed Jesus without hesitation, later hosting a dinner in his home in Jesus' honour (Matthew 9:9-10).",
        h: 'His response mirrors the immediate obedience shown by the fishermen disciples.',
        yr: 'SS2',
      },
      {
        q: "Who did Jesus dine with at Matthew's house, prompting criticism from the Pharisees?",
        o: [
          'Only religious leaders',
          'Tax collectors and sinners',
          'Roman soldiers exclusively',
          'Foreign dignitaries',
        ],
        a: 1,
        e: "At Matthew's house, Jesus ate with many tax collectors and sinners, which drew criticism from the Pharisees, who questioned why He associated with such people (Matthew 9:10-11).",
        h: 'This gathering included people considered social and religious outcasts by many.',
        yr: 'SS2',
      },
      {
        q: "How did Jesus respond to the Pharisees' criticism about eating with tax collectors and sinners?",
        o: [
          'He apologized and left immediately',
          'He explained that He came to call sinners, not the righteous, comparing Himself to a doctor who is needed by the sick',
          'He agreed the criticism was fair',
          'He refused to answer them',
        ],
        a: 1,
        e: "Jesus responded, 'It is not the healthy who need a doctor, but the sick... For I have not come to call the righteous, but sinners' (Matthew 9:12-13), explaining His mission to reach those in spiritual need.",
        h: 'His response used an analogy comparing physical illness to spiritual need.',
        yr: 'SS2',
      },
      {
        q: 'What did Jesus do before selecting the twelve disciples, according to Luke 6:12-13?',
        o: [
          'He consulted with the Pharisees',
          'He spent the night praying to God before choosing them the next day',
          'He held a public vote',
          'He selected them without any preparation',
        ],
        a: 1,
        e: 'Luke 6:12-13 records that Jesus spent an entire night praying to God before calling His disciples and choosing twelve of them to be apostles.',
        h: 'This important decision was preceded by extended, dedicated prayer.',
        yr: 'SS2',
      },
      {
        q: 'How many disciples did Jesus specifically select to be His closest followers, later called apostles?',
        o: ['Seven', 'Ten', 'Twelve', 'Twenty'],
        a: 2,
        e: 'Jesus chose twelve disciples from among His larger group of followers to be His closest companions, later referred to as apostles (Luke 6:13).',
        h: 'This specific number is traditionally associated with the twelve tribes of Israel.',
        yr: 'SS2',
      },
      {
        q: 'Which of these was NOT one of the twelve disciples listed in Luke 6:12-16?',
        o: ['Bartholomew', 'Thomas', 'John the Baptist', 'Thaddaeus'],
        a: 2,
        e: 'John the Baptist was not one of the twelve disciples; he was a separate figure who baptized Jesus and prepared the way for His ministry. The twelve listed in Luke 6:12-16 include Bartholomew, Thomas, and Thaddaeus among others.',
        h: "This person's ministry of baptism and preparation preceded Jesus' own ministry rather than being part of the twelve.",
        yr: 'SS2',
      },
      {
        q: 'What common characteristic did most of the disciples Jesus called share, in terms of social status?',
        o: [
          'They were mostly wealthy religious leaders',
          'They were largely ordinary, working-class people, such as fishermen, rather than the religious elite',
          'They were foreign rulers',
          'They were all former priests',
        ],
        a: 1,
        e: "Most of Jesus' disciples came from ordinary, working-class backgrounds — fishermen and even a despised tax collector — rather than from among the religious elite of the time, such as Pharisees or Sadducees.",
        h: 'Consider the general social and occupational background shared by most of the disciples called.',
        yr: 'SS2',
      },
      {
        q: "What does Jesus' choice of ordinary people as disciples suggest about how God calls people to serve Him?",
        o: [
          'God only calls the wealthy and highly educated',
          'God can call and use ordinary people, regardless of their social status or background, for significant purposes',
          "Social status determines one's calling from God",
          'Only religious professionals can be called by God',
        ],
        a: 1,
        e: "By choosing fishermen and even a despised tax collector rather than the religious elite, Jesus demonstrated that God's calling is not limited by social status, education, or occupation, but is extended to ordinary, willing people.",
        h: "Consider what message is conveyed by Jesus' specific choice of these particular individuals.",
        yr: 'SS2',
      },
      {
        q: "What does the disciples' immediate response to Jesus' call teach about the nature of discipleship?",
        o: [
          'Discipleship should involve lengthy delay and hesitation',
          'Genuine discipleship often requires prompt, wholehearted response, even at significant personal cost',
          'Discipleship requires no real sacrifice or change',
          'Immediate response indicates a lack of careful thought',
        ],
        a: 1,
        e: "The disciples' immediate, unhesitating response — leaving nets, boats, family, and even a lucrative tax booth — teaches that genuine discipleship often calls for prompt, wholehearted commitment, even when it involves real personal cost.",
        h: 'Consider what the swift, decisive nature of their response suggests about true discipleship.',
        yr: 'SS2',
      },
      {
        q: "Why is Jesus' willingness to call and dine with tax collectors and sinners significant for understanding His mission?",
        o: [
          'It shows Jesus approved of sinful behaviour',
          "It shows Jesus' mission was specifically to reach those considered outcasts and in spiritual need, not only the socially respectable",
          'It shows Jesus avoided association with any religious leaders',
          'It has no particular significance for His mission',
        ],
        a: 1,
        e: "Jesus' willingness to associate closely with tax collectors and sinners reveals that His mission specifically targeted those considered spiritually needy and socially marginalized, rather than being limited to the already religiously respectable.",
        h: 'Consider what this association reveals about who Jesus came to reach, based on His own explanation to the Pharisees.',
        yr: 'SS2',
      },
      {
        q: "What can be inferred about Jesus' approach to major decisions from His night of prayer before choosing the twelve?",
        o: [
          'Jesus made major decisions impulsively without preparation',
          "Jesus modelled seeking God's guidance through sustained prayer before making significant decisions",
          'Prayer was unnecessary for someone with divine authority',
          'Jesus only prayed for minor, unimportant matters',
        ],
        a: 1,
        e: "Jesus' choice to spend an entire night in prayer before selecting His twelve closest disciples models the importance of seeking God's guidance through sustained prayer, even for someone with full divine authority, before making significant decisions.",
        h: 'Consider why Jesus, despite His divine nature, still devoted extended time to prayer before this decision.',
        yr: 'SS2',
      },
      {
        q: "How does the calling of the disciples relate to the broader theme of Jesus' ministry beginning after His baptism and temptation?",
        o: [
          'The calling of disciples has no connection to the earlier events of His ministry',
          'It marks the practical next step of Jesus building a team to help extend His ministry after being affirmed at His baptism and proven faithful through temptation',
          'It occurred long before His baptism and temptation',
          'It replaced the need for His baptism and temptation',
        ],
        a: 1,
        e: "Following His baptism (which affirmed His identity) and temptation (which proved His faithfulness), Jesus' calling of the disciples represents the next practical step in launching His public ministry — building a team of followers to learn from and eventually extend His work.",
        h: "Consider how this event fits into the broader sequence of Jesus' ministry as studied in previous topics.",
        yr: 'SS2',
      },
      {
        q: 'Which best summarizes the enduring lesson of the call of the disciples for Christians today?',
        o: [
          'Only specially qualified religious professionals can be called to serve Christ',
          'Jesus calls ordinary people from all walks of life to follow Him wholeheartedly, often requiring them to leave former priorities behind',
          'Discipleship requires no personal cost or change of life',
          'The call to discipleship ended with the original twelve disciples',
        ],
        a: 1,
        e: 'The call of the disciples teaches that Jesus continues to call ordinary people from various backgrounds to follow Him wholeheartedly, often requiring them to reprioritize their lives in response — a call still extended to believers today.',
        h: 'Consider what timeless principle about following Jesus this account teaches beyond its original historical context.',
        yr: 'SS2',
      },
      {
        q: "Which disciple is also referred to as 'the Zealot' among the twelve listed in Luke 6:12-16?",
        o: ['Simon the Zealot', 'Thomas', 'Philip', 'Matthew'],
        a: 0,
        e: "Among the twelve disciples listed in Luke 6:15, Simon is specifically distinguished with the title 'the Zealot,' likely reflecting a background in a nationalist political movement before following Jesus.",
        h: 'This title distinguishes this disciple from the other Simon (Peter) among the twelve.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'The Mission of the Disciples (Luke 10:1-24)',
    topicCode: 'SS2-CRS-18',
    module: 'Life of Christ',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  In Luke 10:1-24, Jesus sends out seventy(-two) of His followers on a specific mission, expanding His ministry reach. This topic examines their commissioning, instructions, experiences, and the lessons drawn from their mission.\n</div>\n\n<h3 class="learn-subheading">1. The Sending of the Seventy</h3>\n<p class="learn-p">Jesus appointed seventy (some manuscripts say seventy-two) others and sent them in pairs ahead of Him into every town and place He intended to visit, to prepare the way for His coming (Luke 10:1). He noted, "The harvest is plentiful, but the workers are few. Ask the Lord of the harvest, therefore, to send out workers into his harvest field" (Luke 10:2).</p>\n\n<h3 class="learn-subheading">2. Jesus\' Instructions for the Mission</h3>\n<p class="learn-p">Jesus instructed them to travel light — without purse, bag, or sandals — trusting God\'s provision, to greet no one along the road (emphasizing urgency), to stay in whatever home welcomed them, to eat what was offered, to heal the sick, and to proclaim, "The kingdom of God has come near to you" (Luke 10:3-9). If a town rejected them, they were to wipe the dust off their feet as a testimony against it (Luke 10:10-11).</p>\n\n<h3 class="learn-subheading">3. The Disciples\' Report and Jesus\' Response</h3>\n<p class="learn-p">The seventy returned with joy, reporting, "Lord, even the demons submit to us in your name" (Luke 10:17). Jesus affirmed their authority over evil but redirected their joy: "Do not rejoice that the spirits submit to you, but rejoice that your names are written in heaven" (Luke 10:19-20).</p>\n\n<h3 class="learn-subheading">4. Jesus\' Rejoicing and Teaching</h3>\n<p class="learn-p">Jesus rejoiced in the Holy Spirit, thanking the Father for revealing spiritual truths to "little children" rather than "the wise and learned" (Luke 10:21). He then privately told the disciples they were blessed to see and hear what many prophets and kings had longed to see but did not (Luke 10:23-24).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Instruction</th><th>Purpose</th></tr></thead>\n    <tbody>\n      <tr><td>Travel without purse, bag, or sandals</td><td>Trust God\'s provision; urgency of the task</td></tr>\n      <tr><td>Stay in one house; eat what is given</td><td>Focus on the mission, not comfort or convenience</td></tr>\n      <tr><td>Heal the sick and proclaim the kingdom</td><td>Demonstrate and announce God\'s reign</td></tr>\n      <tr><td>Wipe dust off feet if rejected</td><td>A symbolic testimony against unrepentant towns</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Remember Jesus\' key redirection of their joy in Luke 10:20 — rejoicing should be rooted in salvation (\'names written in heaven\'), not merely in spiritual power over demons.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> The mission of the seventy shows Jesus expanding His ministry through trained followers, teaching principles of dependent faith, urgency, and rightly-placed joy that remain relevant for Christian mission today.\n</div>\n',
    questions: [
      {
        q: 'How many people did Jesus send out on the mission described in Luke 10:1-24?',
        o: ['Twelve', 'Seventy (or seventy-two, depending on manuscript)', 'One hundred', 'Three'],
        a: 1,
        e: 'Jesus appointed seventy (in some manuscripts, seventy-two) others and sent them out in pairs ahead of Him into every town and place He intended to visit (Luke 10:1).',
        h: 'This number is distinct from the twelve disciples/apostles, being a larger group.',
        yr: 'SS2',
      },
      {
        q: 'How did Jesus send out these followers — individually or in groups?',
        o: ['Alone, one at a time', 'In pairs', 'In groups of ten', 'In one large group together'],
        a: 1,
        e: 'Jesus sent them out two by two, ahead of Him into every town and place He intended to go (Luke 10:1).',
        h: 'This method involved partnering people rather than sending them individually.',
        yr: 'SS2',
      },
      {
        q: 'What image did Jesus use to describe the spiritual need and the shortage of workers?',
        o: [
          "'The harvest is plentiful, but the workers are few'",
          "'The field is barren and needs no workers'",
          "'The harvest has already been completed'",
          "'There are too many workers for the task'",
        ],
        a: 0,
        e: "Jesus described the situation using agricultural imagery: 'The harvest is plentiful, but the workers are few. Ask the Lord of the harvest, therefore, to send out workers into his harvest field' (Luke 10:2).",
        h: 'This image compares spiritual need to a large harvest requiring more labourers.',
        yr: 'SS2',
      },
      {
        q: 'What instruction did Jesus give about what the seventy should carry with them for the journey?',
        o: [
          'To carry extra money and supplies for comfort',
          'To carry no purse, no bag, and no sandals',
          'To bring weapons for protection',
          'To bring gifts for every town they visited',
        ],
        a: 1,
        e: "Jesus instructed them to travel without a purse, bag, or sandals, emphasizing urgency and complete trust in God's provision through the hospitality of those they would meet (Luke 10:4).",
        h: 'This instruction required them to travel with minimal possessions.',
        yr: 'SS2',
      },
      {
        q: 'What were the seventy instructed to do if a house or town welcomed them?',
        o: [
          'To immediately leave for another town',
          'To stay there, eating and drinking whatever was offered',
          'To demand better accommodation',
          'To refuse all hospitality offered',
        ],
        a: 1,
        e: 'If welcomed, they were to remain in that same house, eating and drinking whatever was provided, rather than moving from house to house seeking better accommodation (Luke 10:7-8).',
        h: 'This instruction concerns staying rather than seeking more comfortable lodging elsewhere.',
        yr: 'SS2',
      },
      {
        q: 'What two main tasks did Jesus instruct the seventy to perform when welcomed into a town?',
        o: [
          'To collect offerings and build a temple',
          'To heal the sick and proclaim that the kingdom of God had come near',
          'To recruit soldiers and organize a rebellion',
          'To teach only about Roman law',
        ],
        a: 1,
        e: "Jesus instructed the seventy to heal the sick in the towns that welcomed them and to tell the people, 'The kingdom of God has come near to you' (Luke 10:9).",
        h: "Their tasks combined a demonstration of God's power with a specific spoken message.",
        yr: 'SS2',
      },
      {
        q: 'What symbolic action were the seventy instructed to take if a town rejected them?',
        o: [
          'To curse the town publicly',
          'To wipe the dust of that town off their feet as a testimony against it',
          'To burn down part of the town',
          'To remain and argue until accepted',
        ],
        a: 1,
        e: 'Jesus instructed them, if rejected, to wipe off even the dust of that town that clung to their feet as a warning testimony against it before moving on (Luke 10:10-11).',
        h: 'This symbolic act signified a formal disassociation from a town that rejected the message.',
        yr: 'SS2',
      },
      {
        q: 'What report did the seventy give Jesus with joy upon their return?',
        o: [
          'That they had gathered great wealth',
          "That even the demons submitted to them in Jesus' name",
          'That they had converted an entire nation',
          'That they had built new towns',
        ],
        a: 1,
        e: "The seventy returned with joy, reporting to Jesus, 'Lord, even the demons submit to us in your name' (Luke 10:17), amazed at the authority they had exercised.",
        h: 'Their report specifically concerned authority over evil spiritual forces.',
        yr: 'SS2',
      },
      {
        q: "How did Jesus redirect the disciples' source of joy after their report?",
        o: [
          'He told them not to rejoice at all',
          'He told them to rejoice not that spirits submitted to them, but that their names were written in heaven',
          'He told them their success was insignificant',
          'He told them to focus only on future missions',
        ],
        a: 1,
        e: "Jesus told them, 'Do not rejoice that the spirits submit to you, but rejoice that your names are written in heaven' (Luke 10:20), redirecting their joy from spiritual power to the assurance of salvation.",
        h: 'Jesus specifically contrasted two possible sources of rejoicing in his response.',
        yr: 'SS2',
      },
      {
        q: 'What did Jesus give the seventy authority over, according to Luke 10:19?',
        o: [
          'Authority over Roman rulers',
          'Authority to trample on snakes and scorpions, and over all the power of the enemy',
          'Authority over the weather',
          'Authority to appoint new kings',
        ],
        a: 1,
        e: "Jesus told them, 'I have given you authority to trample on snakes and scorpions and to overcome all the power of the enemy; nothing will harm you' (Luke 10:19).",
        h: 'This authority is described using both literal and symbolic imagery of dangerous creatures representing evil power.',
        yr: 'SS2',
      },
      {
        q: "How did Jesus respond emotionally and spiritually when reflecting on the disciples' mission and its results?",
        o: [
          'He expressed disappointment',
          'He rejoiced in the Holy Spirit and thanked the Father',
          'He remained emotionally unaffected',
          'He rebuked the Father for the outcome',
        ],
        a: 1,
        e: "Luke 10:21 records that Jesus, full of joy through the Holy Spirit, praised the Father, thanking Him for revealing spiritual truths to those who were humble like 'little children' rather than to 'the wise and learned.'",
        h: 'His response involved a specific expression of joy directed toward God the Father.',
        yr: 'SS2',
      },
      {
        q: "According to Jesus' prayer in Luke 10:21, to whom had God revealed spiritual truths, rather than to 'the wise and learned'?",
        o: [
          'To wealthy religious leaders',
          "To 'little children' — those humble and receptive",
          'To Roman officials',
          'To no one at all',
        ],
        a: 1,
        e: "Jesus thanked the Father for hiding spiritual truths from the wise and learned but revealing them to 'little children,' emphasizing that humility and receptivity, not intellectual status, open one to understanding God's kingdom (Luke 10:21).",
        h: 'This phrase describes a quality of humility and receptivity rather than a literal age group.',
        yr: 'SS2',
      },
      {
        q: 'What privilege did Jesus tell the disciples privately that they had, which prophets and kings had longed for?',
        o: [
          'Wealth and political power',
          'The privilege of seeing and hearing what many prophets and kings had desired to see but did not',
          'Immunity from all suffering',
          'The ability to perform any miracle they wished',
        ],
        a: 1,
        e: 'Jesus privately told His disciples they were blessed because many prophets and kings had longed to see what they were seeing and to hear what they were hearing, but did not (Luke 10:23-24).',
        h: 'This privilege concerned witnessing the fulfilment of long-anticipated promises.',
        yr: 'SS2',
      },
      {
        q: 'What does the instruction to travel without purse, bag, or sandals primarily teach about mission work?',
        o: [
          'That comfort should always come before urgency',
          "That dependence on God's provision, through the hospitality of others, should characterize urgent kingdom work",
          'That mission workers should avoid interacting with local communities',
          'That material provisions are unnecessary for any mission',
        ],
        a: 1,
        e: "This instruction emphasizes urgent, faith-filled dependence on God's provision through the hospitality of those they would encounter, rather than relying on extensive personal preparation or resources.",
        h: 'Consider what value is prioritized by travelling with minimal personal provision.',
        yr: 'SS2',
      },
      {
        q: "Why is Jesus' redirection of the disciples' joy (from power over demons to names written in heaven) an important lesson?",
        o: [
          'It suggests spiritual power is unimportant and should be avoided entirely',
          "It teaches that ultimate joy should be rooted in one's relationship with and salvation by God, not merely in visible spiritual accomplishments",
          'It implies believers should never celebrate answered prayer or spiritual victories',
          'It suggests names written in heaven are less important than spiritual power',
        ],
        a: 1,
        e: "Jesus' teaching redirects the proper foundation of Christian joy toward the assurance of salvation and relationship with God, rather than being centred primarily on visible spiritual accomplishments or power, which could lead to pride.",
        h: 'Consider what deeper, more secure foundation for joy Jesus points His disciples toward.',
        yr: 'SS2',
      },
      {
        q: "What does the instruction to wipe dust off their feet when rejected suggest about the disciples' mission?",
        o: [
          'That they should force acceptance from unwilling towns',
          'That rejection of the message carried serious spiritual consequences, and the disciples were not to force acceptance but to move on with a symbolic testimony',
          'That rejection should be met with retaliation',
          'That the mission should end entirely after one rejection',
        ],
        a: 1,
        e: 'This symbolic act acknowledged that rejecting the message of the kingdom carried real spiritual consequences, while also freeing the disciples to move forward in their mission without forcing acceptance from an unwilling town.',
        h: "Consider what this action communicated both about accountability and about the disciples' next steps.",
        yr: 'SS2',
      },
      {
        q: "How does the mission of the seventy relate to and expand upon Jesus' earlier calling of the twelve disciples?",
        o: [
          'It replaced the twelve disciples entirely',
          "It shows Jesus expanding His ministry's reach by training and sending out a larger group beyond the original twelve",
          'It has no relationship to the earlier calling of the twelve',
          "It reduced the disciples' role to a purely passive one",
        ],
        a: 1,
        e: "The sending of the seventy represents an expansion of Jesus' ministry strategy — beyond the original twelve, He now trains and commissions a larger group to extend His reach into more towns and communities ahead of His own arrival.",
        h: 'Consider how this larger mission builds upon and extends the earlier calling of the twelve disciples.',
        yr: 'SS2',
      },
      {
        q: 'What broader lesson for Christian mission today can be drawn from Luke 10:1-24 as a whole?',
        o: [
          'Mission work requires extensive resources and should be delayed until fully prepared',
          "Effective mission work combines dependent faith, proclamation and demonstration of God's kingdom, and rightly-placed joy rooted in relationship with God",
          'Mission success should be measured solely by visible power displayed',
          'Only the original twelve disciples were ever called to participate in mission',
        ],
        a: 1,
        e: "Taken as a whole, this passage teaches that effective Christian mission combines dependent faith in God's provision, both proclaiming and demonstrating the reality of God's kingdom, and maintaining joy rooted in relationship with God rather than visible accomplishments alone.",
        h: "Consider how all the different elements of this passage — instructions, results, and Jesus' teaching — combine into an overall lesson for mission work.",
        yr: 'SS2',
      },
      {
        q: 'What warning did Jesus give about towns that witnessed His miracles yet did not repent, in the context surrounding Luke 10?',
        o: [
          'That such towns would face no consequence at all',
          'That it would be more bearable for Sodom, or for Tyre and Sidon, than for towns that rejected His message despite witnessing His works',
          'That such towns would automatically be blessed regardless',
          'That miracles guarantee repentance',
        ],
        a: 1,
        e: 'Jesus warned that towns like Chorazin and Bethsaida, which witnessed His miracles yet did not repent, would face a judgment more severe than that of notoriously wicked cities like Tyre, Sidon, or Sodom (Luke 10:12-15).',
        h: 'Consider the strong warning Jesus gave regarding towns that witnessed miracles but failed to respond with repentance.',
        yr: 'SS2',
      },
      {
        q: 'What term describes the seventy(-two) followers Jesus sent out, distinguishing their role from the twelve apostles?',
        o: [
          'Missionaries / messengers sent ahead to prepare the way',
          'Roman soldiers',
          'Temple priests',
          'Foreign kings',
        ],
        a: 0,
        e: 'The seventy(-two) functioned as messengers or forerunners sent out ahead of Jesus into towns He intended to visit, distinct in number and specific mission from the twelve apostles.',
        h: 'Consider the specific purpose of going ahead of Jesus into towns He planned to visit.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'The Trial of Jesus Christ and His Death',
    topicCode: 'SS2-CRS-19',
    module: 'Life of Christ',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The final hours of Jesus\' earthly life involved a series of unjust <span class="learn-keyword">trials</span> before religious and political authorities, culminating in His <span class="learn-keyword">crucifixion</span>. This topic traces these events and their significance.\n</div>\n\n<h3 class="learn-subheading">1. Arrest and Trial Before the Jewish Authorities</h3>\n<p class="learn-p">After His betrayal by Judas in the Garden of Gethsemane, Jesus was arrested and brought before Caiaphas the high priest and the Sanhedrin (Jewish ruling council). False witnesses testified against Him, and when Caiaphas asked directly if He was the Christ, the Son of God, Jesus affirmed it. The council condemned Him for blasphemy (Matthew 26:57-68).</p>\n\n<h3 class="learn-subheading">2. Peter\'s Denial</h3>\n<p class="learn-p">While Jesus was on trial, Peter, who had followed at a distance, denied knowing Jesus three times before a rooster crowed, fulfilling Jesus\' earlier prediction. Realizing what he had done, Peter wept bitterly (Matthew 26:69-75).</p>\n\n<h3 class="learn-subheading">3. Trial Before Pilate</h3>\n<p class="learn-p">Since the Jewish council lacked authority to execute, Jesus was brought before Pontius Pilate, the Roman governor. Pilate found no basis for a charge against Him and even sent Him to Herod, who also found nothing deserving of death (Luke 23:1-15). Despite Pilate\'s reluctance, the crowd, incited by the chief priests, demanded Jesus\' crucifixion and the release of Barabbas, a known criminal, instead (Matthew 27:15-26).</p>\n\n<h3 class="learn-subheading">4. The Crucifixion and Death of Jesus</h3>\n<p class="learn-p">Jesus was mocked, flogged, and forced to carry His cross to Golgotha ("the place of the skull"), where He was crucified between two criminals. He spoke several statements from the cross, including "Father, forgive them, for they do not know what they are doing" (Luke 23:34), and finally, "It is finished" (John 19:30), before dying. At His death, the temple curtain was torn in two, and a Roman centurion declared, "Surely this man was the Son of God!" (Mark 15:38-39).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Stage</th><th>Key Detail</th></tr></thead>\n    <tbody>\n      <tr><td>Jewish trial</td><td>Before Caiaphas and the Sanhedrin; condemned for blasphemy</td></tr>\n      <tr><td>Peter\'s denial</td><td>Denied Jesus three times before the rooster crowed</td></tr>\n      <tr><td>Roman trial</td><td>Before Pilate, then Herod; Pilate found no fault but yielded to the crowd</td></tr>\n      <tr><td>Crucifixion</td><td>At Golgotha, between two criminals; temple curtain torn at His death</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Learn the sequence of trials in order (Sanhedrin → Pilate → Herod → Pilate again) and be ready to quote at least two of Jesus\' statements from the cross with their references.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Jesus endured unjust trials before both Jewish and Roman authorities and was crucified despite being found without fault, fulfilling His mission of sacrificial death for humanity\'s sin.\n</div>\n',
    questions: [
      {
        q: 'Who was the high priest before whom Jesus was tried after His arrest?',
        o: ['Annas', 'Caiaphas', 'Herod Antipas', 'Pontius Pilate'],
        a: 1,
        e: 'After His arrest, Jesus was brought before Caiaphas the high priest and the assembled Sanhedrin, where He faced accusations from false witnesses (Matthew 26:57).',
        h: 'This figure led the Jewish council that first tried Jesus.',
        yr: 'SS2',
      },
      {
        q: 'What charge did the Sanhedrin condemn Jesus for after He affirmed being the Christ, the Son of God?',
        o: ['Theft', 'Blasphemy', 'Treason against Rome', 'Sedition against Herod'],
        a: 1,
        e: 'When Jesus affirmed under oath that He was the Christ, the Son of God, Caiaphas tore his clothes and declared this blasphemy, leading the council to condemn Him as deserving death (Matthew 26:63-66).',
        h: 'This charge concerned claiming a status considered offensive to Jewish religious authorities.',
        yr: 'SS2',
      },
      {
        q: 'How many times did Peter deny knowing Jesus, as predicted?',
        o: ['Once', 'Twice', 'Three times', 'Four times'],
        a: 2,
        e: 'Peter denied knowing Jesus three times before the rooster crowed, exactly as Jesus had predicted earlier that evening (Matthew 26:34, 69-75).',
        h: "This number matches Jesus' specific earlier prediction to Peter.",
        yr: 'SS2',
      },
      {
        q: 'What did Peter do after realizing he had denied Jesus as predicted?',
        o: [
          'He laughed it off',
          'He wept bitterly',
          'He immediately confessed publicly',
          'He fled the city permanently',
        ],
        a: 1,
        e: "Upon remembering Jesus' words and realizing what he had done, Peter went outside and wept bitterly (Matthew 26:75).",
        h: 'His reaction reflected deep sorrow and regret over his actions.',
        yr: 'SS2',
      },
      {
        q: 'Why was Jesus brought before Pontius Pilate after His trial before the Jewish council?',
        o: [
          'Because Pilate was a personal friend of Jesus',
          'Because the Jewish authorities lacked the legal authority to carry out an execution',
          'Because Pilate had requested to see Jesus personally',
          'Because Herod ordered it directly',
        ],
        a: 1,
        e: 'Since the Jewish ruling council did not have the authority under Roman law to carry out a death sentence, they brought Jesus before the Roman governor Pilate for that purpose (John 18:31; Luke 23:1).',
        h: "Consider the limitation on the Jewish council's legal power under Roman rule.",
        yr: 'SS2',
      },
      {
        q: "What was Pilate's initial assessment of the charges against Jesus?",
        o: [
          'He found Jesus guilty immediately',
          'He found no basis for a charge against Jesus',
          'He refused to examine the case at all',
          'He deferred entirely to the crowd without investigation',
        ],
        a: 1,
        e: 'After questioning Jesus, Pilate declared that he found no basis for a charge against Him, recognizing the political motivation behind the accusations (Luke 23:4; John 18:38).',
        h: "Pilate's own examination led him to a conclusion favourable to Jesus initially.",
        yr: 'SS2',
      },
      {
        q: 'To which other ruler did Pilate send Jesus during the trial process?',
        o: ['Caesar Augustus', 'Herod Antipas', 'King Agrippa', 'Caiaphas'],
        a: 1,
        e: "Upon learning Jesus was from Galilee, under Herod's jurisdiction, Pilate sent Him to Herod Antipas, who was in Jerusalem at the time; Herod also found nothing deserving of death (Luke 23:6-15).",
        h: "This ruler governed the region of Galilee, Jesus' home area.",
        yr: 'SS2',
      },
      {
        q: 'Who was the prisoner released instead of Jesus, according to the custom of releasing a prisoner at the feast?',
        o: ['Judas Iscariot', 'Barabbas, a known criminal', 'Caiaphas', 'Simon of Cyrene'],
        a: 1,
        e: "Following the crowd's demand, incited by the chief priests, Pilate released Barabbas, a notorious prisoner, instead of Jesus, and handed Jesus over to be crucified (Matthew 27:15-26).",
        h: 'This person was a known criminal chosen by the crowd over Jesus.',
        yr: 'SS2',
      },
      {
        q: "What did Pilate do to symbolically express his unwillingness to bear responsibility for Jesus' death?",
        o: [
          'He resigned his position immediately',
          "He washed his hands before the crowd, declaring himself innocent of Jesus' blood",
          'He publicly defended Jesus against the crowd',
          "He ordered the crowd's arrest",
        ],
        a: 1,
        e: "Pilate took water and washed his hands before the crowd, declaring he was innocent of Jesus' blood, though he still handed Jesus over to be crucified (Matthew 27:24).",
        h: 'This symbolic act involved a physical gesture meant to express personal innocence.',
        yr: 'SS2',
      },
      {
        q: 'What place was Jesus crucified at, and what does its name mean?',
        o: [
          "Bethany, meaning 'house of figs'",
          "Golgotha, meaning 'the place of the skull'",
          "Gethsemane, meaning 'oil press'",
          "Nazareth, meaning 'branch'",
        ],
        a: 1,
        e: "Jesus was crucified at Golgotha, a name meaning 'the place of the skull,' located just outside Jerusalem (Matthew 27:33; John 19:17).",
        h: "This location's name relates to its shape or association with death.",
        yr: 'SS2',
      },
      {
        q: "Who did Jesus refer to when He prayed, 'Father, forgive them, for they do not know what they are doing'?",
        o: [
          'The disciples who abandoned Him',
          'Those responsible for His crucifixion',
          'The Roman emperor',
          'The Pharisees exclusively',
        ],
        a: 1,
        e: 'As He was being crucified, Jesus prayed for forgiveness for those carrying out His execution, showing remarkable mercy even in His suffering (Luke 23:34).',
        h: 'This prayer was spoken in the context of His actual crucifixion.',
        yr: 'SS2',
      },
      {
        q: "What were Jesus' final words before His death, according to John 19:30?",
        o: [
          "'I am thirsty'",
          "'It is finished'",
          "'Why have you forsaken me?'",
          "'Into your hands I commit my spirit'",
        ],
        a: 1,
        e: "Jesus' final recorded words in John's Gospel were, 'It is finished,' after which He bowed His head and gave up His spirit (John 19:30).",
        h: 'This statement signified the completion of His redemptive mission.',
        yr: 'SS2',
      },
      {
        q: "What happened to the temple curtain at the moment of Jesus' death?",
        o: [
          'It remained unchanged',
          'It was torn in two, from top to bottom',
          'It was removed and stored away',
          'It caught fire',
        ],
        a: 1,
        e: "At the moment of Jesus' death, the curtain of the temple was torn in two, from top to bottom, symbolizing new access to God made possible through Christ's sacrifice (Matthew 27:51; Mark 15:38).",
        h: 'This event involved a dramatic physical sign occurring in the temple at the exact moment of His death.',
        yr: 'SS2',
      },
      {
        q: "What declaration did the Roman centurion make after witnessing Jesus' death?",
        o: [
          "'This man was a common criminal'",
          "'Surely this man was the Son of God!'",
          "'This man deserved to die'",
          "'This changes nothing'",
        ],
        a: 1,
        e: "Witnessing the events surrounding Jesus' death, the Roman centurion overseeing the crucifixion declared, 'Surely this man was the Son of God!' (Mark 15:39).",
        h: 'This declaration came from a Roman military official who witnessed the crucifixion firsthand.',
        yr: 'SS2',
      },
      {
        q: 'Why is it significant that Pilate repeatedly found no fault in Jesus yet still handed Him over for crucifixion?',
        o: [
          'It shows Pilate had no political pressure at all',
          "It highlights the injustice of Jesus' trial and execution, driven by political pressure rather than genuine guilt",
          'It proves Jesus was actually guilty despite appearances',
          'It shows Pilate acted with complete independence from outside pressure',
        ],
        a: 1,
        e: "Pilate's repeated declarations of Jesus' innocence, followed by his decision to crucify Him anyway under pressure from the crowd and chief priests, highlight the profound injustice of the trial, driven by political expediency rather than genuine guilt.",
        h: "Consider the contrast between Pilate's stated findings and his ultimate action.",
        yr: 'SS2',
      },
      {
        q: "What is the theological significance of the temple curtain being torn at Jesus' death?",
        o: [
          'It had no theological meaning, merely a physical event',
          "It symbolized that through Christ's sacrifice, direct access to God was now opened to all believers",
          "It signified the temple's destruction",
          'It indicated Jewish worship practices would continue unchanged',
        ],
        a: 1,
        e: "The tearing of the temple curtain, which had separated the Most Holy Place from the rest of the temple, is understood theologically to symbolize that through Jesus' sacrificial death, direct access to God was now made available to all believers (compare Hebrews 10:19-20).",
        h: 'Consider what this curtain had previously separated and what its tearing might represent about access to God.',
        yr: 'SS2',
      },
      {
        q: "What does Peter's denial and subsequent bitter weeping teach about failure and repentance?",
        o: [
          "Failure disqualifies a person from any future usefulness in God's service",
          'Even a close, sincere follower of Jesus can fail seriously, yet genuine repentance and restoration remain possible',
          "Peter's denial had no real consequence for him personally",
          'Weeping over sin indicates a lack of true faith',
        ],
        a: 1,
        e: "Peter's serious failure, followed by his genuine sorrow, models that even a close, sincere follower of Jesus can fail significantly, yet such failure does not have to be final — genuine repentance opens the way to restoration, as Peter's later ministry would show.",
        h: "Consider what Peter's later significant role in the early church (studied in a later topic) suggests about the possibility of restoration after failure.",
        yr: 'SS2',
      },
      {
        q: 'How does the release of Barabbas instead of Jesus illustrate a broader theological theme?',
        o: [
          'It has no broader theological meaning',
          'It illustrates substitution — a guilty man released while an innocent man dies in his place, echoing the theme of Christ dying for sinners',
          'It shows that Roman justice was always fair and impartial',
          "It proves that the crowd's judgment was always correct",
        ],
        a: 1,
        e: "The release of the guilty Barabbas in place of the innocent Jesus is often seen as a vivid illustration of the broader theological theme of substitution — an innocent person suffering the penalty deserved by the guilty — central to Christian understanding of Jesus' death for sinners.",
        h: 'Consider the parallel between an innocent person dying while a guilty person goes free.',
        yr: 'SS2',
      },
      {
        q: "Which best summarizes the overall theological significance of Jesus' trial and death as presented in the Gospels?",
        o: [
          'Jesus was justly condemned as a criminal deserving death',
          "Despite being repeatedly found innocent by human authorities, Jesus willingly endured an unjust death, fulfilling His mission as a sacrifice for humanity's sin",
          'The trial and crucifixion have no lasting theological significance',
          "Jesus' death was purely a political execution with no spiritual meaning",
        ],
        a: 1,
        e: "The Gospel accounts present Jesus' trial and death as both a profound historical injustice — an innocent man condemned through political pressure — and a divinely purposed sacrifice, willingly endured by Jesus, for the sin of humanity, central to Christian faith.",
        h: 'Consider how the Gospels frame both the injustice of the human trial and the deeper divine purpose behind these events.',
        yr: 'SS2',
      },
      {
        q: 'What sign did Judas use to identify Jesus to the arresting soldiers in the Garden of Gethsemane?',
        o: ['A raised hand', 'A kiss', 'A shout', 'A written note'],
        a: 1,
        e: "Judas identified Jesus to the arresting soldiers with a kiss, a prearranged signal that led directly to Jesus' arrest (Matthew 26:47-49).",
        h: 'This act of betrayal used an outward gesture typically associated with affection or greeting.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Resurrection and Second Coming of Jesus Christ',
    topicCode: 'SS2-CRS-20',
    module: 'Life of Christ',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The <span class="learn-keyword">resurrection</span> of Jesus Christ is the central event of the Christian faith, and His promised <span class="learn-keyword">second coming</span> remains a central hope for believers. This topic examines both events and their significance.\n</div>\n\n<h3 class="learn-subheading">1. The Discovery of the Empty Tomb</h3>\n<p class="learn-p">On the first day of the week, women — including Mary Magdalene — came to Jesus\' tomb and found the stone rolled away and the tomb empty. An angel announced, "He is not here; he has risen, just as he said" (Matthew 28:1-7). The women were instructed to tell the disciples, and they ran with fear and great joy to report the news.</p>\n\n<h3 class="learn-subheading">2. Post-Resurrection Appearances</h3>\n<p class="learn-p">Jesus appeared multiple times over forty days: to Mary Magdalene near the tomb (John 20:11-18), to two disciples on the road to Emmaus (Luke 24:13-35), to the disciples gathered in a locked room — including a specific appearance to convince the doubting Thomas by showing His wounds (John 20:19-29), and to over five hundred people at once, as Paul later records (1 Corinthians 15:6).</p>\n\n<h3 class="learn-subheading">3. The Ascension and the Great Commission</h3>\n<p class="learn-p">Before ascending to heaven from the Mount of Olives, Jesus gave His disciples the Great Commission: "Go and make disciples of all nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit... And surely I am with you always, to the very end of the age" (Matthew 28:19-20). As He ascended, two angels told the watching disciples that Jesus would return in the same way He had gone (Acts 1:9-11).</p>\n\n<h3 class="learn-subheading">4. The Promised Second Coming</h3>\n<p class="learn-p">The New Testament repeatedly teaches that Jesus will return visibly and gloriously (Matthew 24:30; Acts 1:11), though "no one knows about that day or hour" (Matthew 24:36). Believers are called to live in readiness, faithfulness, and watchfulness while awaiting this return (Matthew 24:42-44; 1 Thessalonians 5:1-6).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Event</th><th>Key Reference</th></tr></thead>\n    <tbody>\n      <tr><td>Empty tomb discovered</td><td>Matthew 28:1-7</td></tr>\n      <tr><td>Appearance to doubting Thomas</td><td>John 20:24-29</td></tr>\n      <tr><td>The Great Commission</td><td>Matthew 28:19-20</td></tr>\n      <tr><td>The Ascension and promise of return</td><td>Acts 1:9-11</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Memorize the exact wording of the Great Commission (Matthew 28:19-20) and be able to explain why the timing of the Second Coming is described as unknown (Matthew 24:36).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Jesus\' resurrection confirms His victory over death and validates His mission, while His promised second coming calls Christians to live in continual readiness, faithfulness, and hope.\n</div>\n',
    questions: [
      {
        q: "Who were among the first to discover Jesus' tomb was empty?",
        o: [
          'The Roman soldiers guarding the tomb',
          'Women, including Mary Magdalene',
          'The eleven remaining disciples together',
          'The chief priests',
        ],
        a: 1,
        e: 'On the first day of the week, women, including Mary Magdalene, came to the tomb and discovered it empty, with the stone rolled away (Matthew 28:1-6).',
        h: 'This group of visitors to the tomb were followers of Jesus who came early in the morning.',
        yr: 'SS2',
      },
      {
        q: 'What did the angel at the empty tomb announce to the women?',
        o: [
          "'He has been stolen by his disciples'",
          "'He is not here; he has risen, just as he said'",
          "'He will never return'",
          "'This is not the correct tomb'",
        ],
        a: 1,
        e: "The angel told the women, 'He is not here; he has risen, just as he said. Come and see the place where he lay' (Matthew 28:6), confirming the resurrection had occurred exactly as Jesus had predicted.",
        h: 'This announcement directly addressed the empty state of the tomb.',
        yr: 'SS2',
      },
      {
        q: "To whom did Jesus first appear after His resurrection, according to John's Gospel?",
        o: ['Peter', 'Mary Magdalene', 'Thomas', 'The eleven disciples together'],
        a: 1,
        e: 'According to John 20:11-18, Jesus first appeared to Mary Magdalene near the tomb, where she initially mistook Him for the gardener before recognizing Him.',
        h: 'This person had come to the tomb early and was weeping outside it.',
        yr: 'SS2',
      },
      {
        q: 'Where did Jesus appear to two disciples who did not initially recognize Him?',
        o: [
          'On the road to Emmaus',
          'In the temple courts',
          'On the Sea of Galilee',
          'At Bethlehem',
        ],
        a: 0,
        e: 'Jesus appeared to two disciples traveling on the road to Emmaus, walking and talking with them without their immediate recognition, until He broke bread with them (Luke 24:13-31).',
        h: 'This appearance occurred during a journey between two towns, with the disciples not recognizing Him at first.',
        yr: 'SS2',
      },
      {
        q: 'What did Jesus do to convince the doubting disciple Thomas of His resurrection?',
        o: [
          'He refused to appear to Thomas at all',
          'He showed Thomas His hands and side, inviting him to touch His wounds',
          'He sent a letter instead of appearing personally',
          'He appeared only in a dream',
        ],
        a: 1,
        e: "When Thomas expressed doubt, Jesus appeared and specifically invited him to touch His wounded hands and side, leading Thomas to declare, 'My Lord and my God!' (John 20:24-29).",
        h: "This method involved physical, tangible proof addressed directly to Thomas' specific doubts.",
        yr: 'SS2',
      },
      {
        q: 'According to Paul in 1 Corinthians 15:6, to how many people did Jesus appear at one time after His resurrection?',
        o: ['Twelve', 'Over five hundred', 'One hundred', 'Just three'],
        a: 1,
        e: 'Paul records that the risen Jesus appeared to more than five hundred people at the same time, most of whom were still alive at the time Paul wrote (1 Corinthians 15:6), providing strong testimony to the resurrection.',
        h: 'This large gathering is cited by Paul as significant historical evidence for the resurrection.',
        yr: 'SS2',
      },
      {
        q: 'What instruction did Jesus give His disciples in the Great Commission?',
        o: [
          'To remain hidden and avoid public ministry',
          'To go and make disciples of all nations, baptizing and teaching them',
          'To return to their former occupations permanently',
          'To wait indefinitely without any active mission',
        ],
        a: 1,
        e: 'In the Great Commission, Jesus instructed His disciples to go and make disciples of all nations, baptizing them in the name of the Father, Son, and Holy Spirit, and teaching them to obey His commands (Matthew 28:19-20).',
        h: "This instruction outlines the disciples' ongoing mission after His resurrection.",
        yr: 'SS2',
      },
      {
        q: 'What promise did Jesus give at the end of the Great Commission?',
        o: [
          "'You will never face any hardship'",
          "'I am with you always, to the very end of the age'",
          "'I will not return until you have converted the whole world'",
          "'You must complete this mission alone'",
        ],
        a: 1,
        e: "Jesus concluded the Great Commission with the assurance, 'And surely I am with you always, to the very end of the age' (Matthew 28:20), promising His continual presence with His followers.",
        h: "This promise addressed the disciples' need for ongoing assurance as they carried out their mission.",
        yr: 'SS2',
      },
      {
        q: 'From where did Jesus ascend to heaven, according to Acts 1?',
        o: ["Jerusalem's temple courts", 'The Mount of Olives', 'The Sea of Galilee', 'Bethlehem'],
        a: 1,
        e: 'Jesus led His disciples out to the vicinity of Bethany, near the Mount of Olives, and ascended into heaven from there as they watched (Luke 24:50-51; Acts 1:9-12).',
        h: "This location was a hill near Jerusalem associated with several events in Jesus' final days.",
        yr: 'SS2',
      },
      {
        q: 'What did the two angels tell the disciples as they watched Jesus ascend into heaven?',
        o: [
          'That Jesus would never return',
          'That Jesus would return in the same way they had seen Him go into heaven',
          'That the disciples should follow Jesus immediately into heaven',
          'That they should stop believing in Jesus',
        ],
        a: 1,
        e: 'As the disciples watched Jesus ascend, two angels appeared and told them that Jesus would come back in the same way they had seen Him go into heaven (Acts 1:10-11).',
        h: "This message specifically addressed the manner and certainty of Jesus' future return.",
        yr: 'SS2',
      },
      {
        q: "According to Matthew 24:36, who knows the exact day and hour of Jesus' second coming?",
        o: [
          'Only the disciples',
          'No one — not even the angels or the Son, but only the Father',
          'Only religious scholars who study carefully enough',
          'Every believer who prays sincerely',
        ],
        a: 1,
        e: "Matthew 24:36 states that no one knows the day or hour of Jesus' return, not even the angels in heaven, nor the Son, but only the Father, emphasizing the mystery surrounding its exact timing.",
        h: 'This verse specifically excludes even the angels and the Son from knowing the precise timing.',
        yr: 'SS2',
      },
      {
        q: 'What attitude does Jesus instruct His followers to maintain regarding His second coming, according to Matthew 24:42-44?',
        o: [
          'Complacency, since the timing is unknown anyway',
          'Watchfulness and readiness, since the exact time is unknown',
          'Fear and despair',
          'Indifference toward daily faithfulness',
        ],
        a: 1,
        e: "Jesus instructs His followers to 'keep watch' and be ready, since they do not know on what day their Lord will come, using the image of a homeowner needing to be alert against a thief (Matthew 24:42-44).",
        h: 'Consider the practical attitude Jesus recommends given the uncertainty of the timing.',
        yr: 'SS2',
      },
      {
        q: "How does 1 Thessalonians 5:1-6 describe the appropriate posture believers should have toward the day of the Lord's return?",
        o: [
          'Believers should be asleep and unaware, like unbelievers',
          'Believers, as children of light, should be alert and self-controlled, not caught off guard',
          'Believers should attempt to calculate the exact date',
          'Believers should ignore the topic entirely',
        ],
        a: 1,
        e: 'Paul describes the day of the Lord as coming like a thief in the night for the unprepared, but urges believers, as children of the light, to remain alert and self-controlled rather than being caught off guard (1 Thessalonians 5:1-6).',
        h: "Consider the contrast drawn between believers ('children of light') and those unprepared for the day.",
        yr: 'SS2',
      },
      {
        q: 'Why is the resurrection considered central to the Christian faith?',
        o: [
          'Because it has minimal theological significance',
          "Because it confirms Jesus' victory over sin and death and validates His identity and mission as the Son of God",
          'Because it only mattered to the original disciples, not later believers',
          'Because it was primarily a symbolic, non-literal event',
        ],
        a: 1,
        e: "The resurrection is central to Christian faith because it confirms Jesus' victory over sin and death, validates His identity and mission as the Son of God, and provides the basis for the Christian hope of eternal life (see also 1 Corinthians 15:14-17).",
        h: 'Consider what foundational Christian beliefs depend directly on the reality of the resurrection.',
        yr: 'SS2',
      },
      {
        q: 'What significance does the large number of witnesses to the resurrection (including the 500) hold for Christian apologetics?',
        o: [
          'It has no evidentiary value at all',
          'It provides strong historical testimony supporting the reality of the resurrection, since many witnesses were still alive and could be questioned',
          'It suggests the resurrection was merely a rumour with no verification',
          'It proves the resurrection was a private, unverifiable experience',
        ],
        a: 1,
        e: "Paul's reference to over 500 witnesses, most of whom were still alive when he wrote, is often cited as significant historical evidence supporting the resurrection, since such a claim could have been easily challenged if false.",
        h: 'Consider why mentioning many still-living witnesses would strengthen the credibility of this claim.',
        yr: 'SS2',
      },
      {
        q: "How does Jesus' appearance to Thomas address the relationship between faith and evidence?",
        o: [
          'It shows faith should never be connected to any evidence',
          "It shows Jesus graciously provided physical evidence for Thomas' doubt, while also affirming a special blessing for those who believe without seeing",
          'It shows Thomas was rejected for his doubt',
          'It has no bearing on the relationship between faith and evidence',
        ],
        a: 1,
        e: "Jesus' response to Thomas — providing physical evidence for his doubt while stating, 'Blessed are those who have not seen and yet have believed' (John 20:29) — addresses both the value of evidence and the special blessing given to those who trust without needing direct physical proof.",
        h: "Consider Jesus' additional statement following His encounter with Thomas about the blessing of believing without seeing.",
        yr: 'SS2',
      },
      {
        q: 'How does the promise of the Second Coming shape Christian ethical living, according to New Testament teaching?',
        o: [
          'It has no bearing on how Christians should live in the present',
          'It calls believers to live faithfully, watchfully, and in readiness, since the timing is unknown',
          'It encourages believers to become passive and stop all activity',
          'It teaches that ethical living is unrelated to eschatological hope',
        ],
        a: 1,
        e: "The New Testament consistently connects the hope of Jesus' return to a call for faithful, watchful, and ready living in the present, since believers do not know when that day will come (Matthew 24:42-44; 1 Thessalonians 5:6).",
        h: 'Consider how uncertainty about the timing of a future event might shape present daily conduct.',
        yr: 'SS2',
      },
      {
        q: 'What is the relationship between the resurrection and the Great Commission in the Gospel accounts?',
        o: [
          'They are entirely unrelated events with no connection',
          "The resurrected Jesus personally commissions His disciples for ongoing mission, connecting His victory over death to the disciples' worldwide task",
          'The Great Commission occurred before the resurrection',
          'The Great Commission replaced the need for the resurrection',
        ],
        a: 1,
        e: "The Great Commission is given by the resurrected Jesus Himself, directly linking His victory over death to the disciples' commissioning for worldwide mission, showing that the resurrection empowers and authorizes their ongoing task (Matthew 28:18-20).",
        h: 'Consider the timing of the Great Commission within the resurrection narrative — does it come before or after the resurrection?',
        yr: 'SS2',
      },
      {
        q: 'Which best summarizes the combined significance of the resurrection and the promised second coming for Christian hope?',
        o: [
          'They together confirm both the completed victory of Christ over death and the future certainty of His return, anchoring Christian hope in both past accomplishment and future promise',
          'They have no combined significance for Christian belief',
          'Only the resurrection matters; the second coming is an unimportant addition',
          'Both events are purely symbolic with no bearing on genuine hope',
        ],
        a: 0,
        e: "Together, the resurrection (confirming Christ's completed victory over death) and the promised second coming (assuring His future return) anchor Christian hope in both a historical accomplishment already achieved and a certain future promise yet to be fulfilled.",
        h: 'Consider how these two events, one past and one future, work together to form the foundation of Christian hope.',
        yr: 'SS2',
      },
      {
        q: 'What did Jesus do for the two disciples on the road to Emmaus that finally opened their eyes to recognize Him?',
        o: [
          'He performed a miracle in front of a crowd',
          'He broke bread with them at the table',
          'He revealed His identity through a formal speech',
          'He healed a sick person before them',
        ],
        a: 1,
        e: 'As Jesus broke the bread at the table with the two disciples in Emmaus, their eyes were opened and they recognized Him, though He then vanished from their sight (Luke 24:30-31).',
        h: 'Consider the specific familiar action, connected to earlier meals, that triggered their recognition of Him.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Fellowship in the Early Church',
    topicCode: 'SS2-CRS-21',
    module: 'The Early Church',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Following the outpouring of the Holy Spirit at Pentecost, the early believers formed a distinctive <span class="learn-keyword">fellowship</span> marked by devotion, sharing, and unity. This topic examines the nature and characteristics of this fellowship as recorded in Acts.\n</div>\n\n<h3 class="learn-subheading">1. Pentecost and the Birth of the Church</h3>\n<p class="learn-p">On the day of Pentecost, the Holy Spirit came upon the disciples with the sound of a rushing wind and tongues of fire, enabling them to speak in other languages (Acts 2:1-4). Peter preached boldly, and about three thousand people were added to the believers that day after responding to his message (Acts 2:14-41).</p>\n\n<h3 class="learn-subheading">2. The Marks of Early Church Fellowship</h3>\n<p class="learn-p">Acts 2:42-47 describes the believers as devoted to the apostles\' teaching, to fellowship, to the breaking of bread, and to prayer. They shared everything in common, selling possessions and goods to give to anyone in need, met together daily in the temple courts and in homes, and ate together "with glad and sincere hearts," praising God.</p>\n\n<h3 class="learn-subheading">3. Practical Sharing and Generosity</h3>\n<p class="learn-p">The believers\' generosity extended to selling property and possessions to meet the needs of the community (Acts 2:45; 4:32-35). Barnabas, for example, sold a field and brought the money to the apostles for distribution (Acts 4:36-37), exemplifying the spirit of voluntary, joyful giving that characterized the community.</p>\n\n<h3 class="learn-subheading">4. The Result and Significance of This Fellowship</h3>\n<p class="learn-p">This authentic community life resulted in believers enjoying the favour of all the people, and "the Lord added to their number daily those who were being saved" (Acts 2:47). Their fellowship modelled a radical unity and love that testified powerfully to the truth of the gospel.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Mark of Fellowship</th><th>Description</th></tr></thead>\n    <tbody>\n      <tr><td>Devotion to teaching</td><td>Committed to the apostles\' teaching</td></tr>\n      <tr><td>Fellowship</td><td>Meeting together regularly, sharing life</td></tr>\n      <tr><td>Breaking of bread</td><td>Shared meals, including communion</td></tr>\n      <tr><td>Prayer</td><td>Committed to regular, communal prayer</td></tr>\n      <tr><td>Sharing possessions</td><td>Selling goods to meet others\' needs</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Learn Acts 2:42 precisely — the four specific things the early believers "devoted themselves to" is a very commonly tested list.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> The early church\'s fellowship, marked by devotion to teaching, prayer, shared meals, and generous sharing of possessions, models authentic Christian community that continues to inspire the church today.\n</div>\n',
    questions: [
      {
        q: 'What event marked the birth of the church, as recorded in Acts 2?',
        o: [
          "Jesus' resurrection",
          'The outpouring of the Holy Spirit at Pentecost',
          'The Great Commission',
          "Paul's conversion",
        ],
        a: 1,
        e: 'The church is traditionally understood to have begun at Pentecost, when the Holy Spirit came upon the gathered disciples with the sound of a rushing wind and tongues of fire, enabling them to speak in other languages (Acts 2:1-4).',
        h: 'This event occurred on a specific Jewish festival day, fifty days after Passover.',
        yr: 'SS2',
      },
      {
        q: 'What supernatural signs accompanied the coming of the Holy Spirit at Pentecost?',
        o: [
          'A great earthquake only',
          'The sound of a rushing wind and what looked like tongues of fire',
          'Complete darkness over the land',
          'A voice from heaven alone, with no other sign',
        ],
        a: 1,
        e: 'The coming of the Holy Spirit was accompanied by the sound of a violent, rushing wind filling the house, and what appeared to be tongues of fire resting on each of the disciples (Acts 2:2-3).',
        h: 'Two distinct sensory phenomena — one auditory, one visual — accompanied this event.',
        yr: 'SS2',
      },
      {
        q: "Approximately how many people were added to the believers after Peter's sermon at Pentecost?",
        o: ['About one hundred', 'About three thousand', 'About twelve', 'About five hundred'],
        a: 1,
        e: "After Peter's bold sermon explaining the events of Pentecost and calling people to repentance, about three thousand people were baptized and added to the believers that day (Acts 2:41).",
        h: "This large number reflects the powerful, immediate response to Peter's message.",
        yr: 'SS2',
      },
      {
        q: 'According to Acts 2:42, to what four things did the early believers devote themselves?',
        o: [
          'Wealth, power, fame, and comfort',
          "The apostles' teaching, fellowship, the breaking of bread, and prayer",
          'Politics, trade, farming, and travel',
          'Isolation, silence, fasting, and solitude',
        ],
        a: 1,
        e: "Acts 2:42 describes the believers as devoted to the apostles' teaching, to fellowship, to the breaking of bread, and to prayer, forming the core marks of early church community life.",
        h: 'This verse gives a specific list of four distinct practices central to their community.',
        yr: 'SS2',
      },
      {
        q: 'How did the early believers handle their possessions, according to Acts 2:44-45?',
        o: [
          'They kept everything strictly for personal use',
          'They had everything in common, selling possessions to give to anyone who had need',
          'They refused to share anything with others',
          'They were required by law to give everything away',
        ],
        a: 1,
        e: 'Acts 2:44-45 describes the believers as having everything in common, selling their possessions and goods and distributing to anyone who had need, reflecting voluntary generosity within the community.',
        h: 'Consider the description of shared ownership and voluntary distribution among the believers.',
        yr: 'SS2',
      },
      {
        q: 'Where did the early believers meet together, according to Acts 2:46?',
        o: [
          'Only in the wilderness',
          'In the temple courts and in their homes',
          'Only in synagogues',
          'Only outside the city',
        ],
        a: 1,
        e: 'Acts 2:46 records that the believers met together daily in the temple courts and also broke bread together in their homes, combining public and more intimate settings for fellowship.',
        h: 'This description includes both a large public setting and smaller private gatherings.',
        yr: 'SS2',
      },
      {
        q: "What attitude characterized the believers' shared meals together, according to Acts 2:46?",
        o: [
          'Reluctance and suspicion',
          'Glad and sincere hearts',
          'Formal, joyless obligation',
          'Competitive rivalry',
        ],
        a: 1,
        e: "Acts 2:46 describes the believers eating together with 'glad and sincere hearts,' reflecting genuine joy and authenticity in their shared community life.",
        h: 'This description conveys the emotional tone of their communal meals.',
        yr: 'SS2',
      },
      {
        q: "What was the community's standing among the wider public, according to Acts 2:47?",
        o: [
          'They were universally hated',
          'They enjoyed the favour of all the people',
          'They were completely ignored',
          'They faced immediate persecution from everyone',
        ],
        a: 1,
        e: 'Acts 2:47 notes that the believers, praising God, enjoyed the favour of all the people, showing their positive impact and reputation in the wider community at this early stage.',
        h: 'Consider the initial public reception of this community before later persecution developed.',
        yr: 'SS2',
      },
      {
        q: "What happened daily as a result of the early church's fellowship, according to Acts 2:47?",
        o: [
          'No further growth occurred',
          'The Lord added to their number daily those who were being saved',
          'Members left the community daily',
          'The apostles stopped teaching',
        ],
        a: 1,
        e: 'Acts 2:47 concludes with the encouraging note that the Lord added to their number daily those who were being saved, showing the ongoing growth of the church.',
        h: 'This detail describes the continuing, regular growth of the believing community.',
        yr: 'SS2',
      },
      {
        q: 'Who is specifically named in Acts 4:36-37 as an example of selling property to support the community?',
        o: ['Peter', 'Barnabas', 'Judas Iscariot', 'Caiaphas'],
        a: 1,
        e: 'Barnabas, a Levite from Cyprus, sold a field he owned and brought the money to the apostles, exemplifying the generous spirit of sharing within the early church community (Acts 4:36-37).',
        h: 'This individual later became known for his encouraging character and generosity.',
        yr: 'SS2',
      },
      {
        q: 'According to Acts 4:32-35, what characterized the hearts and minds of the believers regarding their possessions?',
        o: [
          'Every believer claimed exclusive ownership of everything they had',
          'They were one in heart and mind, and no one claimed personal ownership of possessions, but shared everything',
          'They constantly disputed over property',
          'They avoided any communal life',
        ],
        a: 1,
        e: 'Acts 4:32 describes the believers as being one in heart and mind, with no one claiming private ownership of possessions but sharing everything they had in common.',
        h: 'This description emphasizes both unity of spirit and practical sharing of material resources.',
        yr: 'SS2',
      },
      {
        q: 'What significance does the sequence — devotion to teaching, then fellowship, then breaking bread, then prayer (Acts 2:42) — suggest about early church priorities?',
        o: [
          'These practices had no particular order or significance',
          'It suggests a balanced community life combining sound doctrine, relationship, shared meals, and communication with God',
          'It suggests only teaching mattered, with the rest being unimportant',
          'It suggests prayer was the least important practice',
        ],
        a: 1,
        e: "The combination of these four practices together suggests a balanced, integrated community life — combining right belief (apostles' teaching), relationship (fellowship), shared experience (breaking of bread), and communication with God (prayer) — rather than emphasizing only one dimension.",
        h: 'Consider what combining all four elements together, rather than just one, suggests about a healthy community.',
        yr: 'SS2',
      },
      {
        q: "Why is the early church's voluntary sharing of possessions significant, compared to a system of forced redistribution?",
        o: [
          'It was legally mandated by Roman authorities',
          'It reflected a voluntary, Spirit-motivated generosity flowing from genuine love and unity, not external compulsion',
          'It had no relationship to their faith at all',
          'It was identical to modern taxation systems',
        ],
        a: 1,
        e: "The early church's sharing was voluntary and motivated by genuine love, unity, and the transforming work of the Holy Spirit within the community, distinguishing it from any externally imposed or legally mandated system of redistribution.",
        h: 'Consider the difference between generosity that flows from genuine transformation versus a system imposed from outside.',
        yr: 'SS2',
      },
      {
        q: "How does the description of the early church meeting 'in the temple courts and in their homes' reflect a balance in their community life?",
        o: [
          'It shows they had no organized gathering at all',
          'It reflects a balance between larger, public worship gatherings and smaller, more intimate fellowship settings',
          'It shows they avoided public spaces entirely',
          'It shows they only ever met in large public gatherings',
        ],
        a: 1,
        e: 'Meeting both in the temple courts (a larger, public setting) and in homes (smaller, more intimate gatherings) reflects a healthy balance between larger corporate worship and closer, relational fellowship within the early church community.',
        h: 'Consider the different dynamics offered by a large public space compared to smaller home gatherings.',
        yr: 'SS2',
      },
      {
        q: "What connection exists between the early church's fellowship and its continued growth, according to Acts 2:46-47?",
        o: [
          'There is no connection between their community life and growth',
          'Their authentic fellowship and favour among the people appear directly connected to the daily growth of new believers',
          'Growth occurred despite, not because of, their fellowship',
          'Growth was entirely unrelated to any social factors',
        ],
        a: 1,
        e: "The passage's structure — describing their fellowship and favour among the people immediately followed by the note of daily growth — suggests the early church's authentic, attractive community life played a role in drawing others to faith.",
        h: 'Consider how the description of their fellowship is immediately connected to the report of growth in the same passage.',
        yr: 'SS2',
      },
      {
        q: "What lesson does Barnabas' example of selling his field teach about generosity within Christian community?",
        o: [
          'Generosity should always be forced upon believers',
          'Voluntary, sacrificial generosity from individuals can significantly support the needs of the wider Christian community',
          'Only apostles were expected to give generously',
          'Selling personal property is required of every Christian at all times',
        ],
        a: 1,
        e: "Barnabas' voluntary sacrifice of selling his own property to support the community models how individual generosity, freely given, can significantly meet the needs of fellow believers, without being a legally mandated requirement for everyone.",
        h: "Consider what Barnabas' specific, individual act of generosity illustrates about the broader pattern of sharing in the early church.",
        yr: 'SS2',
      },
      {
        q: "How might the early church's model of fellowship be relevantly applied by Christian communities today?",
        o: [
          'By ignoring the model entirely as irrelevant to modern life',
          'By intentionally cultivating devotion to teaching, genuine relationship, shared meals, prayer, and practical generosity within their communities',
          'By focusing exclusively on financial matters',
          'By avoiding any communal gatherings altogether',
        ],
        a: 1,
        e: "Applying the early church's model, Christian communities today can intentionally cultivate the same core elements — sound teaching, genuine relationship, shared meals, prayer, and practical generosity — as a foundation for authentic community life.",
        h: 'Consider how the specific elements described in Acts 2:42-47 might translate into practices for a modern congregation.',
        yr: 'SS2',
      },
      {
        q: "Which best summarizes the overall significance of the early church's fellowship as described in Acts 2?",
        o: [
          'It represents an unrealistic ideal with no lasting relevance',
          'It models a Spirit-empowered, authentic community marked by devotion, unity, and generosity that continues to inspire Christian community life today',
          'It was a temporary phenomenon limited strictly to the first believers with no wider application',
          'It primarily emphasizes individual isolation over communal life',
        ],
        a: 1,
        e: "The early church's fellowship, empowered by the Holy Spirit and marked by devoted teaching, genuine relationship, shared meals, prayer, and generous sharing, continues to serve as an inspiring model of authentic Christian community for believers throughout history, including today.",
        h: 'Consider the overall enduring significance of this description for understanding what genuine Christian community should look like.',
        yr: 'SS2',
      },
      {
        q: 'What term is used in Acts 2:42 to describe the shared meals and communal remembrance practiced by the early believers?',
        o: [
          'The breaking of bread',
          'The washing of feet',
          'The anointing of oil',
          'The casting of lots',
        ],
        a: 0,
        e: "Acts 2:42 specifically names 'the breaking of bread' as one of the four things the early believers devoted themselves to, referring to their shared meals and remembrance of Christ.",
        h: 'This specific phrase appears in the list of four devoted practices in Acts 2:42.',
        yr: 'SS2',
      },
      {
        q: "What does the phrase 'they had everything in common' (Acts 2:44) primarily emphasize about the early church?",
        o: [
          'Strict enforced communism under law',
          'A voluntary spirit of shared generosity and unity among believers',
          'Complete abandonment of all personal property rights permanently',
          'A temporary requirement that applied only to the apostles',
        ],
        a: 1,
        e: "This phrase emphasizes the voluntary spirit of generosity and unity among the early believers, who freely shared their resources to meet one another's needs, rather than any legally enforced system.",
        h: 'Consider the voluntary, relational nature of this sharing rather than any external compulsion.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'The Holy Spirit and the Mission of the Gentiles',
    topicCode: 'SS2-CRS-22',
    module: 'The Early Church',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  A pivotal development in the early church was the extension of the gospel beyond the Jewish people to the <span class="learn-keyword">Gentiles</span> (non-Jews), guided decisively by the <span class="learn-keyword">Holy Spirit</span>. This topic examines this expansion through the accounts of Peter and Cornelius, and Paul\'s missionary work.\n</div>\n\n<h3 class="learn-subheading">1. Peter\'s Vision and Cornelius</h3>\n<p class="learn-p">Cornelius, a Roman centurion and devout Gentile, received a vision instructing him to send for Peter (Acts 10:1-8). Meanwhile, Peter received a vision of a sheet containing various animals, with a voice telling him, "Do not call anything impure that God has made clean" (Acts 10:9-16), preparing him to overcome his hesitation about associating with Gentiles. Peter then visited Cornelius\'s household, and as he preached, the Holy Spirit fell upon the Gentiles present, just as it had on the Jewish believers at Pentecost (Acts 10:44-48).</p>\n\n<h3 class="learn-subheading">2. The Jerusalem Council\'s Decision</h3>\n<p class="learn-p">This inclusion of Gentiles raised significant questions about whether they needed to follow Jewish law (particularly circumcision) to be saved. The apostles and elders met at the Jerusalem Council and, guided by the Holy Spirit, concluded that Gentiles did not need to be circumcised or bound by the full Mosaic law to receive salvation, only to abstain from specific practices that would offend Jewish sensibilities (Acts 15:1-29).</p>\n\n<h3 class="learn-subheading">3. Paul\'s Missionary Journeys to the Gentiles</h3>\n<p class="learn-p">The Holy Spirit specifically set apart Barnabas and Saul (Paul) for missionary work among the Gentiles (Acts 13:1-3). Paul undertook several missionary journeys throughout Asia Minor, Greece, and beyond, planting churches and preaching that salvation through Christ was available to Jew and Gentile alike (Acts 13-20; Galatians 3:28).</p>\n\n<h3 class="learn-subheading">4. The Significance of This Expansion</h3>\n<p class="learn-p">This development fulfilled Jesus\' commission to make disciples "of all nations" (Matthew 28:19) and confirmed that the gospel was for all humanity, not limited to any single ethnic or religious group, reflecting Paul\'s teaching that "there is neither Jew nor Gentile... for you are all one in Christ Jesus" (Galatians 3:28).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Event</th><th>Significance</th></tr></thead>\n    <tbody>\n      <tr><td>Peter\'s vision and Cornelius\'s conversion</td><td>Confirmed Gentiles could receive the Holy Spirit and salvation directly</td></tr>\n      <tr><td>The Jerusalem Council</td><td>Ruled Gentiles need not follow the full Mosaic law to be saved</td></tr>\n      <tr><td>Paul\'s missionary journeys</td><td>Spread the gospel widely among Gentile nations</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Know Cornelius\'s occupation (Roman centurion) and the content of Peter\'s vision (a sheet with animals) — these specific details are often tested directly.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Through Peter\'s encounter with Cornelius, the Jerusalem Council\'s ruling, and Paul\'s missionary journeys, the Holy Spirit guided the early church to extend the gospel fully to the Gentiles, confirming salvation is for all people.\n</div>\n',
    questions: [
      {
        q: 'Who was Cornelius, as described in Acts 10?',
        o: [
          'A Jewish high priest',
          'A Roman centurion and devout Gentile',
          'A disciple of John the Baptist',
          'A member of the Sanhedrin',
        ],
        a: 1,
        e: 'Cornelius was a Roman centurion, a devout man who feared God, gave generously to the poor, and prayed regularly, though he was a Gentile, not a Jew (Acts 10:1-2).',
        h: 'This person held a military rank within the Roman army.',
        yr: 'SS2',
      },
      {
        q: 'What instruction did Cornelius receive in his vision?',
        o: [
          'To offer a sacrifice at the temple',
          'To send for Peter, who was staying in Joppa',
          'To travel to Rome immediately',
          'To fast for forty days',
        ],
        a: 1,
        e: 'An angel appeared to Cornelius in a vision, instructing him to send men to Joppa to bring back a man named Simon Peter (Acts 10:3-6).',
        h: 'This instruction directed him to seek out a specific person from the early church leadership.',
        yr: 'SS2',
      },
      {
        q: 'What did Peter see in his vision before visiting Cornelius?',
        o: [
          'A burning bush',
          'A large sheet let down from heaven containing various animals',
          'An angel with a sword',
          'A vision of the temple being destroyed',
        ],
        a: 1,
        e: 'Peter saw a vision of a large sheet being let down from heaven, containing all kinds of animals, and heard a voice telling him to kill and eat, though he initially refused because some were considered ritually impure (Acts 10:9-14).',
        h: 'This vision involved a container coming down from the sky filled with various creatures.',
        yr: 'SS2',
      },
      {
        q: "What message did the voice in Peter's vision convey regarding what God had made clean?",
        o: [
          "'Nothing is truly clean before God'",
          "'Do not call anything impure that God has made clean'",
          "'Only Jewish food laws matter'",
          "'All food must still be avoided'",
        ],
        a: 1,
        e: "The voice told Peter three times, 'Do not call anything impure that God has made clean' (Acts 10:15), preparing him to understand that God's acceptance was no longer limited by the old ceremonial distinctions, extending also to the Gentiles themselves.",
        h: "This message directly challenged Peter's previous understanding of ritual purity.",
        yr: 'SS2',
      },
      {
        q: "What happened while Peter was still speaking to Cornelius's household?",
        o: [
          'Nothing significant occurred',
          'The Holy Spirit fell on all who heard the message, just as at Pentecost',
          "The household rejected Peter's message entirely",
          'Peter was arrested by Roman soldiers',
        ],
        a: 1,
        e: 'While Peter was still speaking, the Holy Spirit fell upon all who heard the message, and the Gentiles present began speaking in tongues and praising God, astonishing the Jewish believers who had come with Peter (Acts 10:44-46).',
        h: 'This event mirrored an earlier significant outpouring of the Spirit upon Jewish believers.',
        yr: 'SS2',
      },
      {
        q: "What did Peter conclude after witnessing the Holy Spirit fall on Cornelius's household?",
        o: [
          'That Gentiles should still be excluded from baptism',
          'That they could not refuse water for baptizing these Gentiles, since they had received the Holy Spirit just as the Jewish believers had',
          'That the vision had been a mistake',
          'That Cornelius should convert to Judaism first',
        ],
        a: 1,
        e: 'Recognizing that God had clearly given the Holy Spirit to the Gentiles just as He had to the Jewish believers, Peter concluded that there was no reason to withhold water baptism from them, and he ordered them to be baptized (Acts 10:47-48).',
        h: 'Consider what evidence convinced Peter that these Gentiles should be fully included in the church.',
        yr: 'SS2',
      },
      {
        q: 'What significant question did the inclusion of Gentiles raise, leading to the Jerusalem Council?',
        o: [
          'Whether Gentiles needed to be circumcised and follow the full Mosaic law to be saved',
          'Whether Gentiles should be allowed to worship at all',
          'Whether the apostles should stop preaching',
          'Whether Jesus had truly risen from the dead',
        ],
        a: 0,
        e: 'Some believers insisted that Gentile converts needed to be circumcised and follow the law of Moses to be saved, prompting a significant council meeting in Jerusalem to resolve the dispute (Acts 15:1-6).',
        h: 'This debate concerned whether Jewish ceremonial requirements applied to new Gentile believers.',
        yr: 'SS2',
      },
      {
        q: "What was the Jerusalem Council's decision regarding Gentile converts, according to Acts 15?",
        o: [
          'Gentiles must be circumcised and fully follow the Mosaic law',
          'Gentiles did not need to be circumcised or bound by the full Mosaic law to be saved, only to abstain from certain specific practices',
          'Gentiles should be entirely excluded from the church',
          'The council reached no decision at all',
        ],
        a: 1,
        e: 'The Jerusalem Council concluded, guided by the Holy Spirit, that Gentile believers did not need to be circumcised or keep the full Mosaic law, requiring only that they abstain from specific practices that would offend Jewish sensibilities, such as food sacrificed to idols and sexual immorality (Acts 15:19-29).',
        h: "Consider the council's ruling on the specific requirement of circumcision that had been debated.",
        yr: 'SS2',
      },
      {
        q: 'Who were specifically set apart by the Holy Spirit for missionary work among the Gentiles, according to Acts 13:1-3?',
        o: ['Peter and John', 'Barnabas and Saul (Paul)', 'James and Andrew', 'Matthew and Thomas'],
        a: 1,
        e: "While the church at Antioch was worshipping and fasting, the Holy Spirit said, 'Set apart for me Barnabas and Saul for the work to which I have called them,' commissioning them specifically for missionary work (Acts 13:1-3).",
        h: 'These two individuals were specifically named and commissioned together for this mission.',
        yr: 'SS2',
      },
      {
        q: "What was the general focus of Paul's missionary journeys as recorded in Acts?",
        o: [
          'Preaching exclusively within Jerusalem',
          'Planting churches and preaching the gospel throughout Asia Minor, Greece, and beyond, largely among Gentiles',
          'Avoiding all interaction with non-Jewish people',
          'Focusing solely on political reform',
        ],
        a: 1,
        e: "Paul's missionary journeys, recorded across Acts 13-20, focused on spreading the gospel and planting churches throughout regions including Asia Minor and Greece, with significant outreach to Gentile populations.",
        h: "Consider the wide geographic scope covered by Paul's recorded travels.",
        yr: 'SS2',
      },
      {
        q: 'What does Galatians 3:28 teach about the status of Jews and Gentiles in Christ?',
        o: [
          'That Gentiles remain permanently inferior to Jews',
          'That there is neither Jew nor Gentile, for all are one in Christ Jesus',
          'That Jews and Gentiles must remain entirely separate',
          'That only Jews can be truly saved',
        ],
        a: 1,
        e: 'Galatians 3:28 declares that in Christ there is neither Jew nor Gentile, slave nor free, male nor female, for all believers are one in Christ Jesus, emphasizing the unity available through faith regardless of ethnic or social background.',
        h: 'This verse addresses several categories of distinction, unified through a common relationship with Christ.',
        yr: 'SS2',
      },
      {
        q: 'Why is the account of Peter and Cornelius considered a turning point in early church history?',
        o: [
          'Because it confirmed Gentiles were permanently excluded from salvation',
          'Because it clearly confirmed that the gospel and the gift of the Holy Spirit were available directly to Gentiles, not only to Jews',
          'Because it had no real impact on later church practice',
          'Because it led to the abandonment of all Christian mission',
        ],
        a: 1,
        e: "This account is considered pivotal because it provided clear, Spirit-confirmed evidence that the gospel and salvation were available directly to Gentiles without first requiring full conversion to Judaism, shaping the future direction of the church's mission.",
        h: 'Consider what this event confirmed that had previously been uncertain or debated among early believers.',
        yr: 'SS2',
      },
      {
        q: "How did Peter's initial hesitation in his vision reflect broader Jewish attitudes of the time toward Gentiles?",
        o: [
          'Jewish believers had no reservations at all about associating with Gentiles',
          'Many Jewish believers, including Peter initially, held reservations about associating closely with Gentiles due to purity laws and cultural separation',
          'Gentiles were considered ritually superior to Jews',
          'This hesitation had no cultural or religious basis',
        ],
        a: 1,
        e: "Peter's initial refusal to eat what he considered ritually unclean reflects the broader Jewish cultural and religious reservations of the time about close association with Gentiles, which his vision was specifically designed to address and overcome.",
        h: 'Consider why Peter would have initially resisted the instruction given in his vision.',
        yr: 'SS2',
      },
      {
        q: 'What role did the Holy Spirit play in resolving the dispute at the Jerusalem Council?',
        o: [
          "The Holy Spirit played no role in the council's decision",
          "The council explicitly attributed their decision to being guided by the Holy Spirit, stating 'it seemed good to the Holy Spirit and to us'",
          "The Holy Spirit opposed the council's final decision",
          'The council ignored any spiritual guidance in reaching their conclusion',
        ],
        a: 1,
        e: "The Jerusalem Council's letter explicitly states, 'It seemed good to the Holy Spirit and to us' (Acts 15:28), showing that the apostles understood their decision as guided by the Holy Spirit's direction, not merely human deliberation.",
        h: "Consider the specific phrase used in the council's letter describing the source of their decision.",
        yr: 'SS2',
      },
      {
        q: "How does the expansion of the gospel to the Gentiles relate to Jesus' earlier Great Commission?",
        o: [
          "It contradicts the Great Commission's instructions",
          "It represents the practical outworking of Jesus' command to make disciples 'of all nations,' not just Jewish people",
          'The Great Commission had no connection to Gentile mission',
          'It shows the Great Commission was never actually fulfilled',
        ],
        a: 1,
        e: "The extension of the gospel to Gentiles through Peter, the Jerusalem Council, and Paul's missionary work represents the practical fulfilment of Jesus' Great Commission to make disciples of 'all nations,' not a limited group (Matthew 28:19).",
        h: "Consider how this expansion connects back to Jesus' earlier instruction to His disciples before His ascension.",
        yr: 'SS2',
      },
      {
        q: "What does the Jerusalem Council's decision (not requiring circumcision) reveal about the basis of salvation according to early church teaching?",
        o: [
          'Salvation depends primarily on adherence to Jewish ceremonial law',
          'Salvation is based on faith in Christ, not adherence to ceremonial requirements like circumcision',
          'Salvation is unrelated to faith altogether',
          'The council concluded salvation was impossible for Gentiles',
        ],
        a: 1,
        e: "By ruling that Gentiles did not need circumcision or the full Mosaic law for salvation, the Jerusalem Council affirmed the early church's understanding that salvation comes through faith in Christ, not through adherence to ceremonial religious requirements.",
        h: "Consider what this decision implies about what actually secures a person's salvation, according to early church teaching.",
        yr: 'SS2',
      },
      {
        q: "Why might Paul's extensive missionary journeys be considered a direct outworking of the Jerusalem Council's decision?",
        o: [
          "Paul's journeys occurred before the Jerusalem Council and were unrelated to it",
          'With the theological question of Gentile inclusion settled, Paul was free to expand his mission among Gentiles without demanding they adopt Jewish ceremonial requirements',
          "The Jerusalem Council actually restricted Paul's missionary activity among Gentiles",
          "Paul's missionary work focused exclusively on Jewish communities after the council",
        ],
        a: 1,
        e: "With the Jerusalem Council's clear ruling that Gentiles need not adopt the full Mosaic law, Paul was free to expand his extensive missionary work among Gentile populations without the complication of requiring circumcision or full Jewish observance, facilitating the rapid spread of the gospel.",
        h: "Consider how settling this theological question would have practically enabled Paul's wide-ranging missionary efforts.",
        yr: 'SS2',
      },
      {
        q: "Which best summarizes the overall significance of the Holy Spirit's role in expanding the church's mission to the Gentiles?",
        o: [
          'The Holy Spirit played no active role in this expansion, which occurred through human decision alone',
          "The Holy Spirit actively guided key events — Peter's vision, the outpouring at Cornelius's household, and the Jerusalem Council — confirming that the gospel was intended for all humanity, not only the Jewish people",
          'The expansion to the Gentiles occurred against the guidance of the Holy Spirit',
          "The Holy Spirit's involvement was limited only to the original Pentecost event",
        ],
        a: 1,
        e: "Throughout this development — Peter's preparatory vision, the direct outpouring of the Spirit on Cornelius's household, and the Spirit's guidance at the Jerusalem Council — the Holy Spirit actively and decisively confirmed that the gospel and salvation through Christ were intended for all humanity, Jew and Gentile alike.",
        h: "Consider the several distinct points across this topic where the Holy Spirit's active guidance is specifically mentioned.",
        yr: 'SS2',
      },
      {
        q: 'What specific dietary and moral requirements did the Jerusalem Council ask Gentile believers to observe, according to Acts 15:29?',
        o: [
          'To abstain from food sacrificed to idols, from blood, from meat of strangled animals, and from sexual immorality',
          'To follow every detail of the Mosaic ceremonial law',
          'To avoid all contact with Jewish believers',
          'To observe the entire Jewish festival calendar',
        ],
        a: 0,
        e: "The Jerusalem Council's letter specifically asked Gentile believers to abstain from food sacrificed to idols, from blood, from the meat of strangled animals, and from sexual immorality (Acts 15:29), rather than the full Mosaic law.",
        h: "This list represents the specific, limited requirements set out in the council's letter.",
        yr: 'SS2',
      },
      {
        q: "What was the general Jewish attitude toward eating with or visiting the home of a Gentile before Peter's vision?",
        o: [
          'It was considered fully normal and encouraged',
          'It was generally considered improper or unlawful due to concerns about ritual purity',
          'It was legally required by Roman law',
          'It had no cultural or religious significance at all',
        ],
        a: 1,
        e: "Peter himself noted to Cornelius's household that it was against Jewish law for him to associate with or visit a Gentile, reflecting the general attitude his vision was meant to correct (Acts 10:28).",
        h: "Consider what Peter explicitly states to Cornelius's household about Jewish custom regarding Gentiles.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Opposition to the Gospel Message I',
    topicCode: 'SS2-CRS-23',
    module: 'The Early Church',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  As the gospel spread, the early church faced fierce <span class="learn-keyword">opposition</span> from religious authorities. This first study examines the persecution faced by Peter, John, and Stephen, and how the believers responded.\n</div>\n\n<h3 class="learn-subheading">1. Peter and John Before the Sanhedrin</h3>\n<p class="learn-p">After healing a lame man at the temple gate and preaching boldly, Peter and John were arrested by the temple authorities and brought before the Sanhedrin, who commanded them to stop speaking in Jesus\' name. Peter and John responded, "Judge for yourselves whether it is right in God\'s sight to obey you rather than God. As for us, we cannot help speaking about what we have seen and heard" (Acts 4:1-20).</p>\n\n<h3 class="learn-subheading">2. The Believers\' Prayer for Boldness</h3>\n<p class="learn-p">Upon their release, Peter and John reported to the other believers, who together prayed not for safety but for continued boldness to speak God\'s word, and for God to stretch out His hand to heal and perform signs (Acts 4:23-31). The place where they were meeting was shaken, and they were filled with the Holy Spirit and spoke the word boldly.</p>\n\n<h3 class="learn-subheading">3. Stephen\'s Arrest and Defence</h3>\n<p class="learn-p">Stephen, one of the seven chosen to serve the early church (Acts 6:1-6), was full of God\'s grace and power and performed great wonders. Opponents from various synagogues could not stand against his wisdom, so they stirred up false witnesses and had him seized and brought before the Sanhedrin on false charges of blasphemy (Acts 6:8-15). Stephen delivered a lengthy defence recounting Israel\'s history and accused his listeners of resisting the Holy Spirit as their ancestors had (Acts 7:1-53).</p>\n\n<h3 class="learn-subheading">4. Stephen\'s Martyrdom</h3>\n<p class="learn-p">Enraged by his speech, the Sanhedrin dragged Stephen out of the city and stoned him to death. As he died, Stephen prayed, "Lord Jesus, receive my spirit," and, echoing Jesus\' own words on the cross, "Lord, do not hold this sin against them" (Acts 7:54-60). A young man named Saul (later Paul) was present, approving of his death (Acts 7:58; 8:1).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Person(s)</th><th>Form of Opposition</th><th>Response</th></tr></thead>\n    <tbody>\n      <tr><td>Peter and John</td><td>Arrest, questioning, command to stop preaching</td><td>Refused to stop; prayed for boldness</td></tr>\n      <tr><td>Stephen</td><td>False witnesses, false accusation, stoning</td><td>Bold defence; forgiveness and prayer even as he died</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Note that Stephen is regarded as the first Christian martyr, and that Saul (later Paul) was present and approved of his death — an important link to later topics on Saul\'s conversion.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> Peter, John, and Stephen faced serious opposition for proclaiming the gospel, yet responded with boldness, faithfulness, and even forgiveness, modelling how believers should respond to persecution.\n</div>\n',
    questions: [
      {
        q: "What miracle led to Peter and John's arrest, as recorded in Acts 3-4?",
        o: [
          'The raising of a dead man',
          'The healing of a lame man at the temple gate',
          'The calming of a storm',
          'Turning water into wine',
        ],
        a: 1,
        e: 'Peter and John healed a man who had been lame from birth at the temple gate called Beautiful, and their subsequent preaching to the amazed crowd led to their arrest by the temple authorities (Acts 3:1-10; 4:1-3).',
        h: 'This healing occurred at a specific gate of the temple, involving a man with a lifelong physical condition.',
        yr: 'SS2',
      },
      {
        q: 'What command did the Sanhedrin give Peter and John after questioning them?',
        o: [
          'To leave Jerusalem permanently',
          'To stop speaking or teaching at all in the name of Jesus',
          'To become priests immediately',
          'To pay a fine and return home',
        ],
        a: 1,
        e: 'The Sanhedrin commanded Peter and John not to speak or teach at all in the name of Jesus, seeking to suppress the spread of the gospel message (Acts 4:18).',
        h: 'Their command specifically targeted speaking about a particular name.',
        yr: 'SS2',
      },
      {
        q: "How did Peter and John respond to the Sanhedrin's command to stop speaking about Jesus?",
        o: [
          'They agreed immediately and stopped preaching',
          'They declared they could not help speaking about what they had seen and heard',
          'They fled the city instead of responding',
          'They denied ever preaching about Jesus',
        ],
        a: 1,
        e: "Peter and John responded, 'Judge for yourselves whether it is right in God's sight to obey you rather than God. As for us, we cannot help speaking about what we have seen and heard' (Acts 4:19-20), refusing to comply with the command.",
        h: 'Their response invoked a higher authority than the human council questioning them.',
        yr: 'SS2',
      },
      {
        q: "What did the believers pray for after Peter and John's release, according to Acts 4:29-30?",
        o: [
          'Safety and an end to all persecution',
          "Boldness to speak God's word, and for God to perform healing and signs",
          'Wealth and comfortable living',
          'Revenge against their persecutors',
        ],
        a: 1,
        e: "Rather than praying for safety, the believers prayed for boldness to continue speaking God's word and for God to stretch out His hand to heal and perform signs and wonders through the name of Jesus (Acts 4:29-30).",
        h: 'Consider what specific quality, rather than safety, the believers requested from God.',
        yr: 'SS2',
      },
      {
        q: 'What happened to the place where the believers were praying after their prayer for boldness?',
        o: [
          'Nothing unusual occurred',
          'The place was shaken, and they were filled with the Holy Spirit and spoke boldly',
          'The building collapsed entirely',
          'A fire consumed the building',
        ],
        a: 1,
        e: "After they prayed, the place where they were meeting was shaken, and they were all filled with the Holy Spirit and spoke the word of God boldly (Acts 4:31), confirming God's response to their prayer.",
        h: 'This physical sign occurred immediately following their specific prayer request.',
        yr: 'SS2',
      },
      {
        q: 'What role did Stephen hold within the early church before his arrest?',
        o: [
          'He was one of the original twelve apostles',
          'He was one of seven men chosen to serve, being full of faith and the Holy Spirit',
          'He was a Roman official',
          'He was a member of the Sanhedrin',
        ],
        a: 1,
        e: 'Stephen was chosen as one of seven men to help oversee the distribution of food to widows in the early church, described as full of faith and the Holy Spirit (Acts 6:1-6).',
        h: 'This role involved practical service within the growing early Christian community.',
        yr: 'SS2',
      },
      {
        q: "What tactic did Stephen's opponents use since they could not out-argue his wisdom?",
        o: [
          'They challenged him to a public debate',
          'They secretly persuaded men to bring false witness against him',
          'They offered him a bribe',
          'They ignored him completely',
        ],
        a: 1,
        e: "Unable to stand against Stephen's wisdom and the Spirit by whom he spoke, his opponents secretly persuaded men to falsely accuse him of blasphemy against Moses and God (Acts 6:9-11).",
        h: 'Their strategy involved dishonest testimony rather than genuine argument.',
        yr: 'SS2',
      },
      {
        q: 'What did Stephen do when brought before the Sanhedrin to answer the charges against him?',
        o: [
          'He remained completely silent',
          "He delivered a lengthy defence recounting Israel's history and accusing his listeners of resisting the Holy Spirit",
          'He immediately confessed to the false charges',
          'He fled before he could be questioned',
        ],
        a: 1,
        e: "Stephen delivered an extensive speech tracing Israel's history from Abraham through Moses and the prophets, concluding with a direct accusation that his listeners, like their ancestors, resisted the Holy Spirit (Acts 7:1-53).",
        h: 'His response involved a detailed historical survey leading to a pointed accusation.',
        yr: 'SS2',
      },
      {
        q: 'What ultimately happened to Stephen after his defence before the Sanhedrin?',
        o: [
          'He was released without punishment',
          'He was dragged out of the city and stoned to death',
          'He was exiled from Jerusalem',
          'He was imprisoned for life',
        ],
        a: 1,
        e: "Enraged by Stephen's speech, the members of the Sanhedrin dragged him out of the city and stoned him to death, making him the first recorded Christian martyr (Acts 7:57-60).",
        h: 'His punishment involved a specific, severe form of execution outside the city.',
        yr: 'SS2',
      },
      {
        q: 'What did Stephen pray as he was being stoned to death?',
        o: [
          "'Save me from this death'",
          "'Lord Jesus, receive my spirit' and 'Lord, do not hold this sin against them'",
          "'I curse those who persecute me'",
          'He said nothing at all',
        ],
        a: 1,
        e: "As he was dying, Stephen prayed, 'Lord Jesus, receive my spirit,' and then, echoing Jesus' own words on the cross, prayed for his persecutors, 'Lord, do not hold this sin against them' (Acts 7:59-60).",
        h: 'His final words closely paralleled statements Jesus made during His own crucifixion.',
        yr: 'SS2',
      },
      {
        q: "Who was present at Stephen's stoning, approving of his death, and would later become significant in church history?",
        o: ['Peter', 'A young man named Saul', 'Barnabas', 'Philip'],
        a: 1,
        e: "A young man named Saul was present at Stephen's death, giving approval to it, and later held the coats of those who stoned him (Acts 7:58; 8:1) — this same Saul would later become the apostle Paul.",
        h: "This person's later dramatic conversion becomes a major focus of subsequent chapters in Acts.",
        yr: 'SS2',
      },
      {
        q: "How does the believers' prayer response in Acts 4 model an approach to facing opposition?",
        o: [
          'By avoiding prayer altogether and relying on human strategy',
          'By seeking boldness to continue their mission rather than merely requesting safety or an end to opposition',
          'By praying only for the removal of all future persecution',
          'By demanding retaliation against their persecutors',
        ],
        a: 1,
        e: 'Rather than praying to escape or avoid difficulty, the believers modelled a response focused on continued faithfulness and boldness in their mission despite opposition, trusting God for strength rather than safety alone.',
        h: 'Consider what the believers specifically requested, compared to what might be a more naturally self-protective request.',
        yr: 'SS2',
      },
      {
        q: "What significance does Stephen's prayer of forgiveness for his persecutors hold in relation to Jesus' own crucifixion?",
        o: [
          "It has no relationship to Jesus' crucifixion at all",
          "It closely echoes Jesus' own prayer of forgiveness from the cross, showing Stephen's Christlike response even in death",
          "It contradicts Jesus' teaching on forgiveness",
          'It shows Stephen was angry and unforgiving toward his persecutors',
        ],
        a: 1,
        e: "Stephen's prayer, 'Lord, do not hold this sin against them,' closely parallels Jesus' own prayer of forgiveness from the cross ('Father, forgive them...'), reflecting a deeply Christlike response to suffering and injustice even in his final moments.",
        h: "Consider the similarity between Stephen's final prayer and an earlier prayer studied concerning Jesus' own death.",
        yr: 'SS2',
      },
      {
        q: "Why is Peter and John's response to the Sanhedrin's command ('we cannot help speaking about what we have seen and heard') significant?",
        o: [
          'It shows a willingness to compromise their message when threatened',
          'It reflects a conviction that obedience to God takes priority over human authority when the two conflict',
          'It shows disrespect for legitimate authority in all circumstances',
          'It has no theological significance',
        ],
        a: 1,
        e: 'Their response reflects the conviction that when human authority directly conflicts with obedience to God, believers must prioritize obedience to God, a principle they explicitly stated when questioned (Acts 4:19-20).',
        h: 'Consider the specific principle Peter and John articulated when explaining their refusal to comply.',
        yr: 'SS2',
      },
      {
        q: "What does Stephen's selection as one of the 'seven' (Acts 6:1-6), alongside his later powerful ministry, suggest about the nature of service in the early church?",
        o: [
          'That practical service roles were unrelated to spiritual gifting or effectiveness',
          'That even those appointed to practical service could be powerfully gifted and used significantly in wider ministry, including preaching and defending the faith',
          'That only apostles could be effective in ministry',
          "That Stephen's practical role prevented him from any other ministry",
        ],
        a: 1,
        e: "Stephen's selection for a primarily practical service role (overseeing food distribution), combined with his evident wisdom, power, and eventual significant ministry and martyrdom, shows that practical service and powerful spiritual ministry were not mutually exclusive in the early church.",
        h: 'Consider what it suggests that someone appointed to a practical role became so significant in preaching and defending the faith.',
        yr: 'SS2',
      },
      {
        q: "How does the presence of Saul at Stephen's death connect this topic to later developments in the book of Acts?",
        o: [
          "Saul's presence here has no connection to any later events",
          "Saul's presence and approval of Stephen's death set up a dramatic contrast with his later conversion and becoming a great missionary to the Gentiles",
          "Saul was actually opposed to Stephen's execution",
          'This connection is never referenced again in Acts',
        ],
        a: 1,
        e: "Saul's presence and approval at Stephen's stoning creates a striking narrative connection to his own dramatic later conversion (studied in a subsequent topic) and his transformation into one of the greatest missionaries and defenders of the very faith he once persecuted.",
        h: 'Consider how this early appearance of Saul sets up a significant future development in the Acts narrative.',
        yr: 'SS2',
      },
      {
        q: 'What common pattern of response to opposition is shown by both Peter/John and Stephen in this topic?',
        o: [
          'Both immediately renounced their faith when threatened',
          'Both remained faithful to their message and mission despite serious threats, responding with courage rather than compromise',
          'Both avoided any direct confrontation with authorities',
          'Both relied solely on political negotiation to resolve the conflict',
        ],
        a: 1,
        e: 'Despite facing serious threats — arrest and command to silence for Peter and John, false accusation and eventual death for Stephen — both responded with courageous faithfulness to their message rather than compromise or silence.',
        h: 'Consider the shared quality of response across both accounts studied in this topic, despite their different specific outcomes.',
        yr: 'SS2',
      },
      {
        q: 'Which best summarizes the overall lesson from the early opposition faced by Peter, John, and Stephen?',
        o: [
          'Opposition should lead believers to abandon their faith to avoid suffering',
          'Faithful proclamation of the gospel often invites real opposition, but believers can respond with courage, prayer for boldness, and even forgiveness toward persecutors',
          'Opposition to the gospel never occurred in the early church',
          'Only the apostles, not ordinary believers like Stephen, faced any opposition',
        ],
        a: 1,
        e: 'This topic teaches that faithful proclamation of the gospel can provoke serious opposition, yet believers — whether apostles like Peter and John or ordinary servants like Stephen — can respond with courage, continued prayer for boldness, and remarkable grace and forgiveness even toward their persecutors.',
        h: 'Consider the overall pattern of faithful response under pressure demonstrated by all three men studied in this topic.',
        yr: 'SS2',
      },
      {
        q: 'What accusation did the false witnesses specifically bring against Stephen before the Sanhedrin?',
        o: [
          'That he had stolen temple funds',
          'That he had spoken words of blasphemy against Moses and God, and against the temple and the law',
          'That he had led a violent uprising',
          'That he had denied the existence of God',
        ],
        a: 1,
        e: 'The false witnesses accused Stephen of never stopping speaking against the holy place (the temple) and the law, claiming he said Jesus would destroy the temple and change the customs Moses handed down (Acts 6:13-14).',
        h: 'Consider the specific religious institutions the false accusations against Stephen centered on.',
        yr: 'SS2',
      },
      {
        q: 'What did the Sanhedrin ultimately do to Peter and John before releasing them, despite finding no legal basis to punish them further?',
        o: [
          'They executed them immediately',
          "They threatened them further and then let them go, unable to find a way to punish them because of the people's reaction",
          'They exiled them from Judea',
          'They imprisoned them permanently',
        ],
        a: 1,
        e: 'Unable to find a way to punish Peter and John further because of the people who were praising God for the healing, the Sanhedrin further threatened them and then let them go (Acts 4:21).',
        h: 'Consider what constraint prevented the Sanhedrin from further punishing Peter and John at this point.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Opposition to the Gospel Message II',
    topicCode: 'SS2-CRS-24',
    module: 'The Early Church',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  This second study of opposition to the gospel examines the persecution and imprisonment faced by <span class="learn-keyword">Peter</span> and by <span class="learn-keyword">Paul and Silas</span>, and the lessons drawn from how they responded through faith and prayer.\n</div>\n\n<h3 class="learn-subheading">1. Herod\'s Persecution and Peter\'s Imprisonment</h3>\n<p class="learn-p">King Herod Agrippa I had James, brother of John, killed with the sword, and seeing this pleased the Jewish leaders, he also arrested Peter, intending to bring him before the people after Passover (Acts 12:1-4). Peter was kept under heavy guard, chained between two soldiers in prison.</p>\n\n<h3 class="learn-subheading">2. The Church\'s Prayer and Peter\'s Miraculous Release</h3>\n<p class="learn-p">While Peter was in prison, the church earnestly prayed for him (Acts 12:5). During the night before his planned trial, an angel appeared, caused Peter\'s chains to fall off, and led him past the guards and through the iron gate, which opened by itself, to freedom. Peter went to the house of Mary, mother of John Mark, where believers were praying, and they were astonished to see him (Acts 12:6-17).</p>\n\n<h3 class="learn-subheading">3. Paul and Silas Imprisoned at Philippi</h3>\n<p class="learn-p">After Paul cast a spirit of divination out of a slave girl, her owners, angry at losing their profit from her fortune-telling, had Paul and Silas seized, beaten, and thrown into prison with their feet fastened in stocks (Acts 16:16-24).</p>\n\n<h3 class="learn-subheading">4. Praying and Singing in Prison, and the Earthquake</h3>\n<p class="learn-p">About midnight, Paul and Silas were praying and singing hymns to God while the other prisoners listened. Suddenly, a violent earthquake shook the prison, opening all the doors and loosening everyone\'s chains. When the jailer, believing the prisoners had escaped, was about to kill himself, Paul stopped him, and the jailer, deeply moved, asked, "What must I do to be saved?" He and his household were baptized that very night (Acts 16:25-34).</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Person(s)</th><th>Form of Opposition</th><th>God\'s Response / Outcome</th></tr></thead>\n    <tbody>\n      <tr><td>Peter</td><td>Imprisoned by Herod, heavily guarded</td><td>Miraculously freed by an angel after the church\'s prayer</td></tr>\n      <tr><td>Paul and Silas</td><td>Beaten and imprisoned at Philippi</td><td>Earthquake freed them; jailer and household converted and baptized</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">💡</span>\n  <strong>Exam Tip:</strong> Note the specific detail that Paul and Silas were praying and singing hymns (not merely praying) at midnight — this detail is frequently quoted directly in exam questions.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">🎯</span>\n  <strong>Summary:</strong> The imprisonments of Peter, and of Paul and Silas, show that even in the face of severe persecution, God can miraculously deliver His people, and their faithful worship even in chains can lead others to salvation.\n</div>\n',
    questions: [
      {
        q: 'Which apostle did King Herod have killed with the sword, as recorded in Acts 12?',
        o: ['Peter', 'James, the brother of John', 'Paul', 'Andrew'],
        a: 1,
        e: 'King Herod Agrippa I had James, the brother of John, put to death with the sword as part of his persecution of the church (Acts 12:1-2).',
        h: "This apostle was part of Jesus' inner circle along with his brother John.",
        yr: 'SS2',
      },
      {
        q: 'Why did Herod also arrest Peter after killing James?',
        o: [
          'Because Peter had committed a crime',
          'Because he saw that killing James pleased the Jewish leaders',
          'Because Peter had requested to be arrested',
          'Because the Romans ordered it directly',
        ],
        a: 1,
        e: 'Seeing that the execution of James pleased the Jewish leaders, Herod proceeded to arrest Peter as well, intending further persecution to gain favour with them (Acts 12:3).',
        h: 'His motivation was connected to the positive reaction his first action received.',
        yr: 'SS2',
      },
      {
        q: 'How was Peter guarded while imprisoned by Herod?',
        o: [
          'He was left completely unguarded',
          'He was bound with chains between two soldiers, with additional guards at the entrance',
          'He was guarded by only one soldier',
          'He was kept in an open courtyard',
        ],
        a: 1,
        e: "Peter was securely bound with chains and sleeping between two soldiers, with additional guards standing at the entrance of the prison, reflecting Herod's determination to prevent any escape (Acts 12:6).",
        h: 'Multiple layers of security were used to ensure Peter could not escape.',
        yr: 'SS2',
      },
      {
        q: 'What was the church doing while Peter was in prison, according to Acts 12:5?',
        o: [
          'Ignoring the situation entirely',
          'Earnestly praying to God for him',
          'Planning a violent rescue',
          'Negotiating with Herod directly',
        ],
        a: 1,
        e: 'Acts 12:5 records that while Peter was in prison, the church was earnestly praying to God for him, showing their reliance on prayer rather than direct confrontation.',
        h: 'Their response to this crisis involved a specific spiritual practice rather than direct action.',
        yr: 'SS2',
      },
      {
        q: "How did Peter's chains fall off and his escape from prison begin?",
        o: [
          'He broke the chains himself using great strength',
          'An angel appeared, and the chains fell off his wrists',
          'A fellow prisoner helped him break free',
          'He bribed the guards to release him',
        ],
        a: 1,
        e: "An angel of the Lord appeared, and the chains fell off Peter's wrists as the angel instructed him to get up quickly, initiating his miraculous escape (Acts 12:7).",
        h: 'This event involved a supernatural visitor and a sudden release from physical restraint.',
        yr: 'SS2',
      },
      {
        q: "How did Peter and the angel pass through the prison's iron gate leading into the city?",
        o: [
          'They had to force it open manually',
          'It opened for them by itself',
          'They climbed over the prison wall instead',
          'A guard unlocked it for them',
        ],
        a: 1,
        e: 'As Peter and the angel approached the iron gate leading into the city, it opened for them by itself, allowing them to pass through without obstruction (Acts 12:10).',
        h: 'This gate opened without any visible human or physical effort being applied.',
        yr: 'SS2',
      },
      {
        q: 'Where did Peter go immediately after his miraculous release from prison?',
        o: [
          "Directly to Herod's palace",
          'To the house of Mary, the mother of John Mark, where believers were praying',
          'Out of the city permanently',
          'To the temple courts',
        ],
        a: 1,
        e: 'After realizing what had happened, Peter went to the house of Mary, the mother of John Mark, where many people had gathered and were praying (Acts 12:12).',
        h: 'This was the location where the earlier prayer meeting for his release had likely been taking place.',
        yr: 'SS2',
      },
      {
        q: 'What was the reaction of the believers when Peter appeared at the door after his escape?',
        o: [
          'They immediately recognized and expected him',
          'They were astonished, initially not believing it was really him',
          'They ignored him completely',
          'They were angry that he had escaped',
        ],
        a: 1,
        e: 'When a servant girl named Rhoda reported that Peter was at the door, the believers initially did not believe her, showing their astonishment once they realized their prayers had truly been answered (Acts 12:13-16).',
        h: 'Their initial response reflected surprise, even disbelief, at the answered prayer.',
        yr: 'SS2',
      },
      {
        q: 'Why were Paul and Silas seized and imprisoned at Philippi?',
        o: [
          'They had stolen property',
          'Paul cast out a spirit of divination from a slave girl, angering her owners who profited from her fortune-telling',
          'They refused to pay Roman taxes',
          'They had attacked a Roman official',
        ],
        a: 1,
        e: 'After Paul cast a spirit of divination out of a slave girl, her owners, realizing their hope of profit from her fortune-telling was gone, seized Paul and Silas and dragged them before the authorities (Acts 16:16-19).',
        h: "Their arrest was motivated by financial loss experienced by the girl's owners.",
        yr: 'SS2',
      },
      {
        q: 'How were Paul and Silas treated after being brought before the authorities at Philippi?',
        o: [
          'They were released without any punishment',
          'They were stripped, severely flogged, and thrown into prison with their feet in stocks',
          'They were given a comfortable cell',
          'They were immediately executed',
        ],
        a: 1,
        e: 'Paul and Silas were stripped and beaten with rods, then thrown into prison, with the jailer commanded to guard them carefully, and their feet fastened in stocks (Acts 16:22-24).',
        h: 'Their treatment involved both physical punishment and additional physical restraint.',
        yr: 'SS2',
      },
      {
        q: 'What were Paul and Silas doing around midnight while in prison?',
        o: [
          'Sleeping soundly',
          'Praying and singing hymns to God, while other prisoners listened',
          'Planning an escape',
          'Arguing with each other',
        ],
        a: 1,
        e: 'About midnight, Paul and Silas were praying and singing hymns to God, and the other prisoners were listening to them (Acts 16:25), demonstrating remarkable faith and worship even amid suffering.',
        h: 'Their activity combined two specific spiritual practices, observed by others in the prison.',
        yr: 'SS2',
      },
      {
        q: 'What supernatural event occurred suddenly while Paul and Silas were praying and singing?',
        o: [
          'A great flood',
          "A violent earthquake that shook the prison, opening all the doors and loosening everyone's chains",
          'A fire that consumed the prison',
          'Complete darkness fell over the city',
        ],
        a: 1,
        e: "Suddenly there was a violent earthquake, so severe that it shook the prison's foundations, and immediately all the prison doors flew open and everyone's chains came loose (Acts 16:26).",
        h: "This natural disaster had a specific, dramatic effect on the prison's physical structures and the prisoners' restraints.",
        yr: 'SS2',
      },
      {
        q: 'What was the Philippian jailer about to do when he woke and saw the prison doors open?',
        o: [
          'Celebrate with the prisoners',
          'Kill himself, believing the prisoners had escaped',
          'Immediately release everyone officially',
          'Call for reinforcements',
        ],
        a: 1,
        e: 'Believing that all the prisoners had escaped and fearing the consequences he would face, the jailer drew his sword and was about to kill himself when Paul called out to stop him, assuring him everyone was still present (Acts 16:27-28).',
        h: 'His intended action reflected the severe consequences he expected to face for a prison escape under his watch.',
        yr: 'SS2',
      },
      {
        q: 'What question did the jailer ask Paul and Silas after this dramatic event?',
        o: [
          "'How can I escape punishment from my superiors?'",
          "'Sirs, what must I do to be saved?'",
          "'Who caused this earthquake?'",
          "'Why did you not escape when you had the chance?'",
        ],
        a: 1,
        e: "Deeply moved by the events, the jailer fell trembling before Paul and Silas and asked, 'Sirs, what must I do to be saved?' (Acts 16:29-30), showing a genuine spiritual response to what he had witnessed.",
        h: 'His question moved beyond his immediate physical crisis to a deeper spiritual concern.',
        yr: 'SS2',
      },
      {
        q: 'What happened to the jailer and his household after Paul and Silas shared the gospel with him?',
        o: [
          'They rejected the message entirely',
          'He and his entire household believed and were baptized that same night',
          'Only the jailer himself believed, not his family',
          'They were arrested along with Paul and Silas',
        ],
        a: 1,
        e: 'The jailer and his entire household believed in the Lord and were baptized that very night, and the jailer brought Paul and Silas into his home and set a meal before them, filled with joy (Acts 16:31-34).',
        h: 'This positive response extended beyond just the jailer to include his whole family.',
        yr: 'SS2',
      },
      {
        q: "What common element links the imprisonments of Peter, and of Paul and Silas, in terms of the church's or the prisoners' response?",
        o: [
          'Both responded with despair and hopelessness',
          "Both involved prayer — the church's prayer for Peter, and Paul and Silas's own prayer and singing — preceding miraculous deliverance",
          'Neither involved any prayer at all',
          'Both resulted in permanent imprisonment with no deliverance',
        ],
        a: 1,
        e: "In both accounts, prayer plays a central role — the church's earnest prayer for Peter's release, and Paul and Silas's own prayer and singing in prison — immediately preceding a miraculous act of God resulting in deliverance.",
        h: 'Consider the specific spiritual practice common to both accounts, occurring just before the miraculous events.',
        yr: 'SS2',
      },
      {
        q: "What does the jailer's conversion suggest about how God can use difficult circumstances, including persecution, for the spread of the gospel?",
        o: [
          'Persecution always prevents any positive outcome for the gospel',
          "Even severe, unjust circumstances like imprisonment can become opportunities for the gospel to reach new people, as with the jailer's conversion",
          "The jailer's conversion had nothing to do with the events of the imprisonment",
          'Only planned evangelism, not unexpected circumstances, can lead to conversions',
        ],
        a: 1,
        e: "The jailer's dramatic conversion, arising directly out of the unjust imprisonment of Paul and Silas, illustrates how God can work through difficult and even unjust circumstances to bring about significant gospel opportunities and new believers.",
        h: 'Consider how an unjust imprisonment ultimately led to an unexpected positive spiritual outcome.',
        yr: 'SS2',
      },
      {
        q: "Why is Paul and Silas's choice to pray and sing hymns, rather than despair, while imprisoned and injured, significant?",
        o: [
          'It shows they were unaffected by their circumstances at all',
          'It demonstrates remarkable faith and worship maintained even amid suffering and injustice, rather than being determined by external circumstances',
          'It suggests they were unaware of the severity of their situation',
          'It shows a lack of genuine emotion regarding their suffering',
        ],
        a: 1,
        e: "Despite having just been severely beaten and unjustly imprisoned, Paul and Silas's choice to pray and sing hymns demonstrates that their worship and faith were not dependent on favourable external circumstances, offering a powerful example of resilient faith.",
        h: 'Consider what their specific response, despite their painful and unjust circumstances, reveals about the source of their faith and worship.',
        yr: 'SS2',
      },
      {
        q: 'How does Peter stopping the jailer from killing himself (in the Philippi account, referring to Paul) reflect a broader Christian concern beyond mere self-preservation?',
        o: [
          'Paul was concerned only about his own safety and reputation',
          "Paul's concern for the jailer's life, even though he could have simply escaped, reflects a genuine care for others even amid his own suffering",
          'Paul wanted the jailer to be punished for imprisoning him',
          'This action had no particular significance',
        ],
        a: 1,
        e: "Paul's immediate concern for the jailer's life — calling out to stop him from suicide rather than using the opportunity to escape — reflects a genuine care for the wellbeing of others, even those responsible for his own suffering, consistent with Christian teaching on love for one's enemies.",
        h: 'Consider what Paul could have done differently in that moment, and what his actual choice reveals about his priorities.',
        yr: 'SS2',
      },
      {
        q: 'Which best summarizes the overall lesson from the imprisonments of Peter, and of Paul and Silas, studied together?',
        o: [
          'Persecution always results in defeat for believers with no positive outcome',
          "Even severe persecution and unjust imprisonment cannot ultimately stop God's purposes, and faithful prayer and worship amid suffering can lead to remarkable deliverance and new believers",
          'Prayer has no real effect on difficult circumstances',
          "Only apostles, not ordinary believers, can expect God's deliverance from persecution",
        ],
        a: 1,
        e: "Together, these accounts teach that even severe persecution and unjust imprisonment cannot ultimately thwart God's purposes; faithful prayer, worship, and trust in God amid suffering — as shown by the church for Peter, and by Paul and Silas themselves — can lead to remarkable deliverance and further spread of the gospel.",
        h: 'Consider the overarching, encouraging lesson these two dramatic accounts of persecution and deliverance offer together.',
        yr: 'SS2',
      },
    ],
  },
];
